import React, { Suspense, lazy, useState, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Separator } from "./components/ui/separator";
import { Calendar, MapPin, Clock, Users, Plus, Copy, Search, Filter, Edit, Trash2, Eye, EyeOff, Settings, Share2, Star, TrendingUp, Globe, BarChart3, MousePointer, Bookmark, ArrowLeft, Save, FileText, Type, Repeat, RefreshCw, Upload, Download, CheckCircle, AlertCircle, Send, Pause, Play, ChevronDown, X } from "lucide-react";
import { useToast } from "./hooks/use-toast";
import { Toaster } from "./components/ui/toaster";

// Authentication imports
import { AuthProvider, useAuth, ProtectedRoute } from './AuthContext';
const AuthPage = lazy(() => import('./components/auth/AuthPage'));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage'));
import OrganizationSwitcher from './components/auth/OrganizationSwitcher';
import UserMenu from './components/auth/UserMenu';
import OrganizationSetup from './components/OrganizationSetup';
import UserManagement from './components/UserManagement';

// Comprehensive fix for ResizeObserver errors
if (typeof window !== 'undefined') {
  // Method 1: Wrap the ResizeObserver constructor
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      const wrappedCallback = (entries, observer) => {
        window.requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (e) {
            // Silently catch ResizeObserver errors
          }
        });
      };
      super(wrappedCallback);
    }
  };

  // Method 2: Global error handler
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  });

  // Method 3: Unhandled rejection handler
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  });
}
import RealtimeSearch from './components/RealtimeSearch';
const SearchPage = lazy(() => import('./components/SearchPage'));
const SuperAdminPortal = lazy(() => import('./components/SuperAdminPortal'));
const EventTemplateManager = lazy(() => import('./components/EventTemplateManager'));
import HierarchicalCategorySelector from './components/HierarchicalCategorySelector';
import SearchableSelect from './components/SearchableSelect';
import { PrivacyPolicyPage, TermsOfUsePage } from './components/LegalPages';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Dynamically determine backend URL
// If REACT_APP_BACKEND_URL is set, use it
// Otherwise, use current origin (for preview environments)
const getBackendURL = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // For custom domains, use the API host instead of the tenant domain.
  // Fall back to current origin for local/preview environments.
  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  const isAppHost = hostname === 'app.whatshappeningevents.com' || hostname === 'api.whatshappeningevents.com';
  if (!isLocal && !isAppHost) {
    return 'https://api.whatshappeningevents.com';
  }
  return window.location.origin;
};

const BACKEND_URL = getBackendURL();
const API = `${BACKEND_URL}/api`;
const EVENT_FALLBACK_IMAGE = "/images/whb-event-fallback.jpg";

const resolveAssetUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const DEFAULT_RECURRENCE_DATA = {
  interval: 1,
  end_type: 'never',
  count: 10,
  until: '',
  days_of_week: [],
  monthly_type: 'same_date',
  month_day: 1,
  week_occurrence: 1,
  week_occurrences: [1],
  weekday: 1
};

const normalizeRecurrenceData = (data = {}) => {
  const raw = data && typeof data === 'object' ? data : {};
  const fallbackOccurrence = Number.isInteger(raw.week_occurrence) && raw.week_occurrence >= 1 && raw.week_occurrence <= 4
    ? raw.week_occurrence
    : 1;
  const normalizedOccurrences = Array.isArray(raw.week_occurrences)
    ? raw.week_occurrences
        .map((value) => parseInt(value, 10))
        .filter((value) => Number.isInteger(value) && value >= 1 && value <= 4)
    : [];
  const week_occurrences = normalizedOccurrences.length ? Array.from(new Set(normalizedOccurrences)).sort((a, b) => a - b) : [fallbackOccurrence];

  return {
    interval: Math.max(1, parseInt(raw.interval, 10) || DEFAULT_RECURRENCE_DATA.interval),
    end_type: ['never', 'count', 'date'].includes(raw.end_type) ? raw.end_type : DEFAULT_RECURRENCE_DATA.end_type,
    count: Math.max(1, parseInt(raw.count, 10) || DEFAULT_RECURRENCE_DATA.count),
    until: typeof raw.until === 'string' ? raw.until : DEFAULT_RECURRENCE_DATA.until,
    days_of_week: Array.isArray(raw.days_of_week)
      ? raw.days_of_week
          .map((value) => parseInt(value, 10))
          .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6)
      : DEFAULT_RECURRENCE_DATA.days_of_week,
    monthly_type: ['same_date', 'same_weekday', 'last_weekday'].includes(raw.monthly_type)
      ? raw.monthly_type
      : DEFAULT_RECURRENCE_DATA.monthly_type,
    month_day: Math.min(31, Math.max(1, parseInt(raw.month_day, 10) || DEFAULT_RECURRENCE_DATA.month_day)),
    week_occurrence: week_occurrences[0],
    week_occurrences,
    weekday: Math.min(6, Math.max(0, parseInt(raw.weekday, 10) || DEFAULT_RECURRENCE_DATA.weekday))
  };
};

// Analytics Tracking Utilities
class EventTracker {
  static async trackEvent(eventId, trackingType = 'visibility', sessionId = null) {
    try {
      const payload = {
        tracking_type: trackingType,
        session_id: sessionId || EventTracker.getSessionId()
      };
      
      await axios.post(`${API}/analytics/events/${eventId}/track`, null, {
        params: payload
      });
      
      console.log(`✅ Tracked ${trackingType} for event ${eventId}`);
    } catch (error) {
      console.warn(`⚠️ Failed to track ${trackingType} for event ${eventId}:`, error);
    }
  }
  
  static trackVisibility(eventId) {
    return EventTracker.trackEvent(eventId, 'visibility');
  }
  
  static trackClick(eventId) {
    return EventTracker.trackEvent(eventId, 'click');
  }
  
  static trackTicketClick(eventId) {
    return EventTracker.trackEvent(eventId, 'ticket_click');
  }
  
  static getSessionId() {
    let sessionId = sessionStorage.getItem('event_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('event_session_id', sessionId);
    }
    return sessionId;
  }
}

// Multi-day helpers: an event occupies every calendar day from its start
// through its end (events without an end_at occupy only their start day).
const eventOccursOnDay = (event, date) => {
  const start = new Date(event.start_at);
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  if (!event.end_at) {
    return start >= dayStart && start <= dayEnd;
  }
  const end = new Date(event.end_at);
  return start <= dayEnd && end >= dayStart;
};

// True on the days of an event's run *after* its first day (used to badge
// continuation days on calendar views).
const isEventContinuation = (event, date) => {
  const start = new Date(event.start_at);
  return eventOccursOnDay(event, date) &&
    !(start.getDate() === date.getDate() &&
      start.getMonth() === date.getMonth() &&
      start.getFullYear() === date.getFullYear());
};

// Visibility Observer Hook for tracking when events become visible
const useVisibilityTracking = (enabled = true) => {
  const [observer, setObserver] = useState(null);
  const trackedEvents = useRef(new Set());

  useEffect(() => {
    if (!enabled) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.dataset.eventId) {
            const eventId = entry.target.dataset.eventId;
            // Only track visibility once per session per event
            if (!trackedEvents.current.has(eventId)) {
              trackedEvents.current.add(eventId);
              EventTracker.trackVisibility(eventId);
            }
          }
        });
      },
      {
        threshold: 0.5, // Track when 50% of the event is visible
        rootMargin: '0px 0px -50px 0px' // Only track when event is well within viewport
      }
    );

    setObserver(obs);

    return () => {
      obs.disconnect();
    };
  }, [enabled]);

  const trackElement = (element, eventId) => {
    if (observer && element && eventId) {
      element.dataset.eventId = eventId;
      observer.observe(element);
    }
  };

  return trackElement;
};

// Updated Dashboard with authentication
const Dashboard = () => {
  const { user, currentOrganization, organizations, token } = useAuth();
  const navigate = useNavigate(); // Add navigate hook
  const [events, setEvents] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [analytics, setAnalytics] = useState({ total_views: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [hidePastEvents, setHidePastEvents] = useState(true); // Default: hide past events
  const [userRole, setUserRole] = useState("organizer"); // organizer or promoter
  const { toast } = useToast();
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  // Duplicate event modal state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [eventToDuplicate, setEventToDuplicate] = useState(null);

  useEffect(() => {
    // Only fetch data when user is authenticated and has a current organization
    if (user && currentOrganization) {
      fetchData();
    }
  }, [user, currentOrganization]);

  const fetchData = async () => {
    // Don't fetch data if user is not authenticated or doesn't have an organization
    if (!user || !currentOrganization) {
      console.log('Skipping data fetch - user not authenticated or no current organization');
      setLoading(false);
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [eventsRes, hostsRes, venuesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/events`, { headers, params: { organization_id: currentOrganization.id, limit: 250 } }),
        axios.get(`${API}/hosts`, { headers }),
        axios.get(`${API}/venues`, { headers }),
        axios.get(`${API}/categories`, { headers })
      ]);
      
      setEvents(eventsRes.data);
      setHosts(hostsRes.data);
      setVenues(venuesRes.data);
      setCategories(categoriesRes.data);

      // Analytics are useful, but they should not block the dashboard becoming usable.
      axios.get(`${API}/analytics/summary`, { headers })
        .then((analyticsRes) => setAnalytics(analyticsRes.data))
        .catch((analyticsError) => console.warn('Analytics summary failed to load', analyticsError));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || event.status === filterStatus;
    
    // Filter past events if toggle is on
    // If event has no end_at, use start_at to determine if it's past
    const eventEndDate = event.end_at ? new Date(event.end_at) : new Date(event.start_at);
    const isPastEvent = hidePastEvents && eventEndDate < new Date();
    
    // CRITICAL: Organizer view should ONLY show events from their own organization
    // Promoter view shows all published events (for aggregation)
    const belongsToOrganization = userRole === "organizer" 
      ? String(event.organization_id) === String(currentOrganization?.id) 
      : true; // Promoters see all published events
    
    // Debug logging (temporary)
    if (userRole === "organizer" && events.indexOf(event) === 0) {
      console.log('DEBUG Filter:', {
        userRole,
        eventOrgId: event.organization_id,
        currentOrgId: currentOrganization?.id,
        match: String(event.organization_id) === String(currentOrganization?.id)
      });
    }
    
    return matchesSearch && matchesStatus && !isPastEvent && belongsToOrganization;
  }).sort((a, b) => {
    const aStart = a.start_at ? new Date(a.start_at).getTime() : Number.MAX_SAFE_INTEGER;
    const bStart = b.start_at ? new Date(b.start_at).getTime() : Number.MAX_SAFE_INTEGER;
    if (aStart !== bStart) return aStart - bStart;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });

  const getRecurringDeleteChoice = (eventTitle = 'this recurring event') => {
    const choice = window.prompt(
      `Delete recurring event template: ${eventTitle}\n\n` +
      `What should happen to the current events on the calendar?\n\n` +
      `1. Delete template only and keep generated events\n` +
      `2. Delete template and all generated events\n\n` +
      `Enter 1 or 2. Leave blank to cancel.`
    );

    if (choice === null || choice.trim() === '') return null;
    if (choice.trim() === '1') return 'template_only';
    if (choice.trim() === '2') return 'all';

    toast({
      title: "Delete cancelled",
      description: "Please enter 1 or 2 when deleting a recurring template.",
      variant: "destructive",
    });
    return null;
  };

  const deleteEvent = async (eventId) => {
    try {
      const eventToDelete = events.find(e => e.id === eventId);
      const params = {};

      if (eventToDelete?.is_recurring_master) {
        const recurringDeleteType = getRecurringDeleteChoice(eventToDelete.title);
        if (!recurringDeleteType) return;
        params.recurring_delete_type = recurringDeleteType;
      }

      const response = await axios.delete(`${API}/events/${eventId}`, { params });
      if (params.recurring_delete_type === 'template_only') {
        setEvents(events
          .filter(e => e.id !== eventId)
          .map(e => e.recurring_master_id === eventId
            ? { ...e, recurring_master_id: null, recurring_instance_date: null, recurrence_type: 'none', recurrence_data: null, is_recurring_master: false }
            : e
          )
        );
      } else {
        setEvents(events.filter(e => e.id !== eventId && e.recurring_master_id !== eventId));
      }
      toast({
        title: "Success",
        description: response.data?.detached_count
          ? `Recurring template deleted. Kept ${response.data.detached_count} generated event(s) on the calendar.`
          : "Event deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete event",
        variant: "destructive",
      });
    }
  };

  const duplicateEvent = (eventId) => {
    if (!eventId) {
      toast({
        title: "Error",
        description: "No event ID provided for duplication",
        variant: "destructive",
      });
      return;
    }

    // Find the event in the local list first (instant, no network call)
    const eventData = events.find(e => e.id === eventId);
    if (!eventData) {
      toast({
        title: "Error",
        description: "Event not found",
        variant: "destructive",
      });
      return;
    }

    // Open the duplicate modal — original event data pre-fills the form,
    // user reviews/edits, then submits to create a brand-new event.
    setEventToDuplicate(eventData);
    setDuplicateModalOpen(true);
  };

  const [publishingEventId, setPublishingEventId] = useState(null);

  const publishEvent = async (eventId) => {
    // Prevent double-clicks
    if (publishingEventId === eventId) {
      return;
    }
    
    setPublishingEventId(eventId);
    
    try {
      await axios.put(`${API}/events/${eventId}`, { status: "published" });
      setEvents(events.map(e => e.id === eventId ? {...e, status: "published"} : e));
      toast({
        title: "Success",
        description: "Event published successfully",
      });
    } catch (error) {
      toast({
        title: "Error", 
        description: "Failed to publish event",
        variant: "destructive",
      });
    } finally {
      setPublishingEventId(null);
    }
  };

  const unpublishEvent = async (eventId) => {
    // Prevent double-clicks
    if (publishingEventId === eventId) {
      return;
    }
    
    setPublishingEventId(eventId);
    
    try {
      await axios.put(`${API}/events/${eventId}`, { status: "draft" });
      setEvents(events.map(e => e.id === eventId ? {...e, status: "draft"} : e));
      toast({
        title: "Success",
        description: "Event unpublished (moved to draft)",
      });
    } catch (error) {
      toast({
        title: "Error", 
        description: "Failed to unpublish event",
        variant: "destructive",
      });
    } finally {
      setPublishingEventId(null);
    }
  };

  const getOrganizerName = (organizerId, event = null) => {
    // First try to use the host_name from the event data (new approach after hosts rename)
    if (event && event.host_name) {
      return event.host_name;
    }
    
    // Fallback: try organizer_name for backward compatibility
    if (event && event.organizer_name) {
      return event.organizer_name;
    }
    
    // Fallback: try event-level organizer info (for syndicated events)
    if (event && event.organizer_name) {
      return event.organizer_name;
    }
    
    // Final fallback: lookup in hosts array using host_id or organizer_id
    const hostId = event?.host_id || organizerId;
    const host = hosts.find(h => h.id === hostId);
    return host ? host.name : "Unknown";
  };

  const getVenueName = (venueId) => {
    const venue = venues.find(v => v.id === venueId);
    return venue ? venue.name : "No venue";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Edit modal functions
  const openEditModal = (event) => {
    setEditingEvent(event);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingEvent(null);
  };

  const handleEventUpdated = () => {
    // Refresh events data after update
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://storage.googleapis.com/msgsndr/XmeXLuZf7puUFVQz6zOn/media/68a68af95f326c2d62b4d325.png" 
                alt="Logo" 
                className="h-12 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <Calendar className="w-8 h-8 text-purple-600 hidden" />
              
            </div>
            <div className="flex items-center space-x-4">
              {/* Role Switcher */}
              <div className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm rounded-lg p-1 border border-gray-200">
                <Button
                  variant={userRole === "organizer" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setUserRole("organizer")}
                  className={userRole === "organizer" ? "bg-purple-600 text-white border border-purple-300" : "text-gray-700 hover:text-purple-600 hover:bg-purple-50 border border-gray-300"}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Organizer
                </Button>
                <Button
                  variant={userRole === "promoter" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setUserRole("promoter")}
                  className={userRole === "promoter" ? "bg-purple-600 text-white border border-purple-300" : "text-gray-700 hover:text-purple-600 hover:bg-purple-50 border border-gray-300"}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Promoter
                </Button>
              </div>
              
              {/* Organization Switcher */}
              <OrganizationSwitcher />
              
              {/* Settings Button */}
              <Link to="/settings">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-gray-600 hover:text-gray-700"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </Link>
              
              {/* User Menu with Sign Out */}
              <UserMenu />
              
              {userRole === "organizer" && (
                <Link to="/events/create">
                  <Button className="bg-brand-primary hover:bg-brand-primary text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {userRole === "organizer" ? (
          <OrganizerDashboard 
            events={filteredEvents}
            organizers={hosts}
            venues={venues}
            categories={categories}
            analytics={analytics}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            hidePastEvents={hidePastEvents}
            setHidePastEvents={setHidePastEvents}
            deleteEvent={deleteEvent}
            duplicateEvent={duplicateEvent}
            publishEvent={publishEvent}
            unpublishEvent={unpublishEvent}
            publishingEventId={publishingEventId}
            getOrganizerName={getOrganizerName}
            getVenueName={getVenueName}
            formatDate={formatDate}
            getStatusColor={getStatusColor}
          />
        ) : (
          <PromoterDashboard 
            events={filteredEvents}
            hosts={hosts}
            venues={venues}
            categories={categories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            hidePastEvents={hidePastEvents}
            setHidePastEvents={setHidePastEvents}
            publishingEventId={publishingEventId}
            getOrganizerName={getOrganizerName}
            getVenueName={getVenueName}
            formatDate={formatDate}
            getStatusColor={getStatusColor}
          />
        )}
      </div>

      {/* Duplicate Event Modal — opens when user clicks Duplicate on any event card */}
      <DuplicateEventModal
        event={eventToDuplicate}
        isOpen={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setEventToDuplicate(null);
        }}
        onEventCreated={() => {
          fetchData();
          setDuplicateModalOpen(false);
          setEventToDuplicate(null);
        }}
      />
    </div>
  );
};

// Recurring Events Manager Component
const RecurringEventsManager = ({ token, organizationId }) => {
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedSeries, setExpandedSeries] = useState(null);
  const [seriesInstances, setSeriesInstances] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    loadRecurringEvents();
  }, [organizationId]);

  const loadRecurringEvents = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/events`, { 
        headers,
        params: { organization_id: organizationId, limit: 500 }
      });
      // Filter to only recurring masters
      const masters = (response.data || []).filter(e => e.is_recurring_master);
      setRecurringEvents(masters);
    } catch (error) {
      console.error('Error loading recurring events:', error);
    }
    setLoading(false);
  };

  const loadSeries = async (masterId) => {
    if (expandedSeries === masterId) {
      setExpandedSeries(null);
      return;
    }
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/events/${masterId}/recurring-series`, { headers });
      setSeriesInstances(prev => ({ ...prev, [masterId]: response.data.instances || [] }));
      setExpandedSeries(masterId);
    } catch (error) {
      console.error('Error loading series:', error);
      toast({ title: "Error", description: "Failed to load series instances", variant: "destructive" });
    }
  };

  const deleteSeries = async (masterId) => {
    const recurringEvent = recurringEvents.find(event => event.id === masterId);
    const choice = window.prompt(
      `Delete recurring event template: ${recurringEvent?.title || 'this recurring event'}\n\n` +
      `What should happen to the current events on the calendar?\n\n` +
      `1. Delete template only and keep generated events\n` +
      `2. Delete template and all generated events\n\n` +
      `Enter 1 or 2. Leave blank to cancel.`
    );

    if (choice === null || choice.trim() === '') return;

    let deleteType;
    if (choice.trim() === '1') {
      deleteType = 'template_only';
    } else if (choice.trim() === '2') {
      deleteType = 'all';
    } else {
      toast({ title: "Delete cancelled", description: "Please enter 1 or 2.", variant: "destructive" });
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.delete(`${API}/recurring-events/${masterId}?delete_type=${deleteType}`, { headers });
      toast({
        title: "Deleted",
        description: response.data?.detached_count
          ? `Recurring template deleted. Kept ${response.data.detached_count} generated event(s) on the calendar.`
          : "Recurring event series deleted"
      });
      loadRecurringEvents();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete series", variant: "destructive" });
    }
  };

  const deleteInstance = async (masterId, instanceDate) => {
    if (!confirm('Delete this single instance?')) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/recurring-events/${masterId}?delete_type=this_only&instance_date=${instanceDate}`, { headers });
      toast({ title: "Deleted", description: "Instance deleted" });
      loadSeries(masterId); // Refresh
      setExpandedSeries(null); // Reset so it reloads
      setTimeout(() => loadSeries(masterId), 100);
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete instance", variant: "destructive" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const getRecurrenceLabel = (event) => {
    const type = event.recurrence_type;
    const data = event.recurrence_data || {};
    const interval = data.interval || 1;
    let label = '';
    if (type === 'daily') label = interval === 1 ? 'Daily' : `Every ${interval} days`;
    else if (type === 'weekly') {
      label = interval === 1 ? 'Weekly' : `Every ${interval} weeks`;
      if (data.days_of_week?.length) {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        label += ` on ${data.days_of_week.map(d => dayNames[d]).join(', ')}`;
      }
    }
    else if (type === 'monthly') label = interval === 1 ? 'Monthly' : `Every ${interval} months`;
    else if (type === 'yearly') label = interval === 1 ? 'Yearly' : `Every ${interval} years`;
    
    if (data.end_type === 'count') label += ` (${data.count} occurrences)`;
    else if (data.end_type === 'date' && data.until) label += ` (until ${new Date(data.until).toLocaleDateString()})`;
    
    return label;
  };

  return (
    <Card className="bg-white/70 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center">
          <RefreshCw className="w-5 h-5 mr-2" />
          Recurring Events
        </CardTitle>
        <CardDescription>
          Manage your recurring event series — view instances, edit patterns, or delete series
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-500 text-sm">Loading recurring events...</p>
        ) : recurringEvents.length === 0 ? (
          <p className="text-gray-500 text-sm">No recurring events found. Create one from the event creation form.</p>
        ) : (
          <div className="space-y-3">
            {recurringEvents.map(event => (
              <div key={event.id} className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                  onClick={() => loadSeries(event.id)}>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{event.title}</h4>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-sm text-blue-600 font-medium">{getRecurrenceLabel(event)}</span>
                      <span className="text-xs text-gray-500">Started: {formatDate(event.start_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); deleteSeries(event.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedSeries === event.id ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                
                {expandedSeries === event.id && (
                  <div className="border-t bg-white p-4">
                    {seriesInstances[event.id]?.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600 mb-3">{seriesInstances[event.id].length} instances</p>
                        <div className="max-h-64 overflow-y-auto space-y-1">
                          {seriesInstances[event.id].map((instance, idx) => (
                            <div key={instance.id || idx} className="flex items-center justify-between py-2 px-3 rounded hover:bg-gray-50">
                              <div>
                                <span className="text-sm">{formatDate(instance.start_at)}</span>
                                {instance.start_at !== instance.recurring_instance_date && (
                                  <span className="text-xs text-orange-500 ml-2">(modified)</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline" className="text-xs">
                                  {instance.status || 'draft'}
                                </Badge>
                                <button
                                  onClick={() => deleteInstance(event.id, instance.recurring_instance_date || instance.start_at)}
                                  className="text-red-400 hover:text-red-600 p-1"
                                  title="Delete this instance"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No instances generated yet.</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Settings Page Component
const SettingsPage = () => {
  const navigate = useNavigate();
  const { currentOrganization, token } = useAuth();
  const [userRole, setUserRole] = useState("organizer");
  const [hosts, setHosts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const { toast } = useToast();

  const [organizerSettings, setOrganizerSettings] = useState({
    defaultOrganizer: undefined,
    defaultVenue: undefined,
    defaultEventType: 'in-person',
    defaultTimezone: 'UTC',
    defaultTags: [],
    autoPublish: false
  });

  // Calendar subscription state
  const [calendarSubscriptions, setCalendarSubscriptions] = useState([]);
  const [showAddSubscription, setShowAddSubscription] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [newSubscription, setNewSubscription] = useState({
    name: '',
    feed_url: '',
    sync_frequency: 'hourly'
  });

  const [promoterSettings, setPromoterSettings] = useState({
    autoSyndicate: true,
    notificationEmail: '',
    weeklyDigest: true,
    analyticsLevel: 'basic',
    outboundWebhookEnabled: false,
    outboundWebhookUrl: '',
    outboundWebhookSecret: '',
    outboundWebhookEvents: [],
    syndicationEnabled: true,
    syndicateAll: true,
    allowedCategories: [],
    allowedHosts: [],
    allowedEventTypes: [],
    requireApproval: false,
    onlyPublished: true,
    onlyFeatured: false,
    minPrice: null,
    maxPrice: null,
    includeFree: true,
    payloadFormat: 'default',
    calendarPlugin: '',
    fieldMapping: {},
    includeFields: [],
    excludeFields: [],
    transformDates: false,
    dateFormat: 'iso'
  });

  const [templates, setTemplates] = useState([]);
  
  // Event subscription / syndication-in settings
  const [eventSubSettings, setEventSubSettings] = useState({ accept_all_events: false });
  
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    fetchData();
    loadSettings();
    loadTemplates();
  }, []);

  const fetchData = async () => {
    try {
      const [hostsRes, venuesRes, categoriesRes, promotersRes] = await Promise.all([
        axios.get(`${API}/hosts`),
        axios.get(`${API}/venues`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/promoters`)
      ]);
      
      setHosts(hostsRes.data);
      setVenues(venuesRes.data);
      setCategories(categoriesRes.data);
      setPromoters(promotersRes.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch form data",
        variant: "destructive",
      });
    }
  };

  const loadSettings = async () => {
    const orgSettings = localStorage.getItem('organizer_settings');
    const promSettings = localStorage.getItem('promoter_settings');
    
    if (orgSettings) setOrganizerSettings(JSON.parse(orgSettings));
    if (promSettings) setPromoterSettings(JSON.parse(promSettings));
    
    // Load outbound webhook settings from backend
    if (currentOrganization && token) {
      try {
        const response = await axios.get(
          `${API}/organizations/${currentOrganization.id}/outbound-webhook-settings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data) {
          setPromoterSettings(prev => ({
            ...prev,
            outboundWebhookEnabled: response.data.webhook_enabled,
            outboundWebhookUrl: response.data.webhook_url,
            outboundWebhookSecret: response.data.webhook_secret,
            outboundWebhookEvents: response.data.webhook_events || [],
            syndicateAll: response.data.syndicate_all !== undefined ? response.data.syndicate_all : true,
            payloadFormat: response.data.payload_format || 'default',
            calendarPlugin: response.data.calendar_plugin || '',
            fieldMapping: response.data.field_mapping || {},
            includeFields: response.data.include_fields || [],
            excludeFields: response.data.exclude_fields || [],
            transformDates: response.data.transform_dates || false,
            dateFormat: response.data.date_format || 'iso'
          }));
        }
      } catch (error) {
        console.error('Error loading webhook settings:', error);
      }
      
      // Load event subscription (syndication-in) settings
      try {
        const subRes = await axios.get(
          `${API}/organizations/${currentOrganization.id}/event-subscription`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEventSubSettings(subRes.data);
      } catch (error) {
        console.error('Error loading event subscription settings:', error);
      }
      
      // Load calendar subscriptions
      fetchCalendarSubscriptions();
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/event-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      // Fallback to localStorage for backward compatibility
      const saved = localStorage.getItem('event_templates');
      if (saved) {
        setTemplates(JSON.parse(saved));
      }
    }
  };

  const saveSettings = async () => {
    try {
      // Save to localStorage for backward compatibility
      localStorage.setItem('organizer_settings', JSON.stringify(organizerSettings));
      localStorage.setItem('promoter_settings', JSON.stringify(promoterSettings));
      
      // Save outbound webhook settings to backend if enabled
      if (currentOrganization && promoterSettings.outboundWebhookEnabled !== undefined) {
        await axios.put(
          `${API}/organizations/${currentOrganization.id}/outbound-webhook-settings`,
          {
            webhook_enabled: promoterSettings.outboundWebhookEnabled,
            webhook_url: promoterSettings.outboundWebhookUrl,
            webhook_secret: promoterSettings.outboundWebhookSecret,
            webhook_events: promoterSettings.outboundWebhookEvents || [],
            syndicate_all: promoterSettings.syndicateAll !== undefined ? promoterSettings.syndicateAll : true,
            payload_format: promoterSettings.payloadFormat || 'default',
            calendar_plugin: promoterSettings.calendarPlugin || '',
            field_mapping: promoterSettings.fieldMapping || {},
            include_fields: promoterSettings.includeFields || [],
            exclude_fields: promoterSettings.excludeFields || [],
            transform_dates: promoterSettings.transformDates || false,
            date_format: promoterSettings.dateFormat || 'iso'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Save event subscription settings
      if (currentOrganization) {
        try {
          await axios.put(
            `${API}/organizations/${currentOrganization.id}/event-subscription`,
            eventSubSettings,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (error) {
          console.error('Error saving event subscription settings:', error);
        }
      }
      
      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save settings",
        variant: "destructive"
      });
    }
  };


  // Calendar Subscription Management Functions
  const fetchCalendarSubscriptions = async () => {
    if (!currentOrganization || !token) return;
    
    try {
      const response = await axios.get(
        `${API}/organizations/${currentOrganization.id}/calendar-subscriptions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalendarSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching calendar subscriptions:', error);
      if (error.response?.status !== 403) {
        toast({
          title: "Error",
          description: "Failed to fetch calendar subscriptions",
          variant: "destructive"
        });
      }
    }
  };

  const addCalendarSubscription = async () => {
    if (!newSubscription.name || !newSubscription.feed_url) {
      toast({
        title: "Error",
        description: "Please provide both name and feed URL",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(
        `${API}/organizations/${currentOrganization.id}/calendar-subscriptions`,
        newSubscription,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCalendarSubscriptions([...calendarSubscriptions, response.data]);
      setNewSubscription({ name: '', feed_url: '', sync_frequency: 'hourly' });
      setShowAddSubscription(false);
      
      toast({
        title: "Success",
        description: "Calendar subscription added successfully. Initial sync started.",
      });
    } catch (error) {
      console.error('Error adding subscription:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add subscription",
        variant: "destructive"
      });
    }
  };

  const updateCalendarSubscription = async (subscriptionId, updates) => {
    try {
      const response = await axios.put(
        `${API}/organizations/${currentOrganization.id}/calendar-subscriptions/${subscriptionId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCalendarSubscriptions(calendarSubscriptions.map(sub => 
        sub.id === subscriptionId ? response.data : sub
      ));
      
      toast({
        title: "Success",
        description: "Subscription updated successfully",
      });
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update subscription",
        variant: "destructive"
      });
    }
  };

  const deleteCalendarSubscription = async (subscriptionId) => {
    if (!confirm('Are you sure you want to delete this subscription? Synced events will remain in your calendar.')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/organizations/${currentOrganization.id}/calendar-subscriptions/${subscriptionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCalendarSubscriptions(calendarSubscriptions.filter(sub => sub.id !== subscriptionId));
      
      toast({
        title: "Success",
        description: "Subscription deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete subscription",
        variant: "destructive"
      });
    }
  };

  const triggerSync = async (subscriptionId) => {
    try {
      await axios.post(
        `${API}/organizations/${currentOrganization.id}/calendar-subscriptions/${subscriptionId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Sync Started",
        description: "Calendar sync triggered. This may take a few moments.",
      });
      
      // Refresh subscriptions after a delay to show updated status
      setTimeout(fetchCalendarSubscriptions, 3000);
    } catch (error) {
      console.error('Error triggering sync:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to trigger sync",
        variant: "destructive"
      });
    }
  };


  const saveTemplates = async (templateData) => {
    try {
      // Build default_event_data conditionally
      const defaultEventData = {
        event_type: templateData.event_type,
        duration: templateData.duration,
        tags: templateData.tags || [],
        is_free: templateData.is_free !== undefined ? templateData.is_free : true,
        timezone: templateData.timezone || 'UTC',
        default_title: templateData.default_title || '',
        default_description: templateData.default_description || '',
        default_category_ids: templateData.default_category_ids || []
      };

      // Only include host_id if it has a value
      if (templateData.default_host_id) {
        defaultEventData.default_host_id = templateData.default_host_id;
      }

      // Only include venue_id if it has a value
      if (templateData.default_venue_id) {
        defaultEventData.default_venue_id = templateData.default_venue_id;
      }

      // Only include price fields if they have values
      if (templateData.default_price_min !== null && templateData.default_price_min !== undefined) {
        defaultEventData.default_price_min = templateData.default_price_min;
      }
      if (templateData.default_price_max !== null && templateData.default_price_max !== undefined) {
        defaultEventData.default_price_max = templateData.default_price_max;
      }
      if (templateData.default_max_attendees !== null && templateData.default_max_attendees !== undefined) {
        defaultEventData.default_max_attendees = templateData.default_max_attendees;
      }

      const payload = {
        name: templateData.name,
        description: templateData.description || '',
        industry_type: templateData.industry_type || 'general',
        is_default: false,
        fields_config: templateData.fields_config || [],
        default_event_data: defaultEventData,
        settings: {}
      };

      console.log('Saving template with payload:', payload);

      const response = await axios.post(`${API}/event-templates`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        await loadTemplates(); // Reload templates
        toast({
          title: "Success",
          description: "Template created successfully",
        });
      }
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle validation errors from backend
      let errorMessage = "Failed to create template";
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors
        if (errorData.detail && Array.isArray(errorData.detail)) {
          const errors = errorData.detail.map(err => 
            `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
          ).join('; ');
          errorMessage = errors;
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      console.error('Full error:', error.response?.data);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/event-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadTemplates();
      toast({
        title: "Success",
        description: "Template deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive"
      });
    }
  };

  const updateTemplate = async (templateId, templateData) => {
    try {
      // Build default_event_data conditionally
      const defaultEventData = {
        event_type: templateData.event_type,
        duration: templateData.duration,
        tags: templateData.tags || [],
        is_free: templateData.is_free !== undefined ? templateData.is_free : true,
        timezone: templateData.timezone || 'UTC',
        default_title: templateData.default_title || '',
        default_description: templateData.default_description || '',
        default_category_ids: templateData.default_category_ids || []
      };

      // Only include host_id if it has a value
      if (templateData.default_host_id) {
        defaultEventData.default_host_id = templateData.default_host_id;
      }

      // Only include venue_id if it has a value
      if (templateData.default_venue_id) {
        defaultEventData.default_venue_id = templateData.default_venue_id;
      }

      // Only include price fields if they have values
      if (templateData.default_price_min !== null && templateData.default_price_min !== undefined) {
        defaultEventData.default_price_min = templateData.default_price_min;
      }
      if (templateData.default_price_max !== null && templateData.default_price_max !== undefined) {
        defaultEventData.default_price_max = templateData.default_price_max;
      }
      if (templateData.default_max_attendees !== null && templateData.default_max_attendees !== undefined) {
        defaultEventData.default_max_attendees = templateData.default_max_attendees;
      }

      const payload = {
        name: templateData.name,
        description: templateData.description || '',
        industry_type: templateData.industry_type || 'general',
        is_default: false,
        fields_config: templateData.fields_config || [],
        default_event_data: defaultEventData,
        settings: {}
      };

      const response = await axios.put(`${API}/event-templates/${templateId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        await loadTemplates(); // Reload templates
        toast({
          title: "Success",
          description: "Template updated successfully",
        });
      }
    } catch (error) {
      console.error('Error updating template:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle validation errors from backend
      let errorMessage = "Failed to update template";
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.detail && Array.isArray(errorData.detail)) {
          const errors = errorData.detail.map(err => 
            `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
          ).join('; ');
          errorMessage = errors;
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };
  
  const deleteTemplateOld = (templateId) => {
    const updated = templates.filter(t => t.id !== templateId);
    localStorage.setItem('event_templates', JSON.stringify(updated));
    setTemplates(updated);
    toast({
      title: "Success",
      description: "Template deleted successfully",
    });
  };

  const useTemplate = (template) => {
    localStorage.setItem('selected_template', JSON.stringify(template));
    navigate('/events/create');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            </div>
            {/* Role Switcher and User Menu */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm rounded-lg p-1">
                <Button
                  variant={userRole === "organizer" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setUserRole("organizer")}
                  className={userRole === "organizer" ? "bg-brand-primary text-white" : ""}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Organizer
                </Button>
                <Button
                  variant={userRole === "promoter" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setUserRole("promoter")}
                  className={userRole === "promoter" ? "bg-purple-600 text-white border border-purple-300" : "border border-gray-300"}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Promoter
                </Button>
              </div>
              
              {/* User Menu with Sign Out */}
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {userRole === "organizer" ? (
          <div className="space-y-8">
            {/* Organizer General Settings */}
            <Card className="bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  General Preferences
                </CardTitle>
                <CardDescription>
                  Configure your default values for creating events
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Default Organizer</Label>
                    <Select value={organizerSettings.defaultOrganizer} onValueChange={(value) => 
                      setOrganizerSettings(prev => ({...prev, defaultOrganizer: value}))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select default organizer" />
                      </SelectTrigger>
                      <SelectContent>
                        {hosts.map(org => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Default Venue</Label>
                    <Select value={organizerSettings.defaultVenue} onValueChange={(value) => 
                      setOrganizerSettings(prev => ({...prev, defaultVenue: value}))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select default venue" />
                      </SelectTrigger>
                      <SelectContent>
                        {venues.map(venue => (
                          <SelectItem key={venue.id} value={venue.id}>{venue.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Default Event Type</Label>
                    <Select value={organizerSettings.defaultEventType} onValueChange={(value) => 
                      setOrganizerSettings(prev => ({...prev, defaultEventType: value}))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in-person">In-Person</SelectItem>
                        <SelectItem value="virtual">Virtual</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Default Timezone</Label>
                    <Select value={organizerSettings.defaultTimezone} onValueChange={(value) => 
                      setOrganizerSettings(prev => ({...prev, defaultTimezone: value}))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="America/New_York">Eastern Time</SelectItem>
                        <SelectItem value="America/Chicago">Central Time</SelectItem>
                        <SelectItem value="America/Denver">Mountain Time</SelectItem>
                        <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="autoPublish"
                    checked={organizerSettings.autoPublish}
                    onChange={(e) => setOrganizerSettings(prev => ({...prev, autoPublish: e.target.checked}))}
                  />
                  <Label htmlFor="autoPublish">Auto-publish events after creation</Label>
                </div>

                <div className="flex justify-end">
                  <Button onClick={saveSettings}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>



            {/* Custom Event Fields */}
            <CustomFieldsManager hosts={hosts} />

            {/* User Management */}
            <UserManagement />

            {/* Event Syndication (Inbound) Settings */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Event Syndication — Inbound
                </CardTitle>
                <CardDescription>
                  Control whether events from other organizations appear on your public calendar.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
                  <div>
                    <Label className="font-semibold">Accept All Platform Events</Label>
                    <p className="text-sm text-gray-600 mt-1">
                      When enabled, every published event across the entire platform will appear
                      on your public calendar — not just events created by your organization.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={eventSubSettings.accept_all_events || false}
                    onChange={(e) => setEventSubSettings(prev => ({
                      ...prev,
                      accept_all_events: e.target.checked
                    }))}
                  />
                </div>
                {eventSubSettings.accept_all_events && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                    ⚠️ Your public calendar will now display events from <strong>all</strong> organizations
                    on the platform. Make sure this is what you want.
                  </div>
                )}
                <Button onClick={saveSettings}>
                  Save Syndication Settings
                </Button>
              </CardContent>
            </Card>

            {/* Event Submission Webhook Settings */}
            <EventSubmissionWebhookManager hosts={hosts} currentOrganization={currentOrganization} />



            {/* Calendar Subscriptions - iCal Feed Sync */}
            <Card className="bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Calendar className="w-5 h-5 mr-2" />
                      Calendar Subscriptions
                    </CardTitle>
                    <CardDescription>
                      Subscribe to external iCal feeds to automatically import and sync events
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowAddSubscription(true)} size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Subscription
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {calendarSubscriptions.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No calendar subscriptions</h3>
                    <p className="text-gray-600 mb-6">
                      Subscribe to external calendars to automatically import events
                    </p>
                    <Button onClick={() => setShowAddSubscription(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Subscription
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {calendarSubscriptions.map((subscription) => (
                      <div 
                        key={subscription.id} 
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-lg">{subscription.name}</h4>
                              <div className="flex items-center gap-2">
                                {subscription.is_active ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Paused
                                  </span>
                                )}
                                {subscription.last_sync_status === 'success' && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    ✓ Synced
                                  </span>
                                )}
                                {subscription.last_sync_status === 'error' && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    ⚠ Error
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-1 text-sm text-gray-600">
                              <p className="font-mono text-xs bg-gray-50 px-2 py-1 rounded break-all">
                                {subscription.feed_url}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <span>Sync: {subscription.sync_frequency}</span>
                                <span>•</span>
                                <span>{subscription.events_synced_count} events</span>
                                {subscription.last_synced_at && (
                                  <>
                                    <span>•</span>
                                    <span>Last synced: {new Date(subscription.last_synced_at).toLocaleString()}</span>
                                  </>
                                )}
                              </div>
                              {subscription.last_sync_error && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                  <strong>Error:</strong> {subscription.last_sync_error}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => triggerSync(subscription.id)}
                              title="Sync now"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateCalendarSubscription(subscription.id, { is_active: !subscription.is_active })}
                              title={subscription.is_active ? "Pause" : "Resume"}
                            >
                              {subscription.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteCalendarSubscription(subscription.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Subscription Modal/Form */}
                {showAddSubscription && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                      <h3 className="text-xl font-semibold mb-4">Add Calendar Subscription</h3>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="subscription-name">Subscription Name</Label>
                          <Input
                            id="subscription-name"
                            value={newSubscription.name}
                            onChange={(e) => setNewSubscription({...newSubscription, name: e.target.value})}
                            placeholder="e.g., Venue Calendar, Festival Feed"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="feed-url">iCal Feed URL</Label>
                          <Input
                            id="feed-url"
                            type="url"
                            value={newSubscription.feed_url}
                            onChange={(e) => setNewSubscription({...newSubscription, feed_url: e.target.value})}
                            placeholder="https://example.com/calendar.ics"
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            The URL to the .ics calendar feed
                          </p>
                        </div>
                        
                        <div>
                          <Label htmlFor="sync-frequency">Sync Frequency</Label>
                          <select
                            id="sync-frequency"
                            value={newSubscription.sync_frequency}
                            onChange={(e) => setNewSubscription({...newSubscription, sync_frequency: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value="hourly">Hourly</option>
                            <option value="daily">Daily</option>
                            <option value="manual">Manual Only</option>
                          </select>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                          <p><strong>Note:</strong> Events will be automatically imported and published. Changes to the external calendar will sync based on the frequency selected.</p>
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-2 mt-6">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowAddSubscription(false);
                            setNewSubscription({ name: '', feed_url: '', sync_frequency: 'hourly' });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button onClick={addCalendarSubscription}>
                          Add Subscription
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Event Templates */}
            <Card className="bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Bookmark className="w-5 h-5 mr-2" />
                      Event Templates
                    </CardTitle>
                    <CardDescription>
                      Create and manage reusable event templates
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateTemplate(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <div className="text-center py-12">
                    <Bookmark className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates yet</h3>
                    <p className="text-gray-600 mb-6">Create templates to quickly set up similar events</p>
                    <Button onClick={() => setShowCreateTemplate(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template) => {
                      // Helper function to get category display
                      const getCategoryDisplay = (categoryIds) => {
                        if (!categoryIds || categoryIds.length === 0) return null;
                        const categoryNames = categoryIds.slice(0, 3).map(id => {
                          const cat = categories.find(c => c.id === id);
                          if (!cat) return null;
                          const parent = categories.find(c => c.id === cat.parent_id);
                          return parent ? `${parent.name} → ${cat.name}` : cat.name;
                        }).filter(Boolean);
                        return categoryNames;
                      };

                      const displayCategories = getCategoryDisplay(template.default_event_data?.default_category_ids);

                      return (
                      <Card key={template.id} className="bg-white/50 backdrop-blur-sm">
                        <CardHeader>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <CardDescription>{template.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 mb-4">
                            {displayCategories && displayCategories.length > 0 && (
                              <div className="text-sm">
                                <strong>Categories:</strong> {displayCategories.join(', ')}
                                {template.default_event_data?.default_category_ids?.length > 3 && (
                                  <span className="text-gray-500"> +{template.default_event_data.default_category_ids.length - 3} more</span>
                                )}
                              </div>
                            )}
                            {template.default_event_data?.event_type && (
                              <div className="text-sm"><strong>Event Type:</strong> {template.default_event_data.event_type}</div>
                            )}
                            {template.default_event_data?.duration && (
                              <div className="text-sm"><strong>Duration:</strong> {template.default_event_data.duration} hours</div>
                            )}
                            {template.fields_config && template.fields_config.length > 0 && (
                              <div className="text-sm"><strong>Fields:</strong> {template.fields_config.length} custom fields</div>
                            )}
                            {template.default_event_data?.tags && template.default_event_data.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {template.default_event_data.tags.map((tag, index) => (
                                  <Badge key={index} variant="secondary" className="text-xs">{tag}</Badge>
                                ))}
                              </div>
                            )}
                            {/* Backward compatibility for old localStorage templates */}
                            {template.event_type && !template.default_event_data && (
                              <>
                                <div className="text-sm"><strong>Event Type:</strong> {template.event_type}</div>
                                <div className="text-sm"><strong>Duration:</strong> {template.duration} hours</div>
                                {template.tags && template.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {template.tags.map((tag, index) => (
                                      <Badge key={index} variant="secondary" className="text-xs">{tag}</Badge>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex justify-between gap-2">
                            <Button variant="outline" onClick={() => useTemplate(template)}>
                              Use Template
                            </Button>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => setEditingTemplate(template)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => deleteTemplate(template.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recurring Events Management */}
            <RecurringEventsManager token={token} organizationId={currentOrganization?.id} />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Promoter General Settings */}
            <Card className="bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Share2 className="w-5 h-5 mr-2" />
                  General Preferences
                </CardTitle>
                <CardDescription>
                  Configure your basic promoter settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Notification Email</Label>
                    <Input
                      type="email"
                      value={promoterSettings.notificationEmail}
                      onChange={(e) => setPromoterSettings(prev => ({...prev, notificationEmail: e.target.value}))}
                      placeholder="notifications@example.com"
                    />
                  </div>

                  <div>
                    <Label>Analytics Level</Label>
                    <Select value={promoterSettings.analyticsLevel} onValueChange={(value) => 
                      setPromoterSettings(prev => ({...prev, analyticsLevel: value}))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="detailed">Detailed</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="autoSyndicate"
                    checked={promoterSettings.autoSyndicate}
                    onChange={(e) => setPromoterSettings(prev => ({...prev, autoSyndicate: e.target.checked}))}
                  />
                  <Label htmlFor="autoSyndicate">Auto-syndicate events based on rules</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="weeklyDigest"
                    checked={promoterSettings.weeklyDigest}
                    onChange={(e) => setPromoterSettings(prev => ({...prev, weeklyDigest: e.target.checked}))}
                  />
                  <Label htmlFor="weeklyDigest">Receive weekly event digest</Label>
                </div>

                <div className="flex justify-end">
                  <Button onClick={saveSettings}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Outbound Webhook Configuration */}
            <Card className="bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Send className="w-5 h-5 mr-2" />
                  Outbound Webhook Configuration
                </CardTitle>
                <CardDescription>
                  Send event data to external calendars when events are created or updated. Uses your Aggregation Rules to filter which events are sent.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>How it works:</strong> When you create or update an event in our system, we'll automatically send the event data to your external calendar endpoint. This allows you to manage events here while publishing to your own calendar system.
                  </p>
                  <p className="text-sm text-blue-800">
                    <strong>📋 Aggregation Rules Apply:</strong> Only events that pass your Aggregation Rules (configured below) will be sent to the webhook. {promoterSettings.syndicateAll ? 'Currently set to "Aggregate All" - exclusion rules will filter out events.' : 'Currently using inclusion rules - only matching events will be sent.'}
                  </p>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    id="enableOutboundWebhook"
                    checked={promoterSettings.outboundWebhookEnabled || false}
                    onChange={(e) => setPromoterSettings(prev => ({...prev, outboundWebhookEnabled: e.target.checked}))}
                  />
                  <Label htmlFor="enableOutboundWebhook" className="font-medium">
                    Enable Outbound Webhook
                  </Label>
                </div>

                {promoterSettings.outboundWebhookEnabled && (
                  <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                    <div>
                      <Label htmlFor="outboundWebhookUrl">External Calendar Webhook URL</Label>
                      <Input
                        id="outboundWebhookUrl"
                        type="url"
                        value={promoterSettings.outboundWebhookUrl || ''}
                        onChange={(e) => setPromoterSettings(prev => ({...prev, outboundWebhookUrl: e.target.value}))}
                        placeholder="https://your-calendar.com/api/webhooks/events"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The URL where event data will be sent (must be HTTPS)
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="outboundWebhookSecret">Webhook Secret (Optional)</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          id="outboundWebhookSecret"
                          type="password"
                          value={promoterSettings.outboundWebhookSecret || ''}
                          onChange={(e) => setPromoterSettings(prev => ({...prev, outboundWebhookSecret: e.target.value}))}
                          placeholder="Enter a secret for authentication"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
                            let secret = '';
                            for (let i = 0; i < 32; i++) {
                              secret += chars.charAt(Math.floor(Math.random() * chars.length));
                            }
                            setPromoterSettings(prev => ({...prev, outboundWebhookSecret: secret}));
                            toast({ title: "Secret Generated!", description: "New webhook secret created" });
                          }}
                        >
                          Generate
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        This secret will be sent in the X-Webhook-Secret header for authentication
                      </p>
                    </div>

                    <div>
                      <Label>Events to Send</Label>
                      <div className="space-y-2 mt-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="webhookEventCreate"
                            checked={promoterSettings.outboundWebhookEvents?.includes('event.created') || false}
                            onChange={(e) => {
                              const events = promoterSettings.outboundWebhookEvents || [];
                              const updated = e.target.checked 
                                ? [...events, 'event.created']
                                : events.filter(ev => ev !== 'event.created');
                              setPromoterSettings(prev => ({...prev, outboundWebhookEvents: updated}));
                            }}
                          />
                          <Label htmlFor="webhookEventCreate" className="font-normal">Event Created</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="webhookEventUpdate"
                            checked={promoterSettings.outboundWebhookEvents?.includes('event.updated') || false}
                            onChange={(e) => {
                              const events = promoterSettings.outboundWebhookEvents || [];
                              const updated = e.target.checked 
                                ? [...events, 'event.updated']
                                : events.filter(ev => ev !== 'event.updated');
                              setPromoterSettings(prev => ({...prev, outboundWebhookEvents: updated}));
                            }}
                          />
                          <Label htmlFor="webhookEventUpdate" className="font-normal">Event Updated</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="webhookEventDelete"
                            checked={promoterSettings.outboundWebhookEvents?.includes('event.deleted') || false}
                            onChange={(e) => {
                              const events = promoterSettings.outboundWebhookEvents || [];
                              const updated = e.target.checked 
                                ? [...events, 'event.deleted']
                                : events.filter(ev => ev !== 'event.deleted');
                              setPromoterSettings(prev => ({...prev, outboundWebhookEvents: updated}));
                            }}
                          />
                          <Label htmlFor="webhookEventDelete" className="font-normal">Event Deleted</Label>
                        </div>
                      </div>
                    </div>

                    {/* Payload Format Configuration */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold mb-3">Payload Format Configuration</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="payloadFormat">Format Template</Label>
                          <select
                            id="payloadFormat"
                            value={promoterSettings.payloadFormat || 'default'}
                            onChange={(e) => setPromoterSettings(prev => ({...prev, payloadFormat: e.target.value}))}
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value="default">Default (WhatsHappeningEvents)</option>
                            <option value="wordpress">WordPress Calendar Plugin</option>
                            <option value="google">Google Calendar API</option>
                            <option value="outlook">Microsoft Outlook Calendar</option>
                            <option value="icalendar">iCalendar (ICS)</option>
                            <option value="custom">Custom (with field mapping)</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Choose a pre-configured format or use custom field mapping
                          </p>
                        </div>

                        {promoterSettings.payloadFormat === 'wordpress' && (
                          <div>
                            <Label htmlFor="calendarPlugin">Calendar Plugin Name</Label>
                            <Input
                              id="calendarPlugin"
                              type="text"
                              value={promoterSettings.calendarPlugin || ''}
                              onChange={(e) => setPromoterSettings(prev => ({...prev, calendarPlugin: e.target.value}))}
                              placeholder="e.g., the-events-calendar, events-manager, modern-events-calendar"
                              className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              The WordPress calendar plugin you're using (leave empty if your custom plugin doesn't need it)
                            </p>
                          </div>
                        )}

                        {promoterSettings.payloadFormat === 'custom' && (
                          <>
                            <div>
                              <Label>Date Format</Label>
                              <div className="flex items-center space-x-2 mt-2">
                                <input
                                  type="checkbox"
                                  id="transformDates"
                                  checked={promoterSettings.transformDates || false}
                                  onChange={(e) => setPromoterSettings(prev => ({...prev, transformDates: e.target.checked}))}
                                />
                                <Label htmlFor="transformDates" className="font-normal">Transform date format</Label>
                              </div>
                              
                              {promoterSettings.transformDates && (
                                <select
                                  value={promoterSettings.dateFormat || 'iso'}
                                  onChange={(e) => setPromoterSettings(prev => ({...prev, dateFormat: e.target.value}))}
                                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md"
                                >
                                  <option value="iso">ISO 8601 (2025-01-20T19:00:00Z)</option>
                                  <option value="unix">Unix Timestamp (1737396000)</option>
                                  <option value="rfc3339">RFC 3339 (2025-01-20T19:00:00Z)</option>
                                  <option value="human">Human Readable (January 20, 2025 at 07:00 PM)</option>
                                </select>
                              )}
                            </div>

                            <div>
                              <Label htmlFor="excludeFields">Exclude Fields (comma-separated)</Label>
                              <Input
                                id="excludeFields"
                                type="text"
                                value={(promoterSettings.excludeFields || []).join(', ')}
                                onChange={(e) => {
                                  const fields = e.target.value.split(',').map(f => f.trim()).filter(Boolean);
                                  setPromoterSettings(prev => ({...prev, excludeFields: fields}));
                                }}
                                placeholder="e.g., created_at, updated_at, internal_notes"
                                className="mt-1"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Fields to exclude from the payload
                              </p>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                              <p className="text-xs text-blue-800">
                                <strong>Field Mapping Example:</strong> Use exclude_fields to remove sensitive data. 
                                The payload will include all event fields except those you exclude.
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold mb-2">Example Payload ({promoterSettings.payloadFormat || 'default'})</h4>
                      <pre className="text-xs bg-gray-800 text-green-400 p-3 rounded overflow-x-auto">
{promoterSettings.payloadFormat === 'google' ? `{
  "summary": "Event Title",
  "description": "Event description",
  "start": {
    "dateTime": "2025-01-20T19:00:00Z",
    "timeZone": "UTC"
  },
  "end": {
    "dateTime": "2025-01-20T22:00:00Z",
    "timeZone": "UTC"
  },
  "location": "Venue Name",
  "status": "confirmed"
}` : promoterSettings.payloadFormat === 'outlook' ? `{
  "subject": "Event Title",
  "body": {
    "contentType": "HTML",
    "content": "Event description"
  },
  "start": {
    "dateTime": "2025-01-20T19:00:00Z",
    "timeZone": "UTC"
  },
  "end": {
    "dateTime": "2025-01-20T22:00:00Z",
    "timeZone": "UTC"
  },
  "location": {
    "displayName": "Venue Name"
  }
}` : `{
  "event_type": "event.created",
  "timestamp": "2025-01-16T12:00:00Z",
  "event": {
    "id": "uuid",
    "title": "Event Title",
    "description": "...",
    "start_at": "2025-01-20T19:00:00Z",
    "end_at": "2025-01-20T22:00:00Z",
    "venue": {...},
    "categories": [...],
    ...
  }
}`}
                      </pre>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={saveSettings}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Webhook Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Public Event Submissions Settings */}
            <PublicSubmissionSettingsManager promoters={promoters} />

            {/* Featured Events Configuration */}
            <FeaturedEventsManager promoters={promoters} organizers={hosts} categories={categories} token={token} />



            {/* Aggregation Rules */}
            <AggregationRulesManager 
              organizers={hosts}
              categories={categories}
              promoterId={promoters[0]?.id}
              token={token}
              promoterSettings={promoterSettings}
              setPromoterSettings={setPromoterSettings}
            />
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      <CreateTemplateModal 
        isOpen={showCreateTemplate}
        onClose={() => setShowCreateTemplate(false)}
        onSave={async (template) => {
          await saveTemplates(template);
          setShowCreateTemplate(false);
        }}
      />

      {/* Edit Template Modal */}
      <CreateTemplateModal 
        isOpen={!!editingTemplate}
        onClose={() => setEditingTemplate(null)}
        existingTemplate={editingTemplate}
        onSave={async (template) => {
          await updateTemplate(editingTemplate.id, template);
          setEditingTemplate(null);
        }}
      />
    </div>
  );
};

// Aggregation Rules Manager Component
const AggregationRulesManager = ({ organizers, categories, promoterId, token, promoterSettings, setPromoterSettings }) => {
  const [rules, setRules] = useState([]);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [publishersList, setPublishersList] = useState([]);
  const { toast } = useToast();
  const [showPublishersModal, setShowPublishersModal] = useState(false);
  const [editingPublisher, setEditingPublisher] = useState(null);
  
  // Alias organizers as hosts for backwards compatibility
  const hosts = organizers || [];

  // Helper function to get category names from IDs
  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0 || !categories) return [];
    return categoryIds
      .map(id => {
        const category = categories.find(cat => cat.id === id);
        if (!category) return null;
        const parent = categories.find(cat => cat.id === category.parent_id);
        return parent ? `${parent.name} → ${category.name}` : category.name;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    loadRules();
    loadPublishers();
  }, []);

  const loadRules = async () => {
    try {
      const response = await axios.get(`${API}/aggregation-rules`);
      if (response.data) {
        setRules(response.data);
      }
    } catch (error) {
      console.error('Error loading aggregation rules:', error);
      // Set default rules if none exist
      // Default rules
      const defaultRules = [
        {
          id: '1',
          name: 'Tech Events in SF',
          type: 'inclusion',
          enabled: true,
          conditions: {
            categories: ['Technology'],
            location: { city: 'San Francisco', radius: 25 },
            hosts: [],
            tags: [],
            priceRange: { min: null, max: null },
            isFree: null
          }
        },
        {
          id: '2', 
          name: 'Free Community Events',
          type: 'inclusion',
          enabled: true,
          conditions: {
            categories: [],
            hosts: [],
            tags: ['community', 'free'],
            priceRange: { min: null, max: null },
            isFree: true,
            minAttendees: 100
          }
        },
        {
          id: '3',
          name: 'Block Spam Organizers',
          type: 'exclusion',
          enabled: true,
          conditions: {
            hosts: [],
            tags: ['spam', 'promotional'],
            categories: []
          }
        }
      ];
      setRules(defaultRules);
    }
  };

  const saveRule = async (newRule) => {
    try {
      const response = await axios.post(`${API}/aggregation-rules`, {
        name: newRule.name,
        rule_type: newRule.type,
        enabled: newRule.active !== false,
        conditions: newRule.condition || {},
        priority: newRule.priority || 1
      });
      
      await loadRules();
      toast({
        title: "Success",
        description: "Aggregation rule created successfully",
      });
    } catch (error) {
      console.error('Error saving rule:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create rule",
        variant: "destructive",
      });
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/aggregation-rules/${ruleId}`);
      await loadRules();
      toast({
        title: "Success",
        description: "Aggregation rule deleted",
      });
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast({
        title: "Error",
        description: "Failed to delete rule",
        variant: "destructive",
      });
    }
  };

  const loadPublishers = async () => {
    if (!promoterId) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/promoters/${promoterId}/authorized-publishers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Transform backend format to match frontend format
      const publishers = response.data.map(pub => ({
        id: pub.id,
        name: pub.name,
        website: pub.website,
        contactEmail: pub.contact_email,
        description: pub.description,
        status: pub.status,
        addedDate: pub.created_at.split('T')[0],
        submitterId: pub.submitter_id,
        submitterEmail: pub.submitter_email,
        submitterOrganization: pub.submitter_organization
      }));
      
      setPublishersList(publishers);
    } catch (error) {
      console.error('Error loading publishers:', error);
      toast({
        title: "Error",
        description: "Failed to load authorized publishers",
        variant: "destructive"
      });
    }
  };

  const saveRules = (newRules) => {
    localStorage.setItem('syndication_rules', JSON.stringify(newRules));
    setRules(newRules);
  };

  const toggleRule = (ruleId) => {
    const updated = rules.map(rule => 
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    );
    saveRules(updated);
  };

  const updatePublisherStatus = async (publisherId, status) => {
    if (!promoterId) return;
    
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/promoters/${promoterId}/authorized-publishers/${publisherId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      const updated = publishersList.map(pub => 
        pub.id === publisherId ? { ...pub, status } : pub
      );
      setPublishersList(updated);
      
      toast({
        title: "Success",
        description: `Publisher ${status}`,
      });
    } catch (error) {
      console.error('Error updating publisher:', error);
      toast({
        title: "Error",
        description: "Failed to update publisher status",
        variant: "destructive"
      });
    }
  };

  const removePublisher = async (publisherId) => {
    if (!promoterId) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/promoters/${promoterId}/authorized-publishers/${publisherId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      const updated = publishersList.filter(pub => pub.id !== publisherId);
      setPublishersList(updated);
      
      toast({
        title: "Success",
        description: "Publisher removed successfully",
      });
    } catch (error) {
      console.error('Error removing publisher:', error);
      toast({
        title: "Error",
        description: "Failed to remove publisher",
        variant: "destructive"
      });
    }
  };

  const editPublisher = (publisher) => {
    setEditingPublisher(publisher);
    setShowPublishersModal(true);
  };

  const getRuleTypeColor = (type) => {
    return type === 'inclusion' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getPublisherStatusColor = (status) => {
    switch (status) {
      case 'authorized': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {/* Authorized Publishers */}
      <Card className="bg-white/70 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                Authorized Publishers
              </CardTitle>
              <CardDescription>
                Manage which websites can syndicate your curated events
              </CardDescription>
            </div>
            <Button onClick={() => setShowPublishersModal(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Publisher
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {publishersList.map((publisher) => (
              <div key={publisher.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <h4 className="font-semibold">{publisher.name}</h4>
                  <p className="text-sm text-gray-600">{publisher.website}</p>
                  <p className="text-xs text-gray-500">Added: {new Date(publisher.addedDate).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={`${getPublisherStatusColor(publisher.status)} border-0`}>
                    {publisher.status}
                  </Badge>
                  
                  {/* Edit Button */}
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => editPublisher(publisher)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Edit publisher"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  
                  {/* Status Actions */}
                  {publisher.status === 'pending' && (
                    <Button 
                      size="sm" 
                      onClick={() => updatePublisherStatus(publisher.id, 'authorized')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Approve
                    </Button>
                  )}
                  {publisher.status === 'authorized' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => updatePublisherStatus(publisher.id, 'blocked')}
                      className="text-red-600 hover:text-red-700"
                    >
                      Block
                    </Button>
                  )}
                  {publisher.status === 'blocked' && (
                    <Button 
                      size="sm" 
                      onClick={() => updatePublisherStatus(publisher.id, 'authorized')}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Unblock
                    </Button>
                  )}
                  
                  {/* Remove Button */}
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => removePublisher(publisher.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Remove publisher"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Aggregation Rules */}
      <Card className="bg-white/70 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Filter className="w-5 h-5 mr-2" />
                Aggregation Rules
              </CardTitle>
              <CardDescription>
                Control which events are automatically included or excluded from your calendar
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateRule(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Aggregate All Toggle */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="aggregateAll"
                checked={promoterSettings.syndicateAll || false}
                onChange={(e) => {
                  setPromoterSettings(prev => ({...prev, syndicateAll: e.target.checked}));
                  toast({
                    title: e.target.checked ? "Aggregate All Enabled" : "Aggregate All Disabled",
                    description: e.target.checked 
                      ? "All events will be aggregated. Rules below become exclusion filters." 
                      : "Only events matching inclusion rules will be aggregated."
                  });
                }}
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="aggregateAll" className="font-semibold text-blue-900 cursor-pointer">
                  Aggregate All Events
                </Label>
                <p className="text-sm text-blue-700 mt-1">
                  {promoterSettings.syndicateAll 
                    ? "✅ All events are included by default. Rules below act as exclusion filters to block specific events."
                    : "ℹ️ Only events matching the inclusion rules below will be aggregated. Add rules to specify which events to include."}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {rules.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold">{rule.name}</h4>
                    <Badge className={`${getRuleTypeColor(promoterSettings.syndicateAll ? 'exclusion' : rule.type)} border-0`}>
                      {promoterSettings.syndicateAll ? 'exclusion' : rule.type}
                    </Badge>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    {rule.conditions.categories?.length > 0 && (
                      <p><strong>Categories:</strong> {getCategoryNames(rule.conditions.categories).join(', ')}</p>
                    )}
                    {rule.conditions.tags?.length > 0 && (
                      <p><strong>Tags:</strong> {rule.conditions.tags.join(', ')}</p>
                    )}
                    {rule.conditions.organizers?.length > 0 && (
                      <p><strong>Organizers:</strong> {rule.conditions.organizers.length} selected</p>
                    )}
                    {rule.conditions.location?.city && (
                      <p><strong>Location:</strong> {rule.conditions.location.city} ({rule.conditions.location.radius}km radius)</p>
                    )}
                    {rule.conditions.isFree !== null && (
                      <p><strong>Price:</strong> {rule.conditions.isFree ? 'Free only' : 'Paid only'}</p>
                    )}
                    {rule.conditions.minAttendees && (
                      <p><strong>Min Attendees:</strong> {rule.conditions.minAttendees}+</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleRule(rule.id)}
                    className={rule.enabled ? "text-green-600" : "text-gray-500"}
                  >
                    {rule.enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => deleteRule(rule.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Rule Modal */}
      <CreateRuleModal 
        isOpen={showCreateRule}
        onClose={() => setShowCreateRule(false)}
        onSave={async (rule) => {
          await saveRule(rule);
          setShowCreateRule(false);
        }}
        organizers={hosts}
        categories={categories}
      />

      {/* Add/Edit Publisher Modal */}
      <AddPublisherModal 
        isOpen={showPublishersModal}
        onClose={() => {
          setShowPublishersModal(false);
          setEditingPublisher(null);
        }}
        editingPublisher={editingPublisher}
        onSave={async (publisher) => {
          if (!promoterId) return;
          
          try {
            if (editingPublisher) {
              // Update existing publisher via API
              await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/promoters/${promoterId}/authorized-publishers/${editingPublisher.id}`,
                {
                  name: publisher.name,
                  website: publisher.website,
                  contact_email: publisher.contactEmail,
                  description: publisher.description
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Update local state
              const updated = publishersList.map(pub => 
                pub.id === editingPublisher.id ? { ...pub, ...publisher } : pub
              );
              setPublishersList(updated);
              
              toast({
                title: "Success", 
                description: "Publisher updated successfully",
              });
            } else {
              // Add new publisher via API
              const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/promoters/${promoterId}/authorized-publishers`,
                {
                  name: publisher.name,
                  website: publisher.website,
                  contact_email: publisher.contactEmail,
                  description: publisher.description
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Add to local state
              const newPublisher = {
                id: response.data.id,
                name: response.data.name,
                website: response.data.website,
                contactEmail: response.data.contact_email,
                description: response.data.description,
                status: response.data.status,
                addedDate: response.data.created_at.split('T')[0]
              };
              setPublishersList([...publishersList, newPublisher]);
              
              toast({
                title: "Success", 
                description: "Publisher added successfully",
              });
            }
          } catch (error) {
            console.error('Error saving publisher:', error);
            toast({
              title: "Error",
              description: `Failed to ${editingPublisher ? 'update' : 'add'} publisher`,
              variant: "destructive"
            });
          }
          
          setShowPublishersModal(false);
          setEditingPublisher(null);
        }}
      />
    </>
  );
};

// Create Rule Modal Component
const CreateRuleModal = ({ isOpen, onClose, onSave, hosts, categories }) => {
  const [ruleData, setRuleData] = useState({
    name: '',
    type: 'inclusion',
    enabled: true,
    conditions: {
      categories: [],
      hosts: [],
      tags: [],
      location: { city: '', radius: 25 },
      priceRange: { min: '', max: '' },
      isFree: null,
      minAttendees: ''
    }
  });

  const [tagInput, setTagInput] = useState('');

  const handleAddTag = () => {
    if (tagInput.trim() && !ruleData.conditions.tags.includes(tagInput.trim())) {
      setRuleData(prev => ({
        ...prev,
        conditions: {
          ...prev.conditions,
          tags: [...prev.conditions.tags, tagInput.trim()]
        }
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setRuleData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        tags: prev.conditions.tags.filter(tag => tag !== tagToRemove)
      }
    }));
  };

  const toggleOrganizer = (organizerId) => {
    setRuleData(prev => {
      const hosts = prev.conditions.hosts.includes(organizerId)
        ? prev.conditions.hosts.filter(o => o !== organizerId)
        : [...prev.conditions.hosts, organizerId];
      
      return {
        ...prev,
        conditions: { ...prev.conditions, hosts }
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(ruleData);
    setRuleData({
      name: '',
      type: 'inclusion',
      enabled: true,
      conditions: {
        categories: [],
        hosts: [],
        tags: [],
        location: { city: '', radius: 25 },
        priceRange: { min: '', max: '' },
        isFree: null,
        minAttendees: ''
      }
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Aggregation Rule</DialogTitle>
          <DialogDescription>
            Define criteria for automatically including or excluding events
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name">Rule Name *</Label>
              <Input
                id="name"
                value={ruleData.name}
                onChange={(e) => setRuleData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Tech Events in SF"
                required
              />
            </div>

            <div>
              <Label>Rule Type</Label>
              <Select value={ruleData.type} onValueChange={(value) => 
                setRuleData(prev => ({...prev, type: value}))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inclusion">Inclusion (Include events matching criteria)</SelectItem>
                  <SelectItem value="exclusion">Exclusion (Exclude events matching criteria)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Categories</Label>
            <HierarchicalCategorySelector
              selectedCategories={ruleData.conditions.categories}
              onChange={(categoryIds) => setRuleData(prev => ({
                ...prev,
                conditions: { ...prev.conditions, categories: categoryIds }
              }))}
              multiple={true}
            />
          </div>

          <div>
            <Label>Organizers</Label>
            <div className="max-h-32 overflow-y-auto border rounded p-2 mt-2">
              {hosts && hosts.length > 0 ? hosts.map((organizer) => (
                <div key={organizer.id} className="flex items-center space-x-2 py-1">
                  <input
                    type="checkbox"
                    id={`org-${organizer.id}`}
                    checked={ruleData.conditions.hosts.includes(organizer.id)}
                    onChange={() => toggleOrganizer(organizer.id)}
                  />
                  <Label htmlFor={`org-${organizer.id}`} className="text-sm">{organizer.name}</Label>
                </div>
              )) : (
                <div className="text-sm text-gray-500">No organizers available</div>
              )}
            </div>
          </div>

          <div>
            <Label>Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add a tag"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {ruleData.conditions.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                  {tag} ×
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Location</Label>
              <div className="space-y-2">
                <Input
                  value={ruleData.conditions.location.city}
                  onChange={(e) => setRuleData(prev => ({
                    ...prev,
                    conditions: {
                      ...prev.conditions,
                      location: { ...prev.conditions.location, city: e.target.value }
                    }
                  }))}
                  placeholder="City name"
                />
                <Input
                  type="number"
                  value={ruleData.conditions.location.radius}
                  onChange={(e) => setRuleData(prev => ({
                    ...prev,
                    conditions: {
                      ...prev.conditions,
                      location: { ...prev.conditions.location, radius: parseInt(e.target.value) }
                    }
                  }))}
                  placeholder="Radius in km"
                />
              </div>
            </div>

            <div>
              <Label>Price Settings</Label>
              <div className="space-y-2">
                <Select 
                  value={ruleData.conditions.isFree === null ? 'any' : ruleData.conditions.isFree ? 'free' : 'paid'} 
                  onValueChange={(value) => 
                    setRuleData(prev => ({
                      ...prev,
                      conditions: {
                        ...prev.conditions,
                        isFree: value === 'any' ? null : value === 'free'
                      }
                    }))
                  }>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any price</SelectItem>
                    <SelectItem value="free">Free events only</SelectItem>
                    <SelectItem value="paid">Paid events only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <Label>Minimum Attendees</Label>
            <Input
              type="number"
              value={ruleData.conditions.minAttendees}
              onChange={(e) => setRuleData(prev => ({
                ...prev,
                conditions: { ...prev.conditions, minAttendees: e.target.value }
              }))}
              placeholder="Leave empty for no minimum"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Create Rule</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Publisher Modal Component
const AddPublisherModal = ({ isOpen, onClose, onSave, editingPublisher }) => {
  const [publisherData, setPublisherData] = useState({
    name: '',
    website: '',
    contactEmail: '',
    description: ''
  });

  // Update form when editingPublisher changes
  useEffect(() => {
    if (editingPublisher) {
      setPublisherData({
        name: editingPublisher.name,
        website: editingPublisher.website || '',
        contactEmail: editingPublisher.contactEmail || '',
        description: editingPublisher.description || ''
      });
    } else {
      setPublisherData({ name: '', website: '', contactEmail: '', description: '' });
    }
  }, [editingPublisher, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(publisherData);
    setPublisherData({ name: '', website: '', contactEmail: '', description: '' });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingPublisher ? 'Edit Publisher' : 'Add Publisher'}</DialogTitle>
          <DialogDescription>
            {editingPublisher 
              ? 'Update the publisher information'
              : 'Add a new website that can syndicate your events'
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="publisherName">Organization Name *</Label>
            <Input
              id="publisherName"
              value={publisherData.name}
              onChange={(e) => setPublisherData(prev => ({...prev, name: e.target.value}))}
              placeholder="e.g., Baltimore Arts Association"
              required
            />
          </div>

          <div>
            <Label htmlFor="website">Website URL</Label>
            <Input
              id="website"
              type="url"
              value={publisherData.website}
              onChange={(e) => setPublisherData(prev => ({...prev, website: e.target.value}))}
              placeholder="https://example.com"
            />
          </div>

          <div>
            <Label htmlFor="contactEmail">Contact Email</Label>
            <Input
              id="contactEmail"
              type="email"
              value={publisherData.contactEmail || ''}
              onChange={(e) => setPublisherData(prev => ({...prev, contactEmail: e.target.value}))}
              placeholder="contact@example.com"
            />
          </div>

          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <textarea
              id="description"
              className="w-full px-3 py-2 border rounded-md min-h-[80px]"
              value={publisherData.description || ''}
              onChange={(e) => setPublisherData(prev => ({...prev, description: e.target.value}))}
              placeholder="Brief description of the organization..."
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">
              {editingPublisher ? 'Update Publisher' : 'Add Publisher'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Custom Fields Manager Component
const CustomFieldsManager = ({ hosts }) => {
  const [customFields, setCustomFields] = useState([]);
  const [showCreateField, setShowCreateField] = useState(false);
  const [loading, setLoading] = useState(true);
  const { currentOrganization, token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (currentOrganization) {
      fetchCustomFields();
    } else {
      setLoading(false);
    }
  }, [currentOrganization]);

  const fetchCustomFields = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/custom-field-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      toast({
        title: "Error",
        description: "Failed to fetch custom fields",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteCustomField = async (fieldId) => {
    try {
      await axios.delete(`${API}/custom-field-templates/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomFields(prev => prev.filter(field => field.id !== fieldId));
      toast({
        title: "Success",
        description: "Custom field deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting custom field:', error);
      toast({
        title: "Error",
        description: "Failed to delete custom field",
        variant: "destructive",
      });
    }
  };

  const toggleFieldActive = async (fieldId, currentActive) => {
    try {
      await axios.put(`${API}/custom-field-templates/${fieldId}`, {
        active: !currentActive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomFields(prev => 
        prev.map(field => 
          field.id === fieldId ? { ...field, active: !currentActive } : field
        )
      );
      toast({
        title: "Success",
        description: `Custom field ${!currentActive ? 'activated' : 'deactivated'}`,
      });
    } catch (error) {
      console.error('Error toggling custom field:', error);
      toast({
        title: "Error",
        description: "Failed to update custom field",
        variant: "destructive",
      });
    }
  };

  const getFieldTypeIcon = (fieldType) => {
    switch (fieldType) {
      case 'text': return <Type className="w-4 h-4" />;
      case 'textarea': return <FileText className="w-4 h-4" />;
      case 'number': return <span className="w-4 h-4 flex items-center justify-center text-xs font-bold">#</span>;
      case 'date': return <Calendar className="w-4 h-4" />;
      case 'dropdown': return <Filter className="w-4 h-4" />;
      case 'checkbox': return <span className="w-4 h-4 flex items-center justify-center text-xs">☑</span>;
      default: return <Type className="w-4 h-4" />;
    }
  };

  const getFieldTypeColor = (fieldType) => {
    switch (fieldType) {
      case 'text': return 'bg-blue-100 text-blue-800';
      case 'textarea': return 'bg-purple-100 text-purple-800';
      case 'number': return 'bg-green-100 text-green-800';
      case 'date': return 'bg-orange-100 text-orange-800';
      case 'dropdown': return 'bg-yellow-100 text-yellow-800';
      case 'checkbox': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="bg-white/70 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Custom Event Fields
            </CardTitle>
            <CardDescription>
              Create custom fields that will appear on your event forms
            </CardDescription>
          </div>
          <Button onClick={() => setShowCreateField(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Field
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading custom fields...</p>
          </div>
        ) : customFields.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No custom fields yet. Click "Add Field" to create your first custom event field.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {customFields.map((field) => (
              <div key={field.id} className="flex items-center justify-between p-4 border rounded-lg bg-white/30">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`p-1 rounded ${getFieldTypeColor(field.field_type)}`}>
                      {getFieldTypeIcon(field.field_type)}
                    </div>
                    <h4 className="font-semibold">{field.field_name}</h4>
                    <Badge className={`${getFieldTypeColor(field.field_type)} border-0 text-xs`}>
                      {field.field_type}
                    </Badge>
                    {field.required && (
                      <Badge variant="outline" className="text-xs border-red-300 text-red-600">
                        Required
                      </Badge>
                    )}
                    {!field.active && (
                      <Badge variant="outline" className="text-xs border-gray-300 text-gray-500">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Key:</strong> {field.field_key}</p>
                    {field.placeholder && (
                      <p><strong>Placeholder:</strong> {field.placeholder}</p>
                    )}
                    {field.help_text && (
                      <p><strong>Help:</strong> {field.help_text}</p>
                    )}
                    {field.options && field.options.length > 0 && (
                      <p><strong>Options:</strong> {field.options.join(', ')}</p>
                    )}
                    {field.default_value && (
                      <p><strong>Default:</strong> {field.default_value}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleFieldActive(field.id, field.active)}
                    className={field.active ? "text-green-600" : "text-gray-500"}
                  >
                    {field.active ? 'Active' : 'Inactive'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => deleteCustomField(field.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create Custom Field Modal */}
      <CreateCustomFieldModal 
        isOpen={showCreateField}
        onClose={() => setShowCreateField(false)}
        onSave={(newField) => {
          setCustomFields(prev => [...prev, newField]);
          setShowCreateField(false);
          toast({
            title: "Success",
            description: "Custom field created successfully",
          });
        }}
      />
    </Card>
  );
};

// Public Submission Settings Manager Component
const PublicSubmissionSettingsManager = ({ promoters }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const { currentOrganization } = useAuth();  // Get organization from auth context
  const { toast } = useToast();

  useEffect(() => {
    console.log('🔍 PublicSubmissionSettings: currentOrganization changed:', currentOrganization);
    if (currentOrganization && currentOrganization.id) {
      console.log('✅ Fetching settings for organization:', currentOrganization.id);
      fetchSettings(currentOrganization.id);
    } else {
      console.warn('⚠️ No currentOrganization found, setting loading to false');
      setLoading(false);
    }
  }, [currentOrganization]);

  const fetchSettings = async (organizationId) => {
    if (!organizationId) {
      console.error('❌ organizationId is missing');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.log('📡 Fetching settings from API:', `${API}/public-submission-settings/${organizationId}`);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/public-submission-settings/${organizationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ Settings fetched successfully:', response.data);
      setSettings(response.data);
    } catch (error) {
      console.error('❌ Error fetching settings:', error);
      console.error('Error response:', error.response?.data);
      // Set default settings if none exist
      setSettings({
        organization_id: organizationId,
        submissions_enabled: false,
        show_on_calendar: true,
        show_share_button: true,
        auto_approval_enabled: false,
        approved_emails: [],
        approved_domains: [],
        blocked_emails: [],
        blocked_domains: [],
        require_phone: false,
        require_organization: false,
        max_submissions_per_day: 10,
        notification_email: '',
        webhook_url: '',
        webhook_events: []
      });
    } finally {
      console.log('✅ Setting loading to false');
      setLoading(false);
    }
  };

  const updateSettings = async (updatedSettings) => {
    if (!currentOrganization) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/public-submission-settings/${currentOrganization.id}`, updatedSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...updatedSettings }));
      toast({
        title: "Success",
        description: "Public submission settings updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update settings",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="bg-white/70 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Share2 className="w-5 h-5 mr-2" />
              Public Event Submissions
            </CardTitle>
            <CardDescription>
              Configure how public users can submit events to your calendar
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-6">
            
            {/* Basic Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Settings</h3>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="submissions_enabled"
                  checked={settings.submissions_enabled || false}
                  onChange={(e) => updateSettings({ submissions_enabled: e.target.checked })}
                />
                <Label htmlFor="submissions_enabled" className="text-base">Enable public event submissions</Label>
              </div>
              
              {settings.submissions_enabled && (
                <>
                  <div className="flex items-center space-x-2 ml-6">
                    <input
                      type="checkbox"
                      id="show_on_calendar"
                      checked={settings.show_on_calendar || false}
                      onChange={(e) => updateSettings({ show_on_calendar: e.target.checked })}
                    />
                    <Label htmlFor="show_on_calendar">Show submission option on calendar page</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-6">
                    <input
                      type="checkbox"
                      id="show_share_button"
                      checked={settings.show_share_button || false}
                      onChange={(e) => updateSettings({ show_share_button: e.target.checked })}
                    />
                    <Label htmlFor="show_share_button">Show "Share An Event" button in header</Label>
                  </div>
                </>
              )}
            </div>

            {settings.submissions_enabled && (
              <>
                {/* Auto-Approval Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Auto-Approval</h3>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="auto_approval_enabled"
                      checked={settings.auto_approval_enabled || false}
                      onChange={(e) => updateSettings({ auto_approval_enabled: e.target.checked })}
                    />
                    <Label htmlFor="auto_approval_enabled">Enable auto-approval for trusted submitters</Label>
                  </div>
                  
                  {settings.auto_approval_enabled && (
                    <div className="ml-6 space-y-4">
                      <div>
                        <Label>Approved Email Addresses</Label>
                        <Textarea
                          placeholder="Enter email addresses (one per line)"
                          value={(settings.approved_emails || []).join('\n')}
                          onChange={(e) => updateSettings({ 
                            approved_emails: e.target.value.split('\n').filter(email => email.trim()) 
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Events from these email addresses will be automatically approved
                        </p>
                      </div>
                      
                      <div>
                        <Label>Approved Email Domains</Label>
                        <Input
                          placeholder="e.g., company.com, university.edu"
                          value={(settings.approved_domains || []).join(', ')}
                          onChange={(e) => updateSettings({ 
                            approved_domains: e.target.value.split(',').map(d => d.trim()).filter(d => d) 
                          })}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Events from these domains will be automatically approved
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Moderation Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Moderation</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="require_phone"
                        checked={settings.require_phone || false}
                        onChange={(e) => updateSettings({ require_phone: e.target.checked })}
                      />
                      <Label htmlFor="require_phone">Require phone number</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="require_organization"
                        checked={settings.require_organization || false}
                        onChange={(e) => updateSettings({ require_organization: e.target.checked })}
                      />
                      <Label htmlFor="require_organization">Require organization name</Label>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="max_submissions">Maximum submissions per day (per IP)</Label>
                    <Input
                      id="max_submissions"
                      type="number"
                      min="1"
                      max="100"
                      value={settings.max_submissions_per_day || 10}
                      onChange={(e) => updateSettings({ max_submissions_per_day: parseInt(e.target.value) || 10 })}
                      className="w-32"
                    />
                  </div>
                  
                  <div>
                    <Label>Blocked Email Addresses</Label>
                    <Textarea
                      placeholder="Enter email addresses to block (one per line)"
                      value={(settings.blocked_emails || []).join('\n')}
                      onChange={(e) => updateSettings({ 
                        blocked_emails: e.target.value.split('\n').filter(email => email.trim()) 
                      })}
                      rows={2}
                    />
                  </div>
                </div>

                {/* Notifications */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Notifications</h3>
                  
                  <div>
                    <Label htmlFor="notification_email">Email notifications</Label>
                    <Input
                      id="notification_email"
                      type="email"
                      placeholder="your@email.com"
                      value={settings.notification_email || ''}
                      onChange={(e) => updateSettings({ notification_email: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Get notified when new events are submitted
                    </p>
                  </div>
                  
                  <div>
                    <Label>Inbound Webhook Configuration</Label>
                    <div className="space-y-3 p-3 bg-gray-50 rounded-md">
                      <div>
                        <Label className="text-sm font-medium">Webhook URL</Label>
                        <div className="flex items-center space-x-2 mt-1">
                          <Input
                            readOnly
                            value={`${API}/webhooks/auto-approval/${currentOrganization?.id || 'ORGANIZATION_ID'}`}
                            className="bg-white font-mono text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(`${API}/webhooks/auto-approval/${currentOrganization?.id || 'ORGANIZATION_ID'}`);
                              toast({ title: "Copied!", description: "Webhook URL copied to clipboard" });
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="webhook_secret" className="text-sm font-medium">Webhook Secret</Label>
                        <div className="flex items-center space-x-2 mt-1">
                          <Input
                            id="webhook_secret"
                            type={showSecret ? "text" : "password"}
                            placeholder="Click 'Generate' to create a secure secret"
                            value={settings.webhook_secret || ''}
                            onChange={(e) => updateSettings({ webhook_secret: e.target.value })}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowSecret(!showSecret)}
                            className="px-2"
                          >
                            {showSecret ? "Hide" : "Show"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Generate a cryptographically secure 32-character secret
                              const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
                              let secret = '';
                              for (let i = 0; i < 32; i++) {
                                secret += chars.charAt(Math.floor(Math.random() * chars.length));
                              }
                              updateSettings({ webhook_secret: secret });
                              toast({ title: "Secret Generated!", description: "New webhook secret created" });
                            }}
                          >
                            Generate
                          </Button>
                          {settings.webhook_secret && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(settings.webhook_secret);
                                toast({ title: "Copied!", description: "Webhook secret copied to clipboard" });
                              }}
                            >
                              Copy
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          This secret authenticates external systems calling your webhook
                        </p>
                      </div>
                      
                      <div className="text-xs text-gray-600 space-y-1">
                        <p><strong>Usage:</strong> External systems can POST to this URL to add/remove users from auto-approval lists</p>
                        <p><strong>Payload:</strong> {"{"}"action": "add|remove", "email": "user@example.com", "webhook_secret": "your_secret"{"}"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!settings.submissions_enabled && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Share2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Public Submissions Disabled</h3>
                <p className="text-gray-600">
                  Enable public event submissions to allow community members to submit events for your review.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Create Custom Field Modal Component
const CreateCustomFieldModal = ({ isOpen, onClose, onSave }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [fieldData, setFieldData] = useState({
    field_name: '',
    field_key: '',
    field_type: 'text',
    required: false,
    placeholder: '',
    default_value: '',
    options: [],
    help_text: '',
    active: true,
    sort_order: 0
  });

  const [optionInput, setOptionInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Auto-generate field key from field name
  useEffect(() => {
    if (fieldData.field_name) {
      const key = fieldData.field_name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50);
      setFieldData(prev => ({ ...prev, field_key: key }));
    }
  }, [fieldData.field_name]);

  const addOption = () => {
    if (optionInput.trim() && !fieldData.options.includes(optionInput.trim())) {
      setFieldData(prev => ({
        ...prev,
        options: [...prev.options, optionInput.trim()]
      }));
      setOptionInput('');
    }
  };

  const removeOption = (optionToRemove) => {
    setFieldData(prev => ({
      ...prev,
      options: prev.options.filter(option => option !== optionToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    const payload = {
      ...fieldData
    };
    
    try {
      const response = await axios.post(`${API}/custom-field-templates`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave(response.data);
      
      // Reset form
      setFieldData({
        field_name: '',
        field_key: '',
        field_type: 'text',
        required: false,
        placeholder: '',
        default_value: '',
        options: [],
        help_text: '',
        active: true,
        sort_order: 0
      });
      setOptionInput('');
    } catch (error) {
      console.error('Error creating custom field:', error);
      console.error('Error response:', error.response?.data);
      console.error('Payload sent:', payload);
      
      let errorMessage = "Failed to create custom field";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => 
            `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
          ).join('; ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Custom Event Field</DialogTitle>
          <DialogDescription>
            Add a custom field that will appear on your event registration forms
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="field_name">Field Name *</Label>
              <Input
                id="field_name"
                value={fieldData.field_name}
                onChange={(e) => setFieldData(prev => ({...prev, field_name: e.target.value}))}
                placeholder="e.g., Registration Type"
                required
              />
            </div>

            <div>
              <Label htmlFor="field_key">Field Key *</Label>
              <Input
                id="field_key"
                value={fieldData.field_key}
                onChange={(e) => setFieldData(prev => ({...prev, field_key: e.target.value}))}
                placeholder="e.g., registration_type"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Used internally - auto-generated from field name</p>
            </div>
          </div>

          <div>
            <Label>Field Type</Label>
            <Select value={fieldData.field_type} onValueChange={(value) => 
              setFieldData(prev => ({...prev, field_type: value, options: value === 'dropdown' ? prev.options : []}))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text Input</SelectItem>
                <SelectItem value="textarea">Text Area</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="dropdown">Dropdown</SelectItem>
                <SelectItem value="checkbox">Checkbox</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="placeholder">Placeholder Text</Label>
              <Input
                id="placeholder"
                value={fieldData.placeholder}
                onChange={(e) => setFieldData(prev => ({...prev, placeholder: e.target.value}))}
                placeholder="Hint text for users"
              />
            </div>

            <div>
              <Label htmlFor="default_value">Default Value</Label>
              <Input
                id="default_value"
                value={fieldData.default_value}
                onChange={(e) => setFieldData(prev => ({...prev, default_value: e.target.value}))}
                placeholder="Default value (optional)"
              />
            </div>
          </div>

          {fieldData.field_type === 'dropdown' && (
            <div>
              <Label>Dropdown Options</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  placeholder="Add an option"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                />
                <Button type="button" onClick={addOption} variant="outline">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {fieldData.options.map((option, index) => (
                  <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => removeOption(option)}>
                    {option} ×
                  </Badge>
                ))}
              </div>
              {fieldData.options.length === 0 && (
                <p className="text-sm text-gray-500">No options added yet</p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="help_text">Help Text</Label>
            <Textarea
              id="help_text"
              value={fieldData.help_text}
              onChange={(e) => setFieldData(prev => ({...prev, help_text: e.target.value}))}
              placeholder="Additional instructions for users"
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="required"
              checked={fieldData.required}
              onChange={(e) => setFieldData(prev => ({...prev, required: e.target.checked}))}
            />
            <Label htmlFor="required">This field is required</Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Field'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Template Modal
const CreateTemplateModal = ({ isOpen, onClose, onSave, existingTemplate }) => {
  const { token } = useAuth();
  
  // Suppress ResizeObserver errors globally
  React.useEffect(() => {
    const errorHandler = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop')) {
        e.stopImmediatePropagation();
        e.stopPropagation();
        return false;
      }
    };
    
    window.addEventListener('error', errorHandler);
    
    return () => {
      window.removeEventListener('error', errorHandler);
    };
  }, []);

  // Initialize template data from existingTemplate when editing
  useEffect(() => {
    if (existingTemplate) {
      setTemplateData({
        name: existingTemplate.name || '',
        description: existingTemplate.description || '',
        industry_type: existingTemplate.industry_type || 'general',
        event_type: existingTemplate.default_event_data?.event_type || 'in-person',
        duration: existingTemplate.default_event_data?.duration || 2,
        tags: existingTemplate.default_event_data?.tags || [],
        is_free: existingTemplate.default_event_data?.is_free !== undefined ? existingTemplate.default_event_data.is_free : true,
        timezone: existingTemplate.default_event_data?.timezone || 'UTC',
        default_host_id: existingTemplate.default_event_data?.default_host_id || '',
        default_venue_id: existingTemplate.default_event_data?.default_venue_id || '',
        default_title: existingTemplate.default_event_data?.default_title || '',
        default_description: existingTemplate.default_event_data?.default_description || '',
        default_category_ids: existingTemplate.default_event_data?.default_category_ids || [],
        default_price_min: existingTemplate.default_event_data?.default_price_min || null,
        default_price_max: existingTemplate.default_event_data?.default_price_max || null,
        default_max_attendees: existingTemplate.default_event_data?.default_max_attendees || null
      });
    }
  }, [existingTemplate]);
  
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    industry_type: 'general',
    event_type: 'in-person',
    duration: 2,
    tags: [],
    is_free: true,
    timezone: 'UTC',
    default_host_id: '',
    default_venue_id: '',
    default_title: '',
    default_description: '',
    default_category_ids: [],
    default_price_min: null,
    default_price_max: null,
    default_max_attendees: null
  });

  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [tagInput, setTagInput] = useState('');
  
  useEffect(() => {
    if (isOpen) {
      loadIndustries();
      loadCategories();
      loadHosts();
      loadVenues();
    }
  }, [isOpen]);
  
  const loadIndustries = async () => {
    try {
      const response = await axios.get(`${API}/event-templates/industries/list`);
      if (response.data) {
        setIndustries(response.data);
      }
    } catch (error) {
      console.error('Error loading industries:', error);
    }
  };
  
  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    }
  };

  const loadHosts = async () => {
    try {
      const response = await axios.get(`${API}/hosts`, { headers: { Authorization: `Bearer ${token}` } });
      setHosts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading hosts:', error);
      setHosts([]);
    }
  };

  const loadVenues = async () => {
    try {
      const response = await axios.get(`${API}/venues`, { headers: { Authorization: `Bearer ${token}` } });
      setVenues(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading venues:', error);
      setVenues([]);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !templateData.tags.includes(tagInput.trim())) {
      setTemplateData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTemplateData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    onSave({
      name: templateData.name,
      description: templateData.description,
      industry_type: templateData.industry_type,
      event_type: templateData.event_type,
      duration: templateData.duration,
      tags: templateData.tags,
      is_free: templateData.is_free,
      timezone: templateData.timezone,
      default_host_id: templateData.default_host_id,
      default_venue_id: templateData.default_venue_id,
      default_title: templateData.default_title,
      default_description: templateData.default_description,
      default_category_ids: templateData.default_category_ids,
      default_price_min: templateData.default_price_min,
      default_price_max: templateData.default_price_max,
      default_max_attendees: templateData.default_max_attendees,
      fields_config: []
    });
    
    // Reset form
    setTemplateData({
      name: '',
      description: '',
      industry_type: 'general',
      event_type: 'in-person',
      duration: 2,
      tags: [],
      is_free: true,
      timezone: 'UTC',
      default_host_id: '',
      default_venue_id: '',
      default_title: '',
      default_description: '',
      default_category_ids: [],
      default_price_min: null,
      default_price_max: null,
      default_max_attendees: null
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existingTemplate ? 'Edit Event Template' : 'Create Event Template'}</DialogTitle>
          <DialogDescription>
            Save common event settings as a template for quick reuse
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="name">Template Name *</Label>
            <Input
              id="name"
              value={templateData.name}
              onChange={(e) => setTemplateData(prev => ({...prev, name: e.target.value}))}
              placeholder="e.g., Tech Meetup Template"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Template Description</Label>
            <Textarea
              id="description"
              value={templateData.description}
              onChange={(e) => setTemplateData(prev => ({...prev, description: e.target.value}))}
              placeholder="Describe what this template is for"
              rows={2}
            />
          </div>

          {/* Event Defaults Section */}
          <div className="border-t pt-3 mt-3">
            <Label className="text-base font-semibold mb-2 block">Event Default Values</Label>
            
            <div className="space-y-3">
              <div>
                <Label>Event Title</Label>
                <Input
                  value={templateData.default_title}
                  onChange={(e) => setTemplateData(prev => ({...prev, default_title: e.target.value}))}
                  placeholder="e.g., Monthly Tech Meetup"
                />
              </div>

              <div>
                <Label>Event Description</Label>
                <Textarea
                  value={templateData.default_description}
                  onChange={(e) => setTemplateData(prev => ({...prev, default_description: e.target.value}))}
                  placeholder="Description for events using this template"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Default Host/Organizer</Label>
                    <AddHostModal onHostAdded={(newHost) => {
                      setHosts(prev => [...prev, newHost]);
                      setTemplateData(prev => ({...prev, default_host_id: newHost.id}));
                    }} />
                  </div>
                  <SearchableSelect
                    options={hosts}
                    value={templateData.default_host_id}
                    onChange={(value) => setTemplateData(prev => ({...prev, default_host_id: value}))}
                    placeholder="Search hosts..."
                    displayKey="name"
                    valueKey="id"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Default Venue</Label>
                    <AddVenueModal onVenueAdded={(newVenue) => {
                      setVenues(prev => [...prev, newVenue]);
                      setTemplateData(prev => ({...prev, default_venue_id: newVenue.id}));
                    }} />
                  </div>
                  <SearchableSelect
                    options={venues}
                    value={templateData.default_venue_id}
                    onChange={(value) => setTemplateData(prev => ({...prev, default_venue_id: value}))}
                    placeholder="Search venues..."
                    displayKey="name"
                    valueKey="id"
                    getDisplayText={(venue) => `${venue.name} - ${venue.city}`}
                  />
                </div>
              </div>

              <div>
                <Label>Categories</Label>
                <HierarchicalCategorySelector
                  selectedCategories={templateData.default_category_ids}
                  onChange={(categoryIds) => setTemplateData(prev => ({
                    ...prev,
                    default_category_ids: categoryIds
                  }))}
                  multiple={true}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Price Min ($)</Label>
                  <Input
                    type="number"
                    value={templateData.default_price_min || ''}
                    onChange={(e) => setTemplateData(prev => ({...prev, default_price_min: e.target.value ? parseFloat(e.target.value) : null}))}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    disabled={templateData.is_free}
                  />
                </div>

                <div>
                  <Label>Price Max ($)</Label>
                  <Input
                    type="number"
                    value={templateData.default_price_max || ''}
                    onChange={(e) => setTemplateData(prev => ({...prev, default_price_max: e.target.value ? parseFloat(e.target.value) : null}))}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    disabled={templateData.is_free}
                  />
                </div>

                <div>
                  <Label>Max Attendees</Label>
                  <Input
                    type="number"
                    value={templateData.default_max_attendees || ''}
                    onChange={(e) => setTemplateData(prev => ({...prev, default_max_attendees: e.target.value ? parseInt(e.target.value) : null}))}
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Event Type</Label>
              <Select value={templateData.event_type} onValueChange={(value) => 
                setTemplateData(prev => ({...prev, event_type: value}))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Duration (hours)</Label>
              <Input
                type="number"
                value={templateData.duration}
                onChange={(e) => setTemplateData(prev => ({...prev, duration: parseFloat(e.target.value)}))}
                min="0.5"
                step="0.5"
              />
            </div>
          </div>

          <div>
            <Label>Default Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add a tag"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {templateData.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                  {tag} ×
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_free"
              checked={templateData.is_free}
              onChange={(e) => setTemplateData(prev => ({...prev, is_free: e.target.checked}))}
            />
            <Label htmlFor="is_free">Default to free events</Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Create Template</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Event Performance Detail Component
// Event Performance Page
const EventPerformancePage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 p-6">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardContent className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h2>
              <p className="text-gray-600">The event you're looking for doesn't exist or has been removed.</p>
              <Link to="/" className="inline-block mt-4">
                <Button>Back to Dashboard</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{event.title}</h1>
            <p className="text-gray-600">Event Performance Analytics</p>
          </div>
        </div>

        {/* Event Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Status</label>
                <div className="mt-1">
                  <Badge className={`${event.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {event.status}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Event Date</label>
                <div className="mt-1 text-sm text-gray-900">
                  {new Date(event.start_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Event Type</label>
                <div className="mt-1 text-sm text-gray-900 capitalize">{event.event_type || 'In-Person'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Analytics Section */}
        <EventAnalytics eventId={eventId} />
      </div>
    </div>
  );
};

const EventPerformance = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const eventRes = await axios.get(`${API}/events/${eventId}`);
      setEvent(eventRes.data);
      
      // Mock analytics data (in real app, this would come from analytics API)
      setAnalytics({
        calendarsDeployedTo: Math.floor(Math.random() * 12) + 1,
        totalViews: Math.floor(Math.random() * 2500) + 100,
        registrationClicks: Math.floor(Math.random() * 150) + 10,
        viewsByCalendar: [
          { name: "TechEvents.com", views: 850 },
          { name: "SF Community Calendar", views: 420 },
          { name: "StartupEvents.io", views: 380 },
          { name: "Bay Area Tech", views: 290 }
        ],
        viewsOverTime: [
          { date: "2025-08-15", views: 45 },
          { date: "2025-08-16", views: 78 },
          { date: "2025-08-17", views: 92 },
          { date: "2025-08-18", views: 156 },
          { date: "2025-08-19", views: 203 },
          { date: "2025-08-20", views: 187 },
          { date: "2025-08-21", views: 234 }
        ]
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch event details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Event not found</h2>
          <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Event Performance</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Event Header */}
        <Card className="bg-white/70 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl">{event.title}</CardTitle>
                <CardDescription className="mt-2">
                  {formatDate(event.start_at)} • {event.venue_id ? 'In-Person' : 'Virtual'}
                </CardDescription>
              </div>
              <Badge className={`${event.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {event.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">{event.description}</p>
            <div className="flex flex-wrap gap-2">
              {event.tags.map((tag, index) => (
                <Badge key={index} variant="secondary">{tag}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white/60 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Calendars</CardTitle>
              <Globe className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{analytics.calendarsDeployedTo}</div>
              <p className="text-xs text-muted-foreground">Deployed to calendars</p>
            </CardContent>
          </Card>

          <Card className="bg-white/60 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{analytics.totalViews}</div>
              <p className="text-xs text-muted-foreground">Across all calendars</p>
            </CardContent>
          </Card>

          <Card className="bg-white/60 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Registration Clicks</CardTitle>
              <MousePointer className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{analytics.registrationClicks}</div>
              <p className="text-xs text-muted-foreground">Click-through rate: {((analytics.registrationClicks / analytics.totalViews) * 100).toFixed(1)}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Views by Calendar */}
        <Card className="bg-white/70 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Views by Calendar</CardTitle>
            <CardDescription>Breakdown of views across different promotional calendars</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.viewsByCalendar.map((calendar, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{calendar.name}</div>
                    <div className="text-sm text-gray-500">{calendar.views} views</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${(calendar.views / analytics.totalViews) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">
                      {((calendar.views / analytics.totalViews) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Views Over Time */}
        <Card className="bg-white/70 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Views Over Time</CardTitle>
            <CardDescription>Daily view trends for the past week</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.viewsOverTime.map((day, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="text-sm font-medium">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${(day.views / Math.max(...analytics.viewsOverTime.map(d => d.views))) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium w-12 text-right">{day.views}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Organizer Dashboard Component
const OrganizerDashboard = ({ 
  events, hosts, venues, categories, analytics, searchTerm, setSearchTerm, 
  filterStatus, setFilterStatus, hidePastEvents, setHidePastEvents, deleteEvent, duplicateEvent, publishEvent, unpublishEvent, publishingEventId, getOrganizerName, 
  getVenueName, formatDate, getStatusColor 
}) => {
  return (
    <>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Events</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{events.length}</div>
            <p className="text-xs text-muted-foreground">Total events created</p>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {events.filter(e => e.status === 'published').length}
            </div>
            <p className="text-xs text-muted-foreground">Live events</p>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Draft Events</CardTitle>
            <Edit className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {events.filter(e => e.status === 'draft').length}
            </div>
            <p className="text-xs text-muted-foreground">Unpublished events</p>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.total_views || analytics.visibility_views || 0}</div>
            <p className="text-xs text-muted-foreground">Event page views</p>
          </CardContent>
        </Card>
      </div>

      {/* Comprehensive Analytics */}
      <div className="mb-8">
        <GlobalAnalyticsDashboard analytics={analytics} />
      </div>

      {/* Organizer Content */}
      <EventAnalyticsBatch
        eventIds={events.filter(e => e.status === 'published').map(e => e.id)}
        onLoad={() => {}}
      />
      <EventList 
        title="My Events"
        description="Manage your events and track performance"
        events={events}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        hidePastEvents={hidePastEvents}
        setHidePastEvents={setHidePastEvents}
        getOrganizerName={getOrganizerName}
        getVenueName={getVenueName}
        formatDate={formatDate}
        getStatusColor={getStatusColor}
        showActions={true}
        deleteEvent={deleteEvent}
        duplicateEvent={duplicateEvent}
        publishEvent={publishEvent}
        unpublishEvent={unpublishEvent}
        publishingEventId={publishingEventId}
        showPerformance={true}
      />
    </>
  );
};

// Promoter Dashboard Component
const PromoterDashboard = ({ 
  events, hosts, venues, categories, searchTerm, setSearchTerm,
  filterStatus, setFilterStatus, hidePastEvents, setHidePastEvents, publishingEventId, getOrganizerName, getVenueName, 
  formatDate, getStatusColor
}) => {
  const [featuredEvents, setFeaturedEvents] = useState([]);

  const addToFeatured = (eventId) => {
    if (!featuredEvents.includes(eventId)) {
      setFeaturedEvents([...featuredEvents, eventId]);
    }
  };

  const removeFromFeatured = (eventId) => {
    setFeaturedEvents(featuredEvents.filter(id => id !== eventId));
  };

  return (
    <>
      {/* Promoter Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Events</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{events.filter(e => e.status === 'published').length}</div>
            <p className="text-xs text-muted-foreground">Ready to promote</p>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Featured Events</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {featuredEvents.length}
            </div>
            <p className="text-xs text-muted-foreground">In your showcase</p>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Organizers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{hosts.length}</div>
            <p className="text-xs text-muted-foreground">Creating events</p>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aggregation Rules</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3</div>
            <p className="text-xs text-muted-foreground">Auto-curation rules</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="discover" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="discover">Discover Events</TabsTrigger>
          <TabsTrigger value="featured">Featured Events</TabsTrigger>
          <TabsTrigger value="calendar">Calendar Builder</TabsTrigger>
          <TabsTrigger value="rules">Aggregation Rules</TabsTrigger>
        </TabsList>
        
        <TabsContent value="discover" className="space-y-6">
          <EventList 
            title="Discover Events"
            description="Find and promote events from hosts"
            events={events.filter(e => e.status === 'published')}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus="published"
            setFilterStatus={setFilterStatus}
            hidePastEvents={hidePastEvents}
            setHidePastEvents={setHidePastEvents}
            getOrganizerName={getOrganizerName}
            getVenueName={getVenueName}
            formatDate={formatDate}
            getStatusColor={getStatusColor}
            showActions={false}
            publishingEventId={publishingEventId}
            featuredEvents={featuredEvents}
            addToFeatured={addToFeatured}
            removeFromFeatured={removeFromFeatured}
            promoterMode={true}
          />
        </TabsContent>
        
        <TabsContent value="featured" className="space-y-6">
          <Card className="bg-white/60 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Featured Events</CardTitle>
              <CardDescription>Events you're actively promoting</CardDescription>
            </CardHeader>
            <CardContent>
              {featuredEvents.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No featured events yet</h3>
                  <p className="text-gray-600 mb-6">Start by adding events from the Discover tab</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.filter(e => featuredEvents.includes(e.id)).map((event) => (
                    <EventCard 
                      key={event.id}
                      event={event}
                      getOrganizerName={getOrganizerName}
                      getVenueName={getVenueName}
                      formatDate={formatDate}
                      getStatusColor={getStatusColor}
                      showActions={false}
                      publishingEventId={publishingEventId}
                      featuredEvents={featuredEvents}
                      removeFromFeatured={removeFromFeatured}
                      promoterMode={true}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="calendar" className="space-y-6">
          <CalendarBuilder />
        </TabsContent>
        
        <TabsContent value="rules" className="space-y-6">
          <Card className="bg-white/60 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Aggregation Rules</CardTitle>
              <CardDescription>Automatically curate events based on your criteria</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Tech Events in SF</h4>
                      <p className="text-sm text-gray-600">Category: Technology, Location: San Francisco</p>
                    </div>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Free Community Events</h4>
                      <p className="text-sm text-gray-600">Price: Free, Max attendees: &gt;100</p>
                    </div>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Weekend Music Events</h4>
                      <p className="text-sm text-gray-600">Category: Music, Days: Saturday-Sunday</p>
                    </div>
                    <Badge variant="outline">Paused</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
};

// Calendar Builder Component
const CalendarBuilder = () => {
  const { token, currentOrganization } = useAuth();
  const [promoter, setPromoter] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    console.log('CalendarBuilder useAuth:', {
      hasToken: !!token,
      hasOrg: !!currentOrganization,
      orgId: currentOrganization?.id || 'none'
    });
    
    if (token && currentOrganization) {
      console.log('✅ Initializing calendar builder...');
      initializeCalendarBuilder();
    } else {
      console.log('⏳ Waiting for auth...');
      setLoading(false);
    }
  }, [token, currentOrganization]);

  const initializeCalendarBuilder = async () => {
    console.log('initializeCalendarBuilder called');
    try {
      // Get promoters with authentication
      console.log('Fetching promoters...');
      const promotersRes = await axios.get(`${API}/promoters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Promoters response:', promotersRes.data);
      let currentPromoter = promotersRes.data[0];
      
      if (!currentPromoter) {
        // Create a demo promoter
        const newPromoterRes = await axios.post(`${API}/promoters`, {
          name: "Demo Promoter",
          email: "demo@example.com",
          organization: "Demo Organization"
        });
        currentPromoter = newPromoterRes.data;
      }
      
      setPromoter(currentPromoter);
      
      // Load templates for this organization with authentication
      console.log('Fetching calendar templates for organization:', currentOrganization.id);
      const templatesRes = await axios.get(
        `${API}/organizations/${currentOrganization.id}/calendar-templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Templates response:', templatesRes.data);
      let orgTemplates = templatesRes.data || [];
      
      // Create a default template if none exist
      if (orgTemplates.length === 0) {
        const defaultTemplateRes = await axios.post(
          `${API}/promoters/${currentPromoter.id}/calendar-templates`, 
          {
            name: "Default Calendar",
            is_default: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        orgTemplates = [defaultTemplateRes.data];
      }
      
      setTemplates(orgTemplates);
      
      // Migrate old hover settings to new split settings if needed
      let templateToSet = orgTemplates.find(t => t.is_default) || orgTemplates[0];
      if (templateToSet?.calendar_config) {
        const config = templateToSet.calendar_config;
        
        // If old settings exist but new ones don't, migrate them
        if (config.enableHover !== undefined && config.enableDayHover === undefined) {
          config.enableDayHover = config.enableHover;
          config.enableEventHover = config.enableHover;
          config.dayHoverDelay = config.hoverDelay || 300;
          config.eventHoverDelay = config.hoverDelay || 300;
          
          // Save the migrated settings
          try {
            await axios.put(
              `${API}/promoters/${currentPromoter.id}/calendar-templates/${templateToSet.id}`,
              { calendar_config: config },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (error) {
            console.error('Error migrating hover settings:', error);
          }
        }
      }
      
      setCurrentTemplate(templateToSet);
      
    } catch (error) {
      console.error('Error initializing calendar builder:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load calendar builder",
        variant: "destructive",
      });
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const refreshCurrentTemplate = async () => {
    if (!promoter || !currentTemplate) return;
    
    try {
      const templateRes = await axios.get(
        `${API}/promoters/${promoter.id}/calendar-templates/${currentTemplate.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentTemplate(templateRes.data);
    } catch (error) {
      console.error('Error refreshing template:', error);
    }
  };

  const updateTemplate = async (updates) => {
    if (!currentTemplate || !promoter) return;
    
    try {
      const updatedTemplateRes = await axios.put(
        `${API}/promoters/${promoter.id}/calendar-templates/${currentTemplate.id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedTemplate = updatedTemplateRes.data;
      setCurrentTemplate(updatedTemplate);
      setTemplates(templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t));
      
      toast({
        title: "Success",
        description: "Calendar settings updated",
      });
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: "Error", 
        description: "Failed to update calendar settings",
        variant: "destructive",
      });
    }
  };

  const updateThemeColors = (colorUpdates) => {
    const newThemeConfig = {
      ...currentTemplate.theme_config,
      colors: {
        ...currentTemplate.theme_config.colors,
        ...colorUpdates
      }
    };
    updateTemplate({ theme_config: newThemeConfig });
  };

  const updateHeaderConfig = (headerUpdates) => {
    const newHeaderConfig = {
      ...currentTemplate.header_config,
      ...headerUpdates
    };
    updateTemplate({ header_config: newHeaderConfig });
  };

  const updateCalendarConfig = (calendarUpdates) => {
    const newCalendarConfig = {
      ...currentTemplate.calendar_config,
      ...calendarUpdates
    };
    updateTemplate({ calendar_config: newCalendarConfig });
  };

  const updateFooterConfig = (footerUpdates) => {
    const newFooterConfig = {
      ...currentTemplate.footer_config,
      ...footerUpdates
    };
    updateTemplate({ footer_config: newFooterConfig });
  };

  if (loading) {
    return <div className="text-center py-8">Loading calendar builder...</div>;
  }

  if (!currentTemplate) {
    return <div className="text-center py-8">No calendar template found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Calendar Builder Header */}
      <Card className="bg-white/60 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Calendar Builder</CardTitle>
              <CardDescription>Customize your event calendar page design and settings</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => {
                  // Open preview in new tab
                  const previewUrl = `/calendar-preview/${currentTemplate.id}`;
                  window.open(previewUrl, '_blank');
                }}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview in New Tab
              </Button>
              <Button 
                variant={previewMode ? "default" : "outline"} 
                onClick={() => setPreviewMode(!previewMode)}
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewMode ? "Edit Mode" : "Inline Preview"}
              </Button>
              <Button 
                variant="default"
                onClick={() => setShowPublishModal(true)}
              >
                <Save className="w-4 h-4 mr-2" />
                Publish Calendar
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {previewMode ? (
        <CalendarPreview template={currentTemplate} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Panel */}
          <div className="space-y-6">
            <CalendarSettingsPanel 
              template={currentTemplate}
              promoter={promoter}
              refreshCurrentTemplate={refreshCurrentTemplate}
              updateThemeColors={updateThemeColors}
              updateHeaderConfig={updateHeaderConfig}
              updateCalendarConfig={updateCalendarConfig}
              updateFooterConfig={updateFooterConfig}
            />
          </div>
          
          {/* Live Preview Panel */}
          <div className="space-y-6">
            <CalendarPreview template={currentTemplate} />
          </div>
        </div>
      )}
      
      {/* Publish Calendar Modal */}
      {showPublishModal && (
        <PublishCalendarModal
          isOpen={showPublishModal}
          onClose={() => setShowPublishModal(false)}
          promoter={promoter}
          template={currentTemplate}
        />
      )}
    </div>
  );
};

// Logo Upload Button Component
const LogoUploadButton = ({ onUploadSuccess, promoterId, templateId, onTemplateRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const fileInputRef = React.useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('=== LOGO UPLOAD DEBUG ===');
    console.log('File selected:', { name: file.name, type: file.type, size: file.size });
    console.log('Template ID:', templateId);
    console.log('Promoter ID:', promoterId);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error", 
        description: "File size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      const uploadUrl = `${API}/promoters/${promoterId}/calendar-templates/${templateId}/logo`;
      
      console.log('Upload URL:', uploadUrl);
      
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Making logo upload request...');
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response not OK:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Upload successful:', data);

      if (data.logo_url) {
        // Logo URL is now a public GCS URL, use it directly
        console.log('Logo URL (public GCS):', data.logo_url);
        
        // Update the header config immediately
        onUploadSuccess(data.logo_url);
        
        // Trigger template refresh to get updated data
        if (onTemplateRefresh) {
          await onTemplateRefresh();
        }
        
        toast({
          title: "Success",
          description: "Logo uploaded successfully",
        });
      } else {
        throw new Error("No logo URL returned");
      }
      
    } catch (error) {
      console.error('=== LOGO UPLOAD ERROR ===');
      console.error('Error:', error);
      
      let errorMessage = "Failed to upload logo";
      if (error.message.includes('HTTP 404')) {
        errorMessage = "Calendar template not found";
      } else if (error.message.includes('HTTP')) {
        errorMessage = error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Logo Upload Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        id="logo-upload-input"
      />
      <Button
        variant="outline"
        onClick={() => {
          console.log('Logo upload button clicked');
          console.log('Promoter ID:', promoterId);
          console.log('Template ID:', templateId);
          fileInputRef.current?.click();
        }}
        disabled={uploading || !promoterId || !templateId}
        className="w-full"
      >
        {uploading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
            Uploading...
          </>
        ) : (
          <>
            <Plus className="w-4 h-4 mr-2" />
            Upload Logo
          </>
        )}
      </Button>
    </div>
  );
};

// Calendar Settings Panel Component  

// Metadata Settings Component
const MetadataSettings = ({ template, updateCalendarConfig }) => {
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const { toast } = useToast();
  
  const metadata = template.calendar_config.metadata || {
    title: '',
    description: '',
    keywords: '',
    ogImage: '',
    faviconUrl: '',
    twitterCard: 'summary_large_image'
  };

  const updateMetadata = (updates) => {
    updateCalendarConfig({
      metadata: {
        ...metadata,
        ...updates
      }
    });
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - check both MIME type and extension
    const validMimeTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    const validExtensions = ['.png', '.jpg', '.jpeg', '.svg', '.ico'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    const isValidMimeType = validMimeTypes.includes(file.type);
    const isValidExtension = validExtensions.includes(fileExtension);
    
    if (!isValidMimeType && !isValidExtension) {
      toast({
        title: "Invalid file type",
        description: `Please upload a PNG, JPG, SVG, or ICO file. Your file type: ${file.type || 'unknown'}, extension: ${fileExtension}`,
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Favicon must be less than 1MB",
        variant: "destructive"
      });
      return;
    }

    setUploadingFavicon(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use the correct favicon upload endpoint
      const uploadUrl = `${API}/promoters/${template.promoter_id}/calendar-templates/${template.id}/favicon`;
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data.url) {
        updateMetadata({ faviconUrl: data.url });
        
        toast({
          title: "Favicon uploaded",
          description: "Your custom favicon has been uploaded successfully",
        });
      } else {
        throw new Error("No favicon URL returned");
      }
    } catch (error) {
      console.error('Error uploading favicon:', error);
      
      let errorMessage = "Failed to upload favicon";
      if (error.message.includes('HTTP 404')) {
        errorMessage = "Calendar template not found";
      } else if (error.message.includes('HTTP')) {
        errorMessage = error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setUploadingFavicon(false);
    }
  };

  return (
    <Card className="bg-white/60 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg">SEO & Metadata Settings</CardTitle>
        <CardDescription>
          Configure how your calendar appears in search engines and social media shares
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Page Title */}
        <div>
          <Label htmlFor="meta-title">
            Page Title *
            <span className="text-xs text-gray-500 ml-2">(appears in browser tab and search results)</span>
          </Label>
          <Input
            id="meta-title"
            value={metadata.title}
            onChange={(e) => updateMetadata({ title: e.target.value })}
            placeholder="e.g., Baltimore Events Calendar - What's Happening"
            className="mt-2"
            maxLength={60}
          />
          <p className="text-xs text-gray-500 mt-1">
            {metadata.title.length}/60 characters • Recommended: 50-60 characters
          </p>
        </div>

        {/* Favicon Upload */}
        <div>
          <Label htmlFor="favicon-upload">
            Custom Favicon
            <span className="text-xs text-gray-500 ml-2">(appears in browser tab)</span>
          </Label>
          <div className="mt-2 space-y-3">
            <div className="flex items-center gap-4">
              {metadata.faviconUrl && (
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                  <img 
                    src={`${API}${metadata.faviconUrl}`}
                    alt="Current favicon" 
                    className="w-8 h-8 object-contain"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Current Favicon</p>
                    <p className="text-xs text-gray-500">Displayed in browser tab</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateMetadata({ faviconUrl: '' })}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <Input
                id="favicon-upload"
                type="file"
                accept=".png,.jpg,.jpeg,.svg,.ico"
                onChange={handleFaviconUpload}
                disabled={uploadingFavicon}
                className="cursor-pointer"
              />
              {uploadingFavicon && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Uploading...
                </div>
              )}
            </div>
            
            <p className="text-xs text-gray-500">
              Recommended: 32×32px or 64×64px • Formats: PNG, JPG, SVG, ICO • Max size: 1MB
            </p>
          </div>
        </div>

        {/* Meta Description */}
        <div>
          <Label htmlFor="meta-description">
            Meta Description *
            <span className="text-xs text-gray-500 ml-2">(appears in search results)</span>
          </Label>
          <Textarea
            id="meta-description"
            value={metadata.description}
            onChange={(e) => updateMetadata({ description: e.target.value })}
            placeholder="e.g., Discover amazing events happening in Baltimore. Find concerts, festivals, workshops, and more on your local events calendar."
            rows={3}
            className="mt-2"
            maxLength={160}
          />
          <p className="text-xs text-gray-500 mt-1">
            {metadata.description.length}/160 characters • Recommended: 150-160 characters
          </p>
        </div>

        {/* Keywords */}
        <div>
          <Label htmlFor="meta-keywords">
            Keywords
            <span className="text-xs text-gray-500 ml-2">(comma-separated)</span>
          </Label>
          <Input
            id="meta-keywords"
            value={metadata.keywords}
            onChange={(e) => updateMetadata({ keywords: e.target.value })}
            placeholder="e.g., events, Baltimore, concerts, festivals, local events"
            className="mt-2"
          />
          <p className="text-xs text-gray-500 mt-1">
            Helps search engines understand your calendar content
          </p>
        </div>

        {/* Open Graph Image */}
        <div>
          <Label htmlFor="og-image">
            Social Media Image URL
            <span className="text-xs text-gray-500 ml-2">(for Facebook, LinkedIn, etc.)</span>
          </Label>
          <Input
            id="og-image"
            type="url"
            value={metadata.ogImage}
            onChange={(e) => updateMetadata({ ogImage: e.target.value })}
            placeholder="https://example.com/calendar-preview.jpg"
            className="mt-2"
          />
          <p className="text-xs text-gray-500 mt-1">
            Recommended size: 1200×630px • Appears when calendar is shared on social media
          </p>
          {metadata.ogImage && (
            <div className="mt-2">
              <img 
                src={metadata.ogImage} 
                alt="Preview" 
                className="max-w-xs border rounded-lg"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>

        {/* Twitter Card Type */}
        <div>
          <Label htmlFor="twitter-card">Twitter Card Type</Label>
          <Select
            value={metadata.twitterCard}
            onValueChange={(value) => updateMetadata({ twitterCard: value })}
          >
            <SelectTrigger className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="summary">Summary (small image)</SelectItem>
              <SelectItem value="summary_large_image">Summary with Large Image</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            How your calendar appears when shared on Twitter/X
          </p>
        </div>

        {/* Preview Section */}
        <div className="border-t pt-4">
          <Label className="text-base font-semibold">Preview</Label>
          <div className="mt-3 space-y-3">
            {/* Search Engine Preview */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <p className="text-xs text-gray-500 mb-2">Google Search Result Preview:</p>
              <div className="text-blue-600 text-lg hover:underline cursor-pointer">
                {metadata.title || 'Your Calendar Title'}
              </div>
              <div className="text-green-700 text-xs mt-1">
                https://yourcalendar.com
              </div>
              <div className="text-gray-700 text-sm mt-1">
                {metadata.description || 'Your calendar description will appear here...'}
              </div>
            </div>

            {/* Social Media Preview */}
            {metadata.ogImage && (
              <div className="border rounded-lg overflow-hidden bg-gray-50">
                <p className="text-xs text-gray-500 p-3 pb-2">Social Media Share Preview:</p>
                <img 
                  src={metadata.ogImage} 
                  alt="Social preview" 
                  className="w-full"
                  style={{ maxHeight: '200px', objectFit: 'cover' }}
                  onError={(e) => e.target.style.display = 'none'}
                />
                <div className="p-3">
                  <div className="font-semibold text-sm">
                    {metadata.title || 'Your Calendar Title'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {metadata.description || 'Your description...'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-sm text-blue-900 mb-2">💡 SEO Tips</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• Include your city/region name in the title for local SEO</li>
            <li>• Use descriptive, action-oriented language in the description</li>
            <li>• Add relevant keywords that people might search for</li>
            <li>• Use a high-quality, eye-catching image for social shares</li>
            <li>• Keep titles under 60 characters to avoid truncation</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};



// Quick Search Filters Settings Component
const QuickSearchFiltersSettings = ({ template, updateCalendarConfig }) => {
  const { toast } = useToast();
  const [editingFilter, setEditingFilter] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  // Default quick search filters
  const defaultFilters = [
    {
      id: '1',
      label: "Today's Events",
      emoji: '📅',
      enabled: true,
      filters: {
        dateRange: 'today'
      }
    },
    {
      id: '2',
      label: 'This Weekend',
      emoji: '🎉',
      enabled: true,
      filters: {
        dateRange: 'weekend'
      }
    },
    {
      id: '3',
      label: 'Free Events',
      emoji: '🆓',
      enabled: true,
      filters: {
        isFree: true
      }
    },
    {
      id: '4',
      label: 'Music Events',
      emoji: '🎵',
      enabled: true,
      filters: {
        categoryName: 'Music'
      }
    },
    {
      id: '5',
      label: 'Food & Drink',
      emoji: '🍕',
      enabled: true,
      filters: {
        categoryName: 'Food & Drink'
      }
    }
  ];

  const quickSearchFilters = template.calendar_config.quickSearchFilters || defaultFilters;
  const showClearButton = template.calendar_config.showClearFiltersButton !== false;

  const addFilter = () => {
    setEditingFilter({
      id: Date.now().toString(),
      label: '',
      emoji: '🔍',
      enabled: true,
      filters: {}
    });
    setShowFilterModal(true);
  };

  const editFilter = (filter) => {
    setEditingFilter({ ...filter });
    setShowFilterModal(true);
  };

  const saveFilter = (filter) => {
    const updatedFilters = editingFilter.id && quickSearchFilters.find(f => f.id === editingFilter.id)
      ? quickSearchFilters.map(f => f.id === filter.id ? filter : f)
      : [...quickSearchFilters, filter];
    
    updateCalendarConfig({ quickSearchFilters: updatedFilters });
    setShowFilterModal(false);
    setEditingFilter(null);
  };

  const deleteFilter = (filterId) => {
    const updatedFilters = quickSearchFilters.filter(f => f.id !== filterId);
    updateCalendarConfig({ quickSearchFilters: updatedFilters });
  };

  const toggleFilterEnabled = (filterId) => {
    const updatedFilters = quickSearchFilters.map(f => 
      f.id === filterId ? { ...f, enabled: !f.enabled } : f
    );
    updateCalendarConfig({ quickSearchFilters: updatedFilters });
  };

  const moveFilter = (index, direction) => {
    const newFilters = [...quickSearchFilters];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFilters.length) return;
    
    [newFilters[index], newFilters[targetIndex]] = [newFilters[targetIndex], newFilters[index]];
    updateCalendarConfig({ quickSearchFilters: newFilters });
  };

  const resetToDefaults = () => {
    updateCalendarConfig({ quickSearchFilters: defaultFilters });
    toast({
      title: "Reset Complete",
      description: "Quick search filters reset to defaults",
    });
  };

  return (
    <Card className="bg-white/60 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Quick Search Filters</CardTitle>
            <CardDescription>
              Customize the quick filter buttons that appear on your calendar (max 10)
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={resetToDefaults}
              disabled={JSON.stringify(quickSearchFilters) === JSON.stringify(defaultFilters)}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button
              size="sm"
              onClick={addFilter}
              disabled={quickSearchFilters.length >= 10}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Filter
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Clear Filters Button Setting */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200">
          <div className="flex items-center space-x-2">
            <input
              id="show-clear-button"
              type="checkbox"
              checked={showClearButton}
              onChange={(e) => updateCalendarConfig({ showClearFiltersButton: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="show-clear-button" className="font-semibold">
              Show "Clear Filters" button
            </Label>
          </div>
          <Badge variant="secondary">Reset Button</Badge>
        </div>

        {/* Filter List */}
        <div className="space-y-2">
          {quickSearchFilters.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Filter className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No quick search filters configured</p>
              <p className="text-sm">Add filters to help visitors find events quickly</p>
            </div>
          ) : (
            quickSearchFilters.map((filter, index) => (
              <div
                key={filter.id}
                className={`flex items-center gap-3 p-3 border rounded-lg ${
                  filter.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'
                }`}
              >
                {/* Emoji/Icon */}
                <div className="text-2xl">{filter.emoji}</div>
                
                {/* Filter Info */}
                <div className="flex-1">
                  <div className="font-semibold">{filter.label}</div>
                  <div className="text-xs text-gray-500">
                    {filter.filters.dateRange && <Badge variant="outline" className="mr-1">Date: {filter.filters.dateRange}</Badge>}
                    {filter.filters.isFree && <Badge variant="outline" className="mr-1">Free Only</Badge>}
                    {filter.filters.categoryName && <Badge variant="outline" className="mr-1">Category: {filter.filters.categoryName}</Badge>}
                    {filter.filters.organizerName && <Badge variant="outline" className="mr-1">Organizer: {filter.filters.organizerName}</Badge>}
                    {filter.filters.venueName && <Badge variant="outline" className="mr-1">Venue: {filter.filters.venueName}</Badge>}
                    {filter.filters.eventType && <Badge variant="outline" className="mr-1">Type: {filter.filters.eventType}</Badge>}
                    {filter.filters.priceRange && <Badge variant="outline" className="mr-1">Price: {filter.filters.priceRange}</Badge>}
                  </div>
                </div>

                {/* Status Badge */}
                <Badge className={filter.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                  {filter.enabled ? 'Enabled' : 'Disabled'}
                </Badge>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {/* Move Up/Down */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveFilter(index, 'up')}
                    disabled={index === 0}
                    title="Move up"
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveFilter(index, 'down')}
                    disabled={index === quickSearchFilters.length - 1}
                    title="Move down"
                  >
                    ↓
                  </Button>
                  
                  {/* Enable/Disable */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleFilterEnabled(filter.id)}
                    title={filter.enabled ? 'Disable' : 'Enable'}
                  >
                    {filter.enabled ? <Eye className="w-4 h-4" /> : <Eye className="w-4 h-4 opacity-30" />}
                  </Button>
                  
                  {/* Edit */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => editFilter(filter)}
                    title="Edit filter"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  
                  {/* Delete */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteFilter(filter.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Delete filter"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {quickSearchFilters.length > 0 && (
          <p className="text-xs text-gray-500 text-center">
            {quickSearchFilters.length} of 10 filters configured • 
            {quickSearchFilters.filter(f => f.enabled).length} enabled
          </p>
        )}
      </CardContent>

      {/* Filter Edit Modal */}
      {showFilterModal && (
        <FilterEditModal
          filter={editingFilter}
          onSave={saveFilter}
          onClose={() => {
            setShowFilterModal(false);
            setEditingFilter(null);
          }}
        />
      )}
    </Card>
  );
};

// Filter Edit Modal Component
const FilterEditModal = ({ filter, onSave, onClose }) => {
  const [label, setLabel] = useState(filter.label);
  const [emoji, setEmoji] = useState(filter.emoji);
  const [filterType, setFilterType] = useState(
    filter.filters.dateRange ? 'date' :
    filter.filters.isFree ? 'free' :
    filter.filters.categoryName ? 'category' :
    filter.filters.organizerName ? 'organizer' :
    filter.filters.venueName ? 'venue' :
    filter.filters.eventType ? 'type' :
    filter.filters.priceRange ? 'price' : 'date'
  );
  const [filterValue, setFilterValue] = useState(
    filter.filters.dateRange ||
    filter.filters.categoryName ||
    filter.filters.organizerName ||
    filter.filters.venueName ||
    filter.filters.eventType ||
    filter.filters.priceRange ||
    'today'
  );

  const commonEmojis = ['📅', '🎉', '🆓', '🎵', '🍕', '🎨', '🏃', '🎭', '💼', '🌟', '🔍', '❤️', '🎸', '🍔', '🏀'];

  const handleSave = () => {
    if (!label.trim()) {
      alert('Please enter a label for the filter');
      return;
    }

    const filters = {};
    
    switch (filterType) {
      case 'date':
        filters.dateRange = filterValue;
        break;
      case 'free':
        filters.isFree = true;
        break;
      case 'category':
        filters.categoryName = filterValue;
        break;
      case 'organizer':
        filters.organizerName = filterValue;
        break;
      case 'venue':
        filters.venueName = filterValue;
        break;
      case 'type':
        filters.eventType = filterValue;
        break;
      case 'price':
        filters.priceRange = filterValue;
        break;
    }

    onSave({
      ...filter,
      label,
      emoji,
      filters
    });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{filter.label ? 'Edit' : 'Add'} Quick Search Filter</DialogTitle>
          <DialogDescription>
            Configure a quick filter button that visitors can use to search events
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Label */}
          <div>
            <Label htmlFor="filter-label">Button Label *</Label>
            <Input
              id="filter-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Today's Events, Free Events"
              className="mt-2"
            />
          </div>

          {/* Emoji Picker */}
          <div>
            <Label>Button Emoji</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {commonEmojis.map((e) => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  className={`text-2xl p-2 rounded border-2 hover:border-blue-500 transition ${
                    emoji === e ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  {e}
                </button>
              ))}
              <Input
                type="text"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                placeholder="Or type emoji"
                className="w-20 text-center text-xl"
                maxLength={2}
              />
            </div>
          </div>

          {/* Filter Type */}
          <div>
            <Label htmlFor="filter-type">Filter Type *</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date Range</SelectItem>
                <SelectItem value="free">Free Events Only</SelectItem>
                <SelectItem value="category">Category</SelectItem>
                <SelectItem value="organizer">Organizer</SelectItem>
                <SelectItem value="venue">Venue</SelectItem>
                <SelectItem value="type">Event Type</SelectItem>
                <SelectItem value="price">Price Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Filter Value based on Type */}
          {filterType === 'date' && (
            <div>
              <Label htmlFor="date-range">Date Range</Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="tomorrow">Tomorrow</SelectItem>
                  <SelectItem value="weekend">This Weekend</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="next_week">Next Week</SelectItem>
                  <SelectItem value="next_month">Next Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {filterType === 'free' && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                ✓ This button will show only free events (no additional configuration needed)
              </p>
            </div>
          )}

          {filterType === 'category' && (
            <div>
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                placeholder="e.g., Music, Food & Drink, Sports"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the exact category name as it appears in your events
              </p>
            </div>
          )}

          {filterType === 'organizer' && (
            <div>
              <Label htmlFor="organizer-name">Organizer Name</Label>
              <Input
                id="organizer-name"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                placeholder="e.g., City Arts Council"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the organizer/host name to filter by
              </p>
            </div>
          )}

          {filterType === 'venue' && (
            <div>
              <Label htmlFor="venue-name">Venue Name</Label>
              <Input
                id="venue-name"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                placeholder="e.g., City Hall, Central Park"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the venue name to filter by
              </p>
            </div>
          )}

          {filterType === 'type' && (
            <div>
              <Label htmlFor="event-type">Event Type</Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {filterType === 'price' && (
            <div>
              <Label htmlFor="price-range">Price Range</Label>
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="under-20">Under $20</SelectItem>
                  <SelectItem value="under-50">Under $50</SelectItem>
                  <SelectItem value="under-100">Under $100</SelectItem>
                  <SelectItem value="over-100">Over $100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Preview */}
          <div className="border-t pt-4">
            <Label>Preview</Label>
            <div className="mt-2 flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
              <span className="text-xl">{emoji}</span>
              <span className="font-medium">{label || 'Button Label'}</span>
              <Badge variant="outline" className="ml-auto">
                {filterType === 'date' ? `Date: ${filterValue}` :
                 filterType === 'free' ? 'Free Only' :
                 filterType === 'category' ? `Category: ${filterValue}` :
                 filterType === 'organizer' ? `Organizer: ${filterValue}` :
                 filterType === 'venue' ? `Venue: ${filterValue}` :
                 filterType === 'type' ? `Type: ${filterValue}` :
                 filterType === 'price' ? `Price: ${filterValue}` : ''
                }
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Save Filter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const CalendarSettingsPanel = ({ 
  template, promoter, refreshCurrentTemplate, updateThemeColors, updateHeaderConfig, updateCalendarConfig, updateFooterConfig 
}) => {
  const { toast } = useToast();
  
  return (
    <div className="space-y-6">
      {/* Theme Colors */}
      <Card className="bg-white/60 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg">Theme Colors</CardTitle>
          <CardDescription>Customize your calendar's color scheme. Hover over labels for details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="primary-color" title="Used for: Event badges with images, buttons, links, today's date">
                Primary Color
                <span className="text-xs text-gray-500 block">Events with images, buttons</span>
              </Label>
              <Input
                id="primary-color"
                type="color"
                value={template.theme_config.colors.primary}
                onChange={(e) => updateThemeColors({ primary: e.target.value })}
                className="w-full h-10"
              />
            </div>
            <div>
              <Label htmlFor="secondary-color" title="Used for: Event badges without images">
                Secondary Color
                <span className="text-xs text-gray-500 block">Events without images</span>
              </Label>
              <Input
                id="secondary-color"
                type="color"
                value={template.theme_config.colors.secondary}
                onChange={(e) => updateThemeColors({ secondary: e.target.value })}
                className="w-full h-10"
              />
            </div>
            <div>
              <Label htmlFor="accent-color" title="Used for: Free badges, special indicators">
                Accent Color
                <span className="text-xs text-gray-500 block">Free badges, highlights</span>
              </Label>
              <Input
                id="accent-color"
                type="color"
                value={template.theme_config.colors.accent}
                onChange={(e) => updateThemeColors({ accent: e.target.value })}
                className="w-full h-10"
              />
            </div>
            <div>
              <Label htmlFor="background-color" title="Used for: Main calendar background, event text color">
                Background Color
                <span className="text-xs text-gray-500 block">Calendar bg, event text</span>
              </Label>
              <Input
                id="background-color"
                type="color"
                value={template.theme_config.colors.background}
                onChange={(e) => updateThemeColors({ background: e.target.value })}
                className="w-full h-10"
              />
            </div>
            <div>
              <Label htmlFor="text-color" title="Used for: Headings, event titles in cards">
                Text Color
                <span className="text-xs text-gray-500 block">Main text, headings</span>
              </Label>
              <Input
                id="text-color"
                type="color"
                value={template.theme_config.colors.text}
                onChange={(e) => updateThemeColors({ text: e.target.value })}
                className="w-full h-10"
              />
            </div>
            <div>
              <Label htmlFor="text-secondary-color" title="Used for: Descriptions, metadata, less prominent text">
                Secondary Text
                <span className="text-xs text-gray-500 block">Descriptions, metadata</span>
              </Label>
              <Input
                id="text-secondary-color"
                type="color"
                value={template.theme_config.colors.textSecondary}
                onChange={(e) => updateThemeColors({ textSecondary: e.target.value })}
                className="w-full h-10"
              />
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-blue-800">
              <strong>💡 Tip:</strong> Event badges use Primary/Secondary colors for background and Background color for text. 
              Ensure good contrast between these colors for readable events.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Header Settings */}
      <Card className="bg-white/60 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg">Header Settings</CardTitle>
          <CardDescription>Configure your calendar header</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="logo-upload">Logo Upload</Label>
            <div className="space-y-2">
              {template.header_config.logoUrl ? (
                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <img 
                    src={resolveAssetUrl(template.header_config.logoUrl)} 
                    alt="Current logo" 
                    className="w-auto h-auto object-contain"
                    style={{
                      maxHeight: template.header_config.logoSize === 'small' ? '36px' 
                        : template.header_config.logoSize === 'large' ? '60px'
                        : template.header_config.logoSize === 'xlarge' ? '80px'
                        : '48px',
                      maxWidth: template.header_config.isHorizontalLogo ? '250px' : '150px'
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">
                      Current logo 
                      {template.header_config.isHorizontalLogo && <span className="text-xs ml-1">(Horizontal)</span>}
                      <span className="text-xs ml-1">({template.header_config.logoSize || 'medium'})</span>
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      try {
                        const deleteUrl = `${API}/promoters/${promoter?.id}/calendar-templates/${template?.id}/logo`;
                        const response = await fetch(deleteUrl, { method: 'DELETE' });
                        
                        if (response.ok) {
                          updateHeaderConfig({ logoUrl: null });
                          if (refreshCurrentTemplate) {
                            await refreshCurrentTemplate();
                          }
                          toast({
                            title: "Success",
                            description: "Logo removed successfully",
                          });
                        } else {
                          throw new Error(`HTTP ${response.status}`);
                        }
                      } catch (error) {
                        console.error('Logo removal failed:', error);
                        toast({
                          title: "Error",
                          description: "Failed to remove logo",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <p className="text-gray-500 mb-2">No logo uploaded</p>
                  <LogoUploadButton 
                    promoterId={promoter?.id}
                    templateId={template?.id}
                    onUploadSuccess={(logoUrl) => updateHeaderConfig({ logoUrl })}
                    onTemplateRefresh={refreshCurrentTemplate}
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              💡 <strong>Tip:</strong> For best quality, upload a logo at least 400x400px (or 600x200px for horizontal). PNG with transparent background or SVG recommended.
            </p>
            
            {/* Logo Orientation Setting */}
            {template.header_config.logoUrl && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                {/* Logo Size Control */}
                <div>
                  <Label htmlFor="logo-size" className="font-medium">Logo Size</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <select
                      id="logo-size"
                      value={template.header_config.logoSize || 'medium'}
                      onChange={(e) => updateHeaderConfig({ logoSize: e.target.value })}
                      className="border rounded-md px-3 py-2 text-sm"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                      <option value="xlarge">Extra Large</option>
                    </select>
                    <span className="text-xs text-gray-500">
                      {template.header_config.logoSize === 'small' && '(48px height)'}
                      {(template.header_config.logoSize === 'medium' || !template.header_config.logoSize) && '(64px height)'}
                      {template.header_config.logoSize === 'large' && '(96px height)'}
                      {template.header_config.logoSize === 'xlarge' && '(128px height)'}
                    </span>
                  </div>
                </div>
                
                {/* Horizontal Logo Toggle */}
                <div className="flex items-center space-x-2">
                  <input
                    id="horizontal-logo"
                    type="checkbox"
                    checked={template.header_config.isHorizontalLogo || false}
                    onChange={(e) => updateHeaderConfig({ isHorizontalLogo: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="horizontal-logo" className="font-medium cursor-pointer">
                    My logo is horizontal/wide (not square)
                  </Label>
                </div>
                <p className="text-xs text-gray-600 ml-6">
                  Check this if your logo is wider than it is tall. This adjusts the max-width constraint.
                </p>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="header-text">Header Text</Label>
            <Input
              id="header-text"
              value={template.header_config.headerText}
              onChange={(e) => updateHeaderConfig({ headerText: e.target.value })}
              placeholder="Enter header text"
            />
          </div>
          <div>
            <Label htmlFor="hero-text">Hero Text</Label>
            <Textarea
              id="hero-text"
              value={template.header_config.heroText}
              onChange={(e) => updateHeaderConfig({ heroText: e.target.value })}
              placeholder="Enter hero/subtitle text"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="header-bg-color">Header Background</Label>
              <Input
                id="header-bg-color"
                type="color"
                value={template.header_config.backgroundColor}
                onChange={(e) => updateHeaderConfig({ backgroundColor: e.target.value })}
                className="w-full h-10"
              />
            </div>
            <div>
              <Label htmlFor="header-text-color">Header Text Color</Label>
              <Input
                id="header-text-color"
                type="color"
                value={template.header_config.textColor}
                onChange={(e) => updateHeaderConfig({ textColor: e.target.value })}
                className="w-full h-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Settings */}
      <Card className="bg-white/60 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg">Calendar Settings</CardTitle>
          <CardDescription>Configure how events are displayed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="view-type">View Type</Label>
            <Select
              value={template.calendar_config.viewType}
              onValueChange={(value) => updateCalendarConfig({ viewType: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select view type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month View</SelectItem>
                <SelectItem value="week">Week View</SelectItem>
                <SelectItem value="weekend">Weekend View</SelectItem>
                <SelectItem value="day">Day View</SelectItem>
                <SelectItem value="list">List View</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                id="show-images"
                type="checkbox"
                checked={template.calendar_config.showImages}
                onChange={(e) => updateCalendarConfig({ showImages: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="show-images">Show Images</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="show-prices"
                type="checkbox"
                checked={template.calendar_config.showPrices}
                onChange={(e) => updateCalendarConfig({ showPrices: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="show-prices">Show Prices</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="show-venues"
                type="checkbox"
                checked={template.calendar_config.showVenues}
                onChange={(e) => updateCalendarConfig({ showVenues: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="show-venues">Show Venues</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="show-hosts"
                type="checkbox"
                checked={template.calendar_config.showOrganizers}
                onChange={(e) => updateCalendarConfig({ showOrganizers: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="show-hosts">Show Organizers</Label>
            </div>
          </div>
          
          {/* Hover Settings */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-base font-semibold mb-3 block">Hover Behavior</Label>
            
            <div className="space-y-4">
              {/* Day Hover Settings */}
              <div className="space-y-3 p-3 bg-gray-50 rounded-md">
                <div className="flex items-center space-x-2">
                  <input
                    id="enable-day-hover"
                    type="checkbox"
                    checked={template.calendar_config.enableDayHover !== false}
                    onChange={(e) => updateCalendarConfig({ enableDayHover: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="enable-day-hover" className="font-semibold">Enable day hover (expand day view)</Label>
                </div>
                
                {(template.calendar_config.enableDayHover !== false) && (
                  <div>
                    <Label htmlFor="day-hover-delay">Day hover delay (milliseconds)</Label>
                    <Input
                      id="day-hover-delay"
                      type="number"
                      min="0"
                      max="5000"
                      step="100"
                      value={template.calendar_config.dayHoverDelay || 300}
                      onChange={(e) => updateCalendarConfig({ dayHoverDelay: parseInt(e.target.value) || 300 })}
                      className="w-full mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Time before showing expanded day view (0-5000ms, default 300ms)
                    </p>
                  </div>
                )}
              </div>
              
              {/* Event Hover Settings */}
              <div className="space-y-3 p-3 bg-gray-50 rounded-md">
                <div className="flex items-center space-x-2">
                  <input
                    id="enable-event-hover"
                    type="checkbox"
                    checked={template.calendar_config.enableEventHover !== false}
                    onChange={(e) => updateCalendarConfig({ enableEventHover: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="enable-event-hover" className="font-semibold">Enable event hover (expand event details)</Label>
                </div>
                
                {(template.calendar_config.enableEventHover !== false) && (
                  <div>
                    <Label htmlFor="event-hover-delay">Event hover delay (milliseconds)</Label>
                    <Input
                      id="event-hover-delay"
                      type="number"
                      min="0"
                      max="5000"
                      step="100"
                      value={template.calendar_config.eventHoverDelay || 300}
                      onChange={(e) => updateCalendarConfig({ eventHoverDelay: parseInt(e.target.value) || 300 })}
                      className="w-full mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Time before showing expanded event details (0-5000ms, default 300ms)
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button Settings */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-base font-semibold mb-3 block">Submit Event Button</Label>
            <p className="text-sm text-gray-600 mb-4">Customize the public event submission button (requires Public Submissions enabled)</p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="submit-button-text">Button Text</Label>
                <Input
                  id="submit-button-text"
                  type="text"
                  value={template.calendar_config.submitButtonText || 'Submit Your Event'}
                  onChange={(e) => updateCalendarConfig({ submitButtonText: e.target.value })}
                  placeholder="Submit Your Event"
                  className="w-full mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="submit-button-subtitle">Button Subtitle</Label>
                <Input
                  id="submit-button-subtitle"
                  type="text"
                  value={template.calendar_config.submitButtonSubtitle || "Share your event with our community - it's free and easy!"}
                  onChange={(e) => updateCalendarConfig({ submitButtonSubtitle: e.target.value })}
                  placeholder="Share your event with our community"
                  className="w-full mt-2"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  id="show-submit-subtitle"
                  type="checkbox"
                  checked={template.calendar_config.showSubmitSubtitle !== false}
                  onChange={(e) => updateCalendarConfig({ showSubmitSubtitle: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="show-submit-subtitle">Show subtitle text</Label>
              </div>
              
              <div>
                <Label htmlFor="submit-button-size">Button Size</Label>
                <Select
                  value={template.calendar_config.submitButtonSize || 'large'}
                  onValueChange={(value) => updateCalendarConfig({ submitButtonSize: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large (Default)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="submit-button-position">Button Position</Label>
                <Select
                  value={template.calendar_config.submitButtonPosition || 'center'}
                  onValueChange={(value) => updateCalendarConfig({ submitButtonPosition: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Center (Default)</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="submit-button-bg-color">Button Background Color</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    id="submit-button-bg-color"
                    type="color"
                    value={template.calendar_config.submitButtonBgColor || '#9275A8'}
                    onChange={(e) => updateCalendarConfig({ submitButtonBgColor: e.target.value })}
                    className="w-12 h-10 rounded border"
                  />
                  <Input
                    type="text"
                    value={template.calendar_config.submitButtonBgColor || '#9275A8'}
                    onChange={(e) => updateCalendarConfig({ submitButtonBgColor: e.target.value })}
                    placeholder="#9275A8"
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="submit-button-text-color">Button Text Color</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    id="submit-button-text-color"
                    type="color"
                    value={template.calendar_config.submitButtonTextColor || '#FFFFFF'}
                    onChange={(e) => updateCalendarConfig({ submitButtonTextColor: e.target.value })}
                    className="w-12 h-10 rounded border"
                  />
                  <Input
                    type="text"
                    value={template.calendar_config.submitButtonTextColor || '#FFFFFF'}
                    onChange={(e) => updateCalendarConfig({ submitButtonTextColor: e.target.value })}
                    placeholder="#FFFFFF"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Quick Search Filters */}
      <QuickSearchFiltersSettings 
        template={template}
        updateCalendarConfig={updateCalendarConfig}
      />

      {/* Metadata Settings */}
      <MetadataSettings 
        template={template}
        updateCalendarConfig={updateCalendarConfig}
      />

      {/* Footer Settings */}
      <Card className="bg-white/60 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg">Footer Settings</CardTitle>
          <CardDescription>Configure your calendar footer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="footer-text">Footer Text</Label>
            <Textarea
              id="footer-text"
              value={template.footer_config.footerText}
              onChange={(e) => updateFooterConfig({ footerText: e.target.value })}
              placeholder="Enter footer text"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="footer-bg-color">Footer Background</Label>
              <Input
                id="footer-bg-color"
                type="color"
                value={template.footer_config.backgroundColor}
                onChange={(e) => updateFooterConfig({ backgroundColor: e.target.value })}
                className="w-full h-10"
              />
            </div>
            <div>
              <Label htmlFor="footer-text-color">Footer Text Color</Label>
              <Input
                id="footer-text-color"
                type="color"
                value={template.footer_config.textColor}
                onChange={(e) => updateFooterConfig({ textColor: e.target.value })}
                className="w-full h-10"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input
              id="show-branding"
              type="checkbox"
              checked={template.footer_config.showBranding}
              onChange={(e) => updateFooterConfig({ showBranding: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="show-branding">Show Powered by Branding</Label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper function to format pricing display (used by multiple components)
const formatPricing = (event) => {
  const pricingStatus = event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'));

  if (pricingStatus === 'free') {
    return { display: 'Free', type: 'free' };
  }

  if (pricingStatus === 'unspecified') {
    return { display: null, type: 'none' };
  }
  
  const pricing_type = event.pricing_type || 'single';
  const price_min = event.price_min;
  const price_max = event.price_max;
  const vip_price = event.vip_price;
  
  if (pricing_type === 'two_tier' && price_min && vip_price) {
    // Two tier: Show both general and VIP
    return {
      display: [
        { label: 'General Admission', price: `$${price_min.toFixed(2)}` },
        { label: 'VIP Admission', price: `$${vip_price.toFixed(2)}` }
      ],
      type: 'two_tier'
    };
  } else if (pricing_type === 'range' && price_min && price_max && price_min !== price_max) {
    // Range: Show low - high
    return { display: `$${price_min.toFixed(2)} - $${price_max.toFixed(2)}`, type: 'range' };
  } else if (price_min) {
    // Single price
    return { display: `$${price_min.toFixed(2)}`, type: 'single' };
  }
  
  return { display: null, type: 'none' };
};

// Interactive Calendar Component with Multiple Views
const InteractiveCalendar = ({ template, events = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState(template?.calendar_config?.viewType || 'month');
  const [hoveredDay, setHoveredDay] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dayHoverTimer, setDayHoverTimer] = useState(null);
  const [eventHoverTimer, setEventHoverTimer] = useState(null);

  // Update view type when template changes
  useEffect(() => {
    if (template?.calendar_config?.viewType) {
      setViewType(template.calendar_config.viewType);
    }
  }, [template?.calendar_config?.viewType]);

  // Navigation functions for different views
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    
    switch (viewType) {
      case 'day':
        newDate.setDate(newDate.getDate() + direction);
        break;
      case 'weekend':
        newDate.setDate(newDate.getDate() + (direction * 4)); // Move by 4 days (Thu-Sun)
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction * 7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + direction);
        break;
      case 'list':
        newDate.setDate(newDate.getDate() + (direction * 7)); // Move by week for list view
        break;
    }
    
    setCurrentDate(newDate);
  };

  const getViewTitle = () => {
    const options = { month: 'long', year: 'numeric' };
    
    switch (viewType) {
      case 'day':
        return currentDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        });
      case 'weekend':
        const weekendStart = getWeekendStart(currentDate);
        const weekendEnd = new Date(weekendStart);
        weekendEnd.setDate(weekendEnd.getDate() + 3); // Thu-Sun
        return `${weekendStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekendEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'week':
        const weekStart = getWeekStart(currentDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'month':
        return currentDate.toLocaleDateString('en-US', options);
      case 'list':
        return 'Upcoming Events';
      default:
        return currentDate.toLocaleDateString('en-US', options);
    }
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const getWeekendStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    // Find Thursday of current week (day 4)
    const diff = d.getDate() - day + 4;
    return new Date(d.setDate(diff));
  };

  const getEventsForDateRange = (startDate, endDate) => {
    return events.filter(event => {
      // Overlap check so multi-day events still match while in progress
      const eventStart = new Date(event.start_at);
      const eventEnd = event.end_at ? new Date(event.end_at) : eventStart;
      return eventStart <= endDate && eventEnd >= startDate;
    }).sort((a, b) => {
      // Sort by: featured first, then by date
      if (a.featured_image && !b.featured_image) return -1;
      if (!a.featured_image && b.featured_image) return 1;
      return new Date(a.start_at) - new Date(b.start_at);
    });
  };

  const getEventsForDay = (date) => {
    return events.filter(event => eventOccursOnDay(event, date)).sort((a, b) => {
      // Sort by: featured first, then by time
      if (a.featured_image && !b.featured_image) return -1;
      if (!a.featured_image && b.featured_image) return 1;
      return new Date(a.start_at) - new Date(b.start_at);
    });
  };

  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  // Event interaction handlers
  const handleDayHover = (day) => {
    // Check if day hover is enabled in calendar config
    if (template?.calendar_config?.enableDayHover === false) {
      return; // Don't set any state or start any timers
    }
    
    if (dayHoverTimer) clearTimeout(dayHoverTimer);
    
    setHoveredDay(day);
    
    if (day && day.events.length > 0) {
      // Use configured day hover delay (default 300ms if not set)
      const dayHoverDelay = template?.calendar_config?.dayHoverDelay || 300;
      
      const timer = setTimeout(() => {
        setSelectedDay(day);
        setDayModalOpen(true);
      }, dayHoverDelay);
      setDayHoverTimer(timer);
    }
  };

  const handleDayHoverExit = () => {
    // Only clean up if day hover is enabled
    if (template?.calendar_config?.enableDayHover === false) {
      return;
    }
    
    if (dayHoverTimer) {
      clearTimeout(dayHoverTimer);
      setDayHoverTimer(null);
    }
    setHoveredDay(null);
  };

  const handleEventHover = (event) => {
    // Check if event hover is enabled in calendar config
    if (template?.calendar_config?.enableEventHover === false) {
      return; // Don't set any state or start any timers
    }
    
    if (eventHoverTimer) clearTimeout(eventHoverTimer);
    
    setHoveredEvent(event);
    
    // Use configured event hover delay (default 300ms if not set)
    const eventHoverDelay = template?.calendar_config?.eventHoverDelay || 300;
    
    const timer = setTimeout(() => {
      setSelectedEvent(event);
      setEventModalOpen(true);
    }, eventHoverDelay);
    setEventHoverTimer(timer);
  };

  const handleEventHoverExit = () => {
    // Only clean up if event hover is enabled
    if (template?.calendar_config?.enableEventHover === false) {
      return;
    }
    
    if (eventHoverTimer) {
      clearTimeout(eventHoverTimer);
      setEventHoverTimer(null);
    }
    setHoveredEvent(null);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventModalOpen(true);
  };

  const colors = template?.theme_config?.colors || {};

  return (
    <div className="w-full">
      {/* Calendar Header with View Selector */}
      <div className="flex items-center justify-between mb-6 calendar-header-mobile">
        <div className="flex items-center gap-4">
          <h2 
            className="text-2xl font-bold"
            style={{ color: colors.text || '#1F2937' }}
          >
            {getViewTitle()}
          </h2>
          
          <div className="flex border rounded-lg overflow-hidden view-selector-mobile" style={{ borderColor: colors.border || '#E5E7EB' }}>
            {['list', 'day', 'weekend', 'week', 'month'].map((view) => (
              <Button
                key={view}
                variant={viewType === view ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewType(view)}
                className="rounded-none border-r last:border-r-0"
                style={{
                  backgroundColor: viewType === view ? colors.primary || '#9275A8' : 'transparent',
                  color: viewType === view ? 'white' : colors.text || '#1F2937',
                  borderColor: colors.border || '#E5E7EB'
                }}
              >
                {view.charAt(0).toUpperCase() + view.slice(1)}
              </Button>
            ))}
          </div>
        </div>
        
        <div className="flex gap-2 nav-buttons-mobile">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateDate(-1)}
            style={{ 
              borderColor: colors.primary || '#9275A8',
              color: colors.primary || '#9275A8'
            }}
          >
            ← Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(new Date())}
            style={{ 
              borderColor: colors.primary || '#9275A8',
              color: colors.primary || '#9275A8'
            }}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateDate(1)}
            style={{ 
              borderColor: colors.primary || '#9275A8',
              color: colors.primary || '#9275A8'
            }}
          >
            Next →
          </Button>
        </div>
      </div>

      {/* View Content */}
      <div className="border rounded-lg overflow-hidden calendar-container-mobile" style={{ borderColor: colors.border || '#E5E7EB' }}>
        {viewType === 'month' && (
          <MonthView 
            currentDate={currentDate}
            events={events}
            template={template}
            hoveredDay={hoveredDay}
            hoveredEvent={hoveredEvent}
            onDayHover={handleDayHover}
            onDayHoverExit={handleDayHoverExit}
            onEventHover={handleEventHover}
            onEventHoverExit={handleEventHoverExit}
            onEventClick={handleEventClick}
            onDayModalOpen={(day) => {
              setSelectedDay(day);
              setDayModalOpen(true);
            }}
          />
        )}
        
        {viewType === 'week' && (
          <WeekView 
            currentDate={currentDate}
            events={events}
            template={template}
            hoveredEvent={hoveredEvent}
            onEventHover={handleEventHover}
            onEventHoverExit={handleEventHoverExit}
            onEventClick={handleEventClick}
          />
        )}
        
        {viewType === 'weekend' && (
          <WeekendView 
            currentDate={currentDate}
            events={events}
            template={template}
            hoveredEvent={hoveredEvent}
            onEventHover={handleEventHover}
            onEventHoverExit={handleEventHoverExit}
            onEventClick={handleEventClick}
          />
        )}
        
        {viewType === 'day' && (
          <DayView 
            currentDate={currentDate}
            events={events}
            template={template}
            hoveredEvent={hoveredEvent}
            onEventHover={handleEventHover}
            onEventHoverExit={handleEventHoverExit}
            onEventClick={handleEventClick}
          />
        )}
        
        {viewType === 'list' && (
          <ListView 
            currentDate={currentDate}
            events={events}
            template={template}
            hoveredEvent={hoveredEvent}
            onEventHover={handleEventHover}
            onEventHoverExit={handleEventHoverExit}
            onEventClick={handleEventClick}
          />
        )}
      </div>

      {/* Duplicate Navigation Buttons Below Calendar */}
      <div className="flex items-center justify-between mt-4 nav-buttons-mobile">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateDate(-1)}
            style={{ 
              borderColor: colors.primary || '#9275A8',
              color: colors.primary || '#9275A8'
            }}
          >
            ← Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(new Date())}
            style={{ 
              borderColor: colors.primary || '#9275A8',
              color: colors.primary || '#9275A8'
            }}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateDate(1)}
            style={{ 
              borderColor: colors.primary || '#9275A8',
              color: colors.primary || '#9275A8'
            }}
          >
            Next →
          </Button>
        </div>
        <span 
          className="text-sm font-medium"
          style={{ color: colors.textSecondary || '#6B7280' }}
        >
          {getViewTitle()}
        </span>
      </div>

      {/* Modals */}
      {dayModalOpen && selectedDay && (
        <DayModal
          day={selectedDay}
          template={template}
          onClose={() => {
            setDayModalOpen(false);
            setSelectedDay(null);
          }}
          onEventClick={handleEventClick}
        />
      )}

      {eventModalOpen && selectedEvent && (
        <EventModal
          event={selectedEvent}
          template={template}
          onClose={() => {
            setEventModalOpen(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
};

// Month View Component (existing functionality)
const MonthView = ({ currentDate, events, template, hoveredDay, hoveredEvent, onDayHover, onDayHoverExit, onEventHover, onEventHoverExit, onEventClick, onDayModalOpen }) => {
  const colors = template?.theme_config?.colors || {};
  // Disable visibility tracking in calendar builder to prevent unwanted hover triggers
  const trackElement = () => {};

  const getCalendarData = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const weeks = [];
    const currentDateRef = new Date(startDate);
    
    while (currentDateRef <= endDate) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const dayEvents = events.filter(event => eventOccursOnDay(event, currentDateRef)).sort((a, b) => {
          if (a.featured_image && !b.featured_image) return -1;
          if (!a.featured_image && b.featured_image) return 1;
          return new Date(a.start_at) - new Date(b.start_at);
        });
        
        week.push({
          date: new Date(currentDateRef),
          isCurrentMonth: currentDateRef.getMonth() === month,
          isToday: currentDateRef.toDateString() === new Date().toDateString(),
          events: dayEvents,
          featuredEvents: dayEvents.filter(e => e.featured_image),
          otherEvents: dayEvents.filter(e => !e.featured_image)
        });
        currentDateRef.setDate(currentDateRef.getDate() + 1);
      }
      weeks.push(week);
    }
    
    return weeks;
  };

  // Event preview component with tracking
  const EventPreview = ({ event, isHovered, colors, template, dayDate }) => {
    const eventRef = useRef(null);
    
    useEffect(() => {
      if (eventRef.current && event?.id) {
        trackElement(eventRef.current, event.id);
      }
    }, [event?.id]);

    return (
      <div
        ref={eventRef}
        key={event.id}
        className={`text-xs p-1 rounded mb-1 cursor-pointer transition-all duration-200 event-pill-mobile ${
          isHovered ? 'transform scale-105 shadow-md' : ''
        }`}
        style={{
          backgroundColor: (event.featured_image || event.image_url) ? colors.primary : colors.secondary,
          color: colors.background || '#FFFFFF', // Use background color for text (provides contrast)
          opacity: isHovered ? 1 : 0.9
        }}
        onMouseEnter={() => onEventHover(event)}
        onMouseLeave={onEventHoverExit}
        onClick={(e) => {
          e.stopPropagation();
          onEventClick(event);
        }}
      >
        <div className="font-medium truncate">{event.title}</div>
        {dayDate && isEventContinuation(event, dayDate) && (
          <div className="text-xs opacity-75 italic">Ongoing</div>
        )}
        {template?.calendar_config?.showPrices !== false && ((event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free') && (
          <div className="text-xs opacity-75">Free</div>
        )}
      </div>
    );
  };

  const renderEventPreview = (event, isHovered, dayDate) => {
    return <EventPreview event={event} isHovered={isHovered} colors={colors} template={template} dayDate={dayDate} />;
  };

  const renderDayEvents = (day) => {
    const { featuredEvents, otherEvents } = day;
    const allEvents = [...featuredEvents, ...otherEvents];
    const visibleEvents = allEvents.slice(0, 5);
    const hiddenCount = allEvents.length - 5;

    return (
      <div className="flex-1 min-h-0">
        {visibleEvents.map(event =>
          renderEventPreview(event, hoveredEvent?.id === event.id, day.date)
        )}
        
        {hiddenCount > 0 && (
          <div
            className="text-xs font-medium cursor-pointer mt-1 px-2 py-1 rounded"
            style={{
              backgroundColor: template?.theme_config?.colors?.accent || '#F59E0B',
              color: 'white'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDayModalOpen(day);
            }}
          >
            +{hiddenCount} More
          </div>
        )}
      </div>
    );
  };

  const weeks = getCalendarData();

  return (
    <>
      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b" style={{ borderColor: colors.border || '#E5E7EB' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div 
            key={day} 
            className="p-3 text-center font-semibold day-header-mobile"
            style={{ 
              backgroundColor: colors.surface || '#F9FAFB',
              color: colors.text || '#1F2937'
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Weeks */}
      <div className="divide-y" style={{ borderColor: colors.border || '#E5E7EB' }}>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={`min-h-32 p-2 border-r cursor-pointer transition-all duration-200 month-grid-mobile ${
                  !day.isCurrentMonth ? 'opacity-50' : ''
                } ${day.isToday ? 'ring-2 ring-blue-500' : ''} ${
                  hoveredDay === day ? 'transform scale-102 shadow-lg' : ''
                }`}
                style={{ 
                  borderColor: colors.border || '#E5E7EB',
                  backgroundColor: day.isToday 
                    ? colors.primary + '10' 
                    : hoveredDay === day 
                      ? colors.surface || '#F9FAFB'
                      : colors.background || '#FFFFFF'
                }}
                onMouseEnter={() => onDayHover(day)}
                onMouseLeave={onDayHoverExit}
                onClick={() => day.events.length > 0 && onDayModalOpen(day)}
              >
                <div className="flex flex-col h-full">
                  <div 
                    className={`text-sm font-medium mb-2 ${day.isToday ? 'font-bold' : ''}`}
                    style={{ color: day.isToday ? colors.primary : colors.text || '#1F2937' }}
                  >
                    {day.date.getDate()}
                  </div>
                  {renderDayEvents(day)}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

// Day Modal Component
const DayModal = ({ day, template, onClose, onEventClick }) => {
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [eventHoverTimer, setEventHoverTimer] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  if (!day) return null;

  const handleEventHover = (event) => {
    // Check if event hover is enabled in calendar config
    if (template?.calendar_config?.enableEventHover === false) {
      return;
    }
    
    if (eventHoverTimer) clearTimeout(eventHoverTimer);
    
    setHoveredEvent(event);
    
    // Use configured event hover delay (default 300ms if not set)
    const eventHoverDelay = template?.calendar_config?.eventHoverDelay || 300;
    
    const timer = setTimeout(() => {
      setSelectedEvent(event);
      setEventModalOpen(true);
    }, eventHoverDelay);
    setEventHoverTimer(timer);
  };

  const handleEventHoverExit = () => {
    // Only clean up if event hover is enabled
    if (template?.calendar_config?.enableEventHover === false) {
      return;
    }
    
    if (eventHoverTimer) {
      clearTimeout(eventHoverTimer);
      setEventHoverTimer(null);
    }
    setHoveredEvent(null);
  };

  const colors = template?.theme_config?.colors || {};
  const { featuredEvents, otherEvents } = day;

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: colors.text || '#1F2937' }}>
              Events for {day.date.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Featured Events */}
            {featuredEvents.length > 0 && (
              <div>
                <h3 
                  className="text-lg font-semibold mb-3 flex items-center"
                  style={{ color: colors.primary || '#9275A8' }}
                >
                  <Star className="w-5 h-5 mr-2" />
                  Featured Events
                </h3>
                <div className="space-y-3">
                  {featuredEvents.map(event => (
                    <DayEventCard 
                      key={event.id} 
                      event={event} 
                      template={template}
                      isHovered={hoveredEvent?.id === event.id}
                      onHover={() => handleEventHover(event)}
                      onHoverExit={handleEventHoverExit}
                      onClick={() => onEventClick(event)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Other Events */}
            {otherEvents.length > 0 && (
              <div>
                <h3 
                  className="text-lg font-semibold mb-3"
                  style={{ color: colors.text || '#1F2937' }}
                >
                  All Events
                </h3>
                <div className="space-y-3">
                  {otherEvents.map(event => (
                    <DayEventCard 
                      key={event.id} 
                      event={event} 
                      template={template}
                      isHovered={hoveredEvent?.id === event.id}
                      onHover={() => handleEventHover(event)}
                      onHoverExit={handleEventHoverExit}
                      onClick={() => onEventClick(event)}
                    />
                  ))}
                </div>
              </div>
            )}

            {featuredEvents.length === 0 && otherEvents.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-3" style={{ color: colors.textSecondary || '#6B7280' }} />
                <p style={{ color: colors.textSecondary || '#6B7280' }}>No events scheduled for this day</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal (Second Level) */}
      {eventModalOpen && selectedEvent && (
        <EventModal
          event={selectedEvent}
          template={template}
          onClose={() => {
            setEventModalOpen(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </>
  );
};

// Helper function to get event image (featured or first available)
// Used by multiple components to display event images
const resolveEventImageSrc = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http') || imageUrl.startsWith('/images/')) return imageUrl;
  return `${BACKEND_URL}${imageUrl}`;
};

const getEventImage = (event, useFallback = true) => {
  // First try featured_image
  if (event.featured_image && (event.featured_image.url || event.featured_image.public_url)) {
    return event.featured_image.url || event.featured_image.public_url;
  }
  // Try image_url (used by iCal sync)
  if (event.image_url) {
    return event.image_url;
  }
  // Fallback to first image in images array
  if (event.images && event.images.length > 0) {
    const firstImage = event.images[0];
    if (typeof firstImage === 'string') {
      return firstImage;
    }
    if (firstImage?.url || firstImage?.public_url) {
      return firstImage.url || firstImage.public_url;
    }
  }
  return useFallback ? EVENT_FALLBACK_IMAGE : null;
};

// Day Event Card Component
const DayEventCard = ({ event, template, isHovered, onHover, onHoverExit, onClick }) => {
  const colors = template?.theme_config?.colors || {};
  // Disable visibility tracking in calendar builder
  const trackElement = () => {};
  const cardRef = useRef(null);

  // Visibility tracking disabled for calendar builder
  useEffect(() => {
    // Tracking disabled to prevent unwanted hover triggers
  }, [event?.id]);

  return (
    <div
      ref={cardRef}
      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
        isHovered ? 'transform scale-102 shadow-lg ring-2' : 'hover:shadow-md'
      }`}
      style={{ 
        borderColor: colors.border || '#E5E7EB',
        backgroundColor: colors.background || '#FFFFFF',
        ringColor: isHovered ? colors.primary || '#9275A8' : 'transparent'
      }}
      onMouseEnter={onHover}
      onMouseLeave={onHoverExit}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        {(() => {
          const imageUrl = getEventImage(event);
          return imageUrl && template?.calendar_config?.showImages !== false && (
            <img
              src={resolveEventImageSrc(imageUrl)}
              alt={event.title}
              className="w-16 h-16 object-cover rounded"
            />
          );
        })()}
        
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold truncate" style={{ color: colors.text || '#1F2937' }}>
            {event.title}
          </h4>
          <p className="text-sm mt-1 line-clamp-2" style={{ color: colors.textSecondary || '#6B7280' }}>
            {event.description}
          </p>
          
          <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
            <span className="flex items-center gap-1" style={{ color: colors.textSecondary || '#6B7280' }}>
              <Clock className="w-4 h-4" />
              {event.is_all_day ? (
                event.is_multi_day && event.end_at ? 
                  `${new Date(event.start_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(event.end_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` :
                  'All Day'
              ) : (
                new Date(event.start_at).toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit'
                })
              )}
            </span>
            
            {template?.calendar_config?.showVenues !== false && event.venue_name && (
              <span className="flex items-center gap-1 text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                <MapPin className="w-4 h-4" />
                {event.venue_name}
              </span>
            )}
            
            {template?.calendar_config?.showOrganizers !== false && event.organizer_name && (
              <span className="flex items-center gap-1 text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                <Users className="w-4 h-4" />
                {event.organizer_name}
              </span>
            )}
            
            {template?.calendar_config?.showPrices !== false && ((event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free') && (
              <span 
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ 
                  backgroundColor: colors.accent + '20' || '#F59E0B20',
                  color: colors.accent || '#F59E0B'
                }}
              >
                Free
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Event Modal Component
const EventModal = ({ event, template, onClose }) => {
  if (!event) return null;

  const colors = template?.theme_config?.colors || {};
  const navigate = useNavigate();
  const location = useLocation();

  // Track event click when modal opens
  useEffect(() => {
    if (event?.id) {
      EventTracker.trackClick(event.id);
    }
  }, [event?.id]);

  const handleTicketClick = (url) => {
    if (event?.id) {
      EventTracker.trackTicketClick(event.id);
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleViewFullDetails = () => {
    const isCalendarPreviewRoute = location.pathname.startsWith('/calendar-preview/');
    const destination = isCalendarPreviewRoute
      ? `${location.pathname.replace(/\/$/, '')}/events/${event.id}`
      : `/events/${event.id}`;

    onClose?.();
    navigate(destination);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ color: colors.text || '#1F2937' }}>
            {event.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {(() => {
            const imageUrl = getEventImage(event);
            return imageUrl && template?.calendar_config?.showImages !== false && (
              <div className="aspect-video w-full rounded-lg overflow-hidden">
                <img
                  src={resolveEventImageSrc(imageUrl)}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: colors.text || '#1F2937' }}>
                  Description
                </h3>
                <p style={{ color: colors.textSecondary || '#6B7280' }}>
                  {event.description}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: colors.text || '#1F2937' }}>
                  Event Details
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: colors.primary || '#9275A8' }} />
                    <span className="text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                      {new Date(event.start_at).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" style={{ color: colors.primary || '#9275A8' }} />
                    <span className="text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                      {new Date(event.start_at).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit'
                      })} - {new Date(event.end_at).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  {template?.calendar_config?.showVenues !== false && (event.venue_id || event.venue_name || event.venue_address || event.venue_city || event.venue_state || event.venue_postal_code) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.primary || '#9275A8' }} />
                      <div className="text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                        <div>{event.venue_name || 'Event Venue'}</div>
                        {(() => {
                          const locationParts = [
                            event.venue_address,
                            [event.venue_city, event.venue_state].filter(Boolean).join(', ') || null,
                            event.venue_postal_code,
                          ].filter(Boolean);

                          return locationParts.length > 0 ? (
                            <div className="text-xs mt-0.5" style={{ color: colors.textSecondary || '#6B7280' }}>
                              {locationParts.join(', ')}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Pricing Display */}
                  {template?.calendar_config?.showPrices !== false && (
                    <div className="space-y-2">
                      {(() => {
                        const pricing = formatPricing(event);
                        if (pricing.type === 'free') {
                          return (
                            <div 
                              className="px-3 py-2 rounded-lg text-center font-medium"
                              style={{ 
                                backgroundColor: colors.accent + '20' || '#F59E0B20',
                                color: colors.accent || '#F59E0B'
                              }}
                            >
                              {pricing.display}
                            </div>
                          );
                        } else if (pricing.type === 'two_tier') {
                          return (
                            <div className="space-y-2">
                              {pricing.display.map((tier, idx) => (
                                <div 
                                  key={idx}
                                  className="px-3 py-2 rounded-lg font-medium flex justify-between items-center"
                                  style={{ 
                                    backgroundColor: colors.primary + '20' || '#9275A820',
                                    color: colors.primary || '#9275A8'
                                  }}
                                >
                                  <span>{tier.label}:</span>
                                  <span className="font-bold">{tier.price}</span>
                                </div>
                              ))}
                            </div>
                          );
                        } else if (pricing.type === 'none') {
                          return null;
                        } else {
                          return (
                            <div 
                              className="px-3 py-2 rounded-lg text-center font-medium"
                              style={{ 
                                backgroundColor: colors.primary + '20' || '#9275A820',
                                color: colors.primary || '#9275A8'
                              }}
                            >
                              {pricing.display}
                            </div>
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Fields Display */}
              {event.custom_fields && Object.keys(event.custom_fields).length > 0 && (
                <div className="space-y-3 border-t pt-4 mt-4">
                  <h4 className="font-semibold text-sm" style={{ color: colors.text || '#1F2937' }}>
                    Additional Information
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(event.custom_fields).map(([key, value]) => {
                      if (!value) return null;
                      
                      // Skip complex objects like submitter (they're displayed elsewhere)
                      if (typeof value === 'object' && !Array.isArray(value)) return null;
                      
                      // Convert snake_case to Title Case
                      const label = key.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ');
                      
                      // Format the value for display
                      let displayValue = value;
                      if (typeof value === 'boolean') {
                        displayValue = value ? 'Yes' : 'No';
                      } else if (Array.isArray(value)) {
                        displayValue = value.join(', ');
                      } else {
                        displayValue = String(value);
                      }
                      
                      return (
                        <div key={key} className="text-sm">
                          <span className="font-medium" style={{ color: colors.text || '#1F2937' }}>
                            {label}:
                          </span>{' '}
                          <span style={{ color: colors.textSecondary || '#6B7280' }}>
                            {displayValue}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full"
                style={{ 
                  borderColor: colors.primary || '#9275A8',
                  color: colors.primary || '#9275A8'
                }}
                onClick={handleViewFullDetails}
              >
                View full event details
              </Button>

              {event.ticket_url && (
                <Button
                  className="w-full"
                  style={{ 
                    backgroundColor: colors.primary || '#9275A8',
                    color: 'white'
                  }}
                  onClick={() => handleTicketClick(event.ticket_url)}
                >
                  Get Tickets
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PublicEventDetailsPage = ({ templateOverride = null, eventsOverride = null }) => {
  const { templateId, eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [template, setTemplate] = useState(templateOverride);
  const [events, setEvents] = useState(Array.isArray(eventsOverride) ? eventsOverride : []);
  const [loading, setLoading] = useState(!templateOverride);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (templateOverride) {
      setTemplate(templateOverride);
      setEvents(Array.isArray(eventsOverride) ? eventsOverride : []);
      setLoading(false);
      return;
    }

    const loadPageData = async () => {
      try {
        setLoading(true);
        const templateRes = await axios.get(`${API}/calendar-templates/${templateId}`);
        setTemplate(templateRes.data);

        try {
          const eventsRes = await axios.get(`${API}/events`);
          const publishedEvents = eventsRes.data.filter(e => e.status === 'published');
          setEvents(publishedEvents);
        } catch (eventsError) {
          console.warn('Could not load published events for public details page:', eventsError);
          setEvents([]);
        }
      } catch (err) {
        console.error('Error loading full event details page:', err);
        setError('Unable to load this event right now.');
      } finally {
        setLoading(false);
      }
    };

    loadPageData();
  }, [templateId, templateOverride, eventsOverride]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p>Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700 mb-4">{error || 'Unable to load this event.'}</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const event = events.find(e => String(e.id) === String(eventId));
  const colors = template?.theme_config?.colors || {};
  const isCalendarPreviewRoute = location.pathname.startsWith('/calendar-preview/');
  const calendarPath = isCalendarPreviewRoute ? `/calendar-preview/${templateId}` : '/';

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: colors.background || '#FFFFFF' }}>
        <div className="text-center max-w-lg">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: colors.primary || '#9275A8' }} />
          <h1 className="text-2xl font-bold mb-2" style={{ color: colors.text || '#1F2937' }}>Event not found</h1>
          <p className="mb-6" style={{ color: colors.textSecondary || '#6B7280' }}>
            This event may have been removed or is no longer available on the public calendar.
          </p>
          <Button
            onClick={() => navigate(calendarPath)}
            style={{ backgroundColor: colors.primary || '#9275A8', color: 'white' }}
          >
            Back to calendar
          </Button>
        </div>
      </div>
    );
  }

  const formatDateRange = () => {
    const start = new Date(event.start_at);
    const end = event.end_at ? new Date(event.end_at) : null;
    const startDate = start.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const endTime = end ? end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;
    return endTime ? `${startDate} • ${startTime} - ${endTime}` : `${startDate} • ${startTime}`;
  };

  const fullAddress = [
    event.venue_address,
    [event.venue_city, event.venue_state].filter(Boolean).join(', ') || null,
    event.venue_postal_code,
  ].filter(Boolean).join(', ');

  const normalizeSeriesKey = (candidate) => {
    const normalizedTitle = (candidate.title || '')
      .toLowerCase()
      .replace(/\([^)]*\)/g, '')
      .replace(/\[[^\]]*\]/g, '')
      .replace(/\b(live|show|concert|performance|night|series)\b/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    return [
      normalizedTitle,
      candidate.host_id || candidate.organizer_name || '',
      candidate.venue_id || candidate.venue_name || '',
    ].join('|');
  };

  const sortUpcoming = (items) => items
    .slice()
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

  const dedupeBySeries = (items, limit = 8) => {
    const seen = new Set();
    const output = [];

    for (const item of sortUpcoming(items)) {
      const key = normalizeSeriesKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      output.push(item);
      if (output.length >= limit) break;
    }

    return output;
  };

  const eventCategories = new Set(event.category_ids || []);
  const eventTags = new Set((event.tags || []).map(tag => String(tag).toLowerCase()));

  const candidateEvents = sortUpcoming(events.filter(candidate => candidate.id !== event.id));

  const otherDatesForThisEvent = candidateEvents
    .filter(candidate => normalizeSeriesKey(candidate) === normalizeSeriesKey(event))
    .slice(0, 8);

  const organizerEvents = dedupeBySeries(
    candidateEvents.filter(candidate => event.host_id && candidate.host_id === event.host_id && normalizeSeriesKey(candidate) !== normalizeSeriesKey(event)),
    8
  );

  const venueEvents = dedupeBySeries(
    candidateEvents.filter(candidate => event.venue_id && candidate.venue_id === event.venue_id && normalizeSeriesKey(candidate) !== normalizeSeriesKey(event)),
    8
  );

  const similarEvents = dedupeBySeries(
    candidateEvents
      .map(candidate => {
        let score = 0;
        const candidateCategoryIds = candidate.category_ids || [];
        const sharedCategories = candidateCategoryIds.filter(categoryId => eventCategories.has(categoryId));
        const sharedTags = (candidate.tags || []).filter(tag => eventTags.has(String(tag).toLowerCase()));

        if (sharedCategories.length > 0) score += sharedCategories.length * 3;
        if (sharedTags.length > 0) score += sharedTags.length * 2;
        if (candidate.event_type && event.event_type && candidate.event_type === event.event_type) score += 1;
        if (candidate.host_id && event.host_id && candidate.host_id === event.host_id) score -= 2;
        if (candidate.venue_id && event.venue_id && candidate.venue_id === event.venue_id) score -= 1;
        if (normalizeSeriesKey(candidate) === normalizeSeriesKey(event)) score -= 10;

        return { candidate, score };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return new Date(a.candidate.start_at) - new Date(b.candidate.start_at);
      })
      .map(item => item.candidate),
    8
  );

  const relatedSections = [
    {
      key: 'other-dates',
      title: 'Other dates for this event',
      description: 'More upcoming instances from this event series.',
      events: otherDatesForThisEvent,
    },
    {
      key: 'organizer',
      title: `More from ${event.organizer_name || 'this organizer'}`,
      description: 'Other upcoming events from the same organizer or performer.',
      events: organizerEvents,
    },
    {
      key: 'venue',
      title: `More at ${event.venue_name || 'this venue'}`,
      description: 'Other upcoming events happening at the same venue.',
      events: venueEvents,
    },
    {
      key: 'similar',
      title: 'You might also like',
      description: 'More diverse picks based on shared categories, tags, and event type.',
      events: similarEvents,
    },
  ].filter(section => section.events.length > 0);

  const imageUrl = getEventImage(event);
  const pricing = formatPricing(event);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const resolvedShareImage = imageUrl ? resolveEventImageSrc(imageUrl) : EVENT_FALLBACK_IMAGE;
  const absoluteShareImage = typeof window !== 'undefined' && resolvedShareImage
    ? new URL(resolvedShareImage, window.location.origin).href
    : resolvedShareImage;
  const shareDescription = [
    event.description,
    formatDateRange(),
    event.venue_name || fullAddress,
  ].filter(Boolean).join(' • ');
  const shareText = `${event.title}
${formatDateRange()}${event.venue_name ? `
${event.venue_name}` : ''}${event.description ? `

${event.description}` : ''}`;
  const encodedShareUrl = encodeURIComponent(shareUrl);
  const encodedShareTitle = encodeURIComponent(event.title || 'Event');
  const encodedShareText = encodeURIComponent(shareText);
  const rawEventWebsite = event.custom_fields?.musicians_website || event.custom_fields?.event_website || event.custom_fields?.website_url || event.event_website;
  const eventWebsite = rawEventWebsite
    ? (/^https?:\/\//i.test(String(rawEventWebsite)) ? String(rawEventWebsite) : `https://${String(rawEventWebsite)}`)
    : null;
  const handleCopyShareUrl = async () => {
    await navigator.clipboard?.writeText(shareUrl);
    toast({ title: 'Link copied', description: 'Event link copied to clipboard.' });
  };
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text: shareText, url: shareUrl });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await navigator.clipboard?.writeText(shareUrl);
    toast({ title: 'Link copied', description: 'Event link copied to clipboard.' });
  };

  const RelatedEventScroller = ({ title, description, items }) => (
    <div className="space-y-3">
      <div>
        <h3 className="text-xl font-semibold" style={{ color: colors.text || '#1F2937' }}>{title}</h3>
        {description && (
          <p className="text-sm mt-1" style={{ color: colors.textSecondary || '#6B7280' }}>{description}</p>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2">
        {items.map(candidate => {
          const candidateImage = getEventImage(candidate);
          return (
            <button
              key={candidate.id}
              type="button"
              onClick={() => navigate(isCalendarPreviewRoute ? `/calendar-preview/${templateId}/events/${candidate.id}` : `/events/${candidate.id}`)}
              className="shrink-0 w-[280px] text-left border rounded-2xl overflow-hidden transition-all hover:shadow-md"
              style={{ borderColor: colors.border || '#E5E7EB', backgroundColor: colors.surface || '#FFFFFF' }}
            >
              {candidateImage && template?.calendar_config?.showImages !== false && (
                <div className="h-40 w-full overflow-hidden">
                  <img
                    src={resolveEventImageSrc(candidateImage)}
                    alt={candidate.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-4 space-y-2">
                <div className="font-semibold line-clamp-2" style={{ color: colors.text || '#1F2937' }}>{candidate.title}</div>
                <div className="text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                  {new Date(candidate.start_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' • '}
                  {new Date(candidate.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
                {(candidate.organizer_name || candidate.venue_name) && (
                  <div className="text-sm line-clamp-2" style={{ color: colors.textSecondary || '#6B7280' }}>
                    {[candidate.organizer_name, candidate.venue_name].filter(Boolean).join(' • ')}
                  </div>
                )}
                {candidate.category_names?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {candidate.category_names.slice(0, 2).map(category => (
                      <Badge key={category} variant="secondary">{category}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background || '#FFFFFF' }}>
      <Helmet>
        <title>{`${event.title} | What's Happening Events`}</title>
        <meta name="description" content={shareDescription} />
        <meta property="og:type" content="event" />
        <meta property="og:title" content={event.title} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:url" content={shareUrl} />
        {absoluteShareImage && <meta property="og:image" content={absoluteShareImage} />}
        <meta property="event:start_time" content={event.start_at} />
        {event.end_at && <meta property="event:end_time" content={event.end_at} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event.title} />
        <meta name="twitter:description" content={shareDescription} />
        {absoluteShareImage && <meta name="twitter:image" content={absoluteShareImage} />}
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(calendarPath)}
            style={{ borderColor: colors.primary || '#9275A8', color: colors.primary || '#9275A8' }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to calendar
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold mb-4" style={{ color: colors.text || '#1F2937' }}>
                {event.title}
              </h1>
              <div className="flex flex-wrap gap-3 text-sm">
                {event.category_names?.map(category => (
                  <Badge
                    key={category}
                    variant="outline"
                    style={{ borderColor: colors.primary || '#9275A8', color: colors.primary || '#9275A8' }}
                  >
                    {category}
                  </Badge>
                ))}
              </div>
            </div>

            {imageUrl && template?.calendar_config?.showImages !== false && (
              <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={resolveEventImageSrc(imageUrl)}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <Card style={{ borderColor: colors.border || '#E5E7EB', backgroundColor: colors.surface || '#FFFFFF' }}>
              <CardHeader>
                <CardTitle style={{ color: colors.text || '#1F2937' }}>About this event</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="leading-7 whitespace-pre-wrap" style={{ color: colors.textSecondary || '#6B7280' }}>
                  {event.description || 'More details coming soon.'}
                </p>
              </CardContent>
            </Card>

            {event.custom_fields && Object.keys(event.custom_fields).length > 0 && (
              <Card style={{ borderColor: colors.border || '#E5E7EB', backgroundColor: colors.surface || '#FFFFFF' }}>
                <CardHeader>
                  <CardTitle style={{ color: colors.text || '#1F2937' }}>Additional information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(event.custom_fields).map(([key, value]) => {
                    if (!value) return null;
                    if (['musicians_website', 'event_website', 'website_url'].includes(key)) return null;
                    if (typeof value === 'object' && !Array.isArray(value)) return null;
                    const label = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                    const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : Array.isArray(value) ? value.join(', ') : String(value);
                    return (
                      <div key={key} className="text-sm">
                        <span className="font-medium" style={{ color: colors.text || '#1F2937' }}>{label}:</span>{' '}
                        <span style={{ color: colors.textSecondary || '#6B7280' }}>{displayValue}</span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            <Card style={{ borderColor: colors.border || '#E5E7EB', backgroundColor: colors.surface || '#FFFFFF' }}>
              <CardHeader>
                <CardTitle style={{ color: colors.text || '#1F2937' }}>Keep exploring</CardTitle>
                <CardDescription>
                  Dedicated lanes for other dates, more from the organizer, more at the venue, and similar events.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {relatedSections.length === 0 ? (
                  <p style={{ color: colors.textSecondary || '#6B7280' }}>No related events found yet.</p>
                ) : (
                  relatedSections.map(section => (
                    <RelatedEventScroller
                      key={section.key}
                      title={section.title}
                      description={section.description}
                      items={section.events}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card style={{ borderColor: colors.border || '#E5E7EB', backgroundColor: colors.surface || '#FFFFFF' }}>
              <CardHeader>
                <CardTitle style={{ color: colors.text || '#1F2937' }}>Event details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 mt-0.5" style={{ color: colors.primary || '#9275A8' }} />
                  <div className="text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>{formatDateRange()}</div>
                </div>

                {(event.venue_name || fullAddress) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 mt-0.5" style={{ color: colors.primary || '#9275A8' }} />
                    <div className="text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                      {event.venue_name && <div className="font-medium" style={{ color: colors.text || '#1F2937' }}>{event.venue_name}</div>}
                      {fullAddress && <div>{fullAddress}</div>}
                    </div>
                  </div>
                )}

                {eventWebsite && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 mt-0.5" style={{ color: colors.primary || '#9275A8' }} />
                    <div className="text-sm" style={{ color: colors.textSecondary || '#6B7280' }}>
                      <div className="font-medium" style={{ color: colors.text || '#1F2937' }}>Musician Website</div>
                      <a
                        href={eventWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all hover:underline"
                        style={{ color: colors.primary || '#9275A8' }}
                      >
                        {String(rawEventWebsite).replace(/^https?:\/\//i, '')}
                      </a>
                    </div>
                  </div>
                )}

                {pricing.display && (
                  <div className="rounded-xl px-4 py-3 text-center font-medium" style={{ backgroundColor: `${colors.primary || '#9275A8'}20`, color: colors.primary || '#9275A8' }}>
                    {Array.isArray(pricing.display) ? pricing.display.map(tier => `${tier.label}: ${tier.price}`).join(' • ') : pricing.display}
                  </div>
                )}

                {event.ticket_url && (
                  <Button
                    className="w-full"
                    style={{ backgroundColor: colors.primary || '#9275A8', color: 'white' }}
                    onClick={() => {
                      EventTracker.trackTicketClick(event.id);
                      window.open(event.ticket_url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Get Tickets
                  </Button>
                )}

                <div className="pt-4 border-t space-y-3" style={{ borderColor: colors.border || '#E5E7EB' }}>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    style={{ borderColor: colors.primary || '#9275A8', color: colors.primary || '#9275A8' }}
                    onClick={handleShare}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share this event
                  </Button>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <button type="button" className="text-center rounded-md border px-3 py-2 hover:bg-gray-50" onClick={handleCopyShareUrl}>
                      <Copy className="w-4 h-4 inline-block mr-1" />
                      Copy Link
                    </button>
                    <a className="text-center rounded-md border px-3 py-2 hover:bg-gray-50" href={`https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`} target="_blank" rel="noopener noreferrer">Facebook</a>
                    <a className="text-center rounded-md border px-3 py-2 hover:bg-gray-50" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`} target="_blank" rel="noopener noreferrer">LinkedIn</a>
                    <a className="text-center rounded-md border px-3 py-2 hover:bg-gray-50" href={`mailto:?subject=${encodedShareTitle}&body=${encodedShareText}%0A%0A${encodedShareUrl}`}>Email</a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Preview Component (Updated)
const CalendarPreview = ({ template }) => {
  const [events, setEvents] = useState([]);
  
  useEffect(() => {
    // Load sample events for preview
    const loadEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data.filter(e => e.status === 'published')); // Only show published events
      } catch (error) {
        console.error('Error loading events for preview:', error);
      }
    };
    loadEvents();
  }, []);

  return (
    <Card className="bg-white/60 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg">Live Preview</CardTitle>
        <CardDescription>Interactive calendar with hover effects and modals</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden bg-white p-6">
          {/* Mini Search Bar for Preview */}
          <div className="mb-6">
            <RealtimeSearch
              placeholder="Search events in preview..."
              onEventSelect={(event) => {
                console.log('Selected event from preview search:', event);
              }}
              className="text-sm"
              showSuggestions={false}
              embedded={true}
            />
          </div>
          
          {/* Quick Search Filter Buttons Preview */}
          <div className="mb-6 scale-90 origin-top">
            <QuickSearchFilterButtons template={template} />
          </div>
          
          <InteractiveCalendar template={template} events={events} />
        </div>
      </CardContent>
    </Card>
  );
};

// Event List Component
const EventList = ({ 
  title, description, events, searchTerm, setSearchTerm, filterStatus, setFilterStatus,
  hidePastEvents, setHidePastEvents,
  getOrganizerName, getVenueName, formatDate, getStatusColor, showActions,
  deleteEvent, duplicateEvent, publishEvent, unpublishEvent, publishingEventId, featuredEvents, addToFeatured, removeFromFeatured, 
  promoterMode, showPerformance
}) => {
  return (
    <>
      {/* Filters */}
      <Card className="mb-8 bg-white/60 backdrop-blur-sm">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Events</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Search by title or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {!promoterMode && (
              <div className="w-full sm:w-48">
                <Label>Filter by Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-full sm:w-48 flex items-end">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hidePastEvents"
                  checked={hidePastEvents}
                  onChange={(e) => setHidePastEvents(e.target.checked)}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                />
                <Label htmlFor="hidePastEvents" className="text-sm font-normal cursor-pointer">
                  Hide past events
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            getOrganizerName={getOrganizerName}
            getVenueName={getVenueName}
            formatDate={formatDate}
            getStatusColor={getStatusColor}
            showActions={showActions}
            deleteEvent={deleteEvent}
            duplicateEvent={duplicateEvent}
            publishEvent={publishEvent}
            unpublishEvent={unpublishEvent}
            publishingEventId={publishingEventId}
            featuredEvents={featuredEvents}
            addToFeatured={addToFeatured}
            removeFromFeatured={removeFromFeatured}
            promoterMode={promoterMode}
            showPerformance={showPerformance}
          />
        ))}
      </div>

      {events.length === 0 && (
        <Card className="bg-white/60 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No events found</h3>
            <p className="text-gray-600 mb-6">
              {promoterMode ? "No events match your search criteria" : "Get started by creating your first event"}
            </p>
            {!promoterMode && (
              <Link to="/events/create">
                <Button className="bg-brand-primary hover:bg-brand-primary text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

// Event Card Component
const EventCard = ({ 
  event, getOrganizerName, getVenueName, formatDate, getStatusColor,
  showActions, deleteEvent, duplicateEvent, publishEvent, unpublishEvent, publishingEventId, featuredEvents, addToFeatured, 
  removeFromFeatured, promoterMode, showPerformance
}) => {
  const isFeatured = featuredEvents && featuredEvents.includes(event.id);

  return (
    <Card className="bg-white/70 backdrop-blur-sm hover:shadow-lg transition-all duration-200 border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-gray-900 line-clamp-2">
              {showPerformance && event.status === 'published' ? (
                <Link to={`/events/${event.id}/performance`} className="hover:text-brand-primary">
                  {event.title}
                </Link>
              ) : (
                event.title
              )}
            </CardTitle>
            <CardDescription className="text-sm text-gray-600 mt-1">
              by {getOrganizerName(event.host_id || event.organizer_id, event)}
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className={`${getStatusColor(event.status)} border-0`}>
              {event.status}
            </Badge>
            {isFeatured && (
              <Star className="w-4 h-4 text-yellow-500 fill-current" />
            )}
          </div>
        </div>
      </CardHeader>
      
      {/* Event Image */}
      {(() => {
        const imageUrl = getEventImage(event);
        return imageUrl && (
          <div className="px-6 pb-4">
            <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={resolveEventImageSrc(imageUrl)}
                alt={event.title}
                className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="absolute inset-0 bg-gray-200 hidden items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {event.images && event.images.length > 1 && (
                <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                  +{event.images.length - 1} more
                </div>
              )}
            </div>
          </div>
        );
      })()}
      
      <CardContent>
        <div className="space-y-3">
          <p className="text-sm text-gray-600 line-clamp-3">{event.description}</p>
          
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-2" />
            {formatDate(event.start_at)}
          </div>
          
          {event.venue_id && (
            <div className="flex items-center text-sm text-gray-500">
              <MapPin className="w-4 h-4 mr-2" />
              {getVenueName(event.venue_id)}
            </div>
          )}

          {event.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {event.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {event.tags.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{event.tags.length - 3} more
                </Badge>
              )}
            </div>
          )}

          {/* Individual Event Analytics */}
          {showPerformance && event.status === 'published' && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <EventAnalytics eventId={event.id} inline={true} />
            </div>
          )}

          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center space-x-2">
              {showActions && (
                <>
                  <EditEventModal 
                    event={event}
                    onEventUpdated={() => window.location.reload()} // Simple refresh for now
                  />
                  {event.status === 'draft' ? (
                    <Button 
                      onClick={() => publishEvent(event.id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                      disabled={publishingEventId === event.id}
                    >
                      {publishingEventId === event.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Publish
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => unpublishEvent(event.id)}
                      variant="outline"
                      className="border-orange-600 text-orange-600 hover:bg-orange-50"
                      size="sm"
                      disabled={publishingEventId === event.id}
                    >
                      {publishingEventId === event.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Unpublishing...
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          Unpublish
                        </>
                      )}
                    </Button>
                  )}
                  {event.status === 'published' && showPerformance && (
                    <Link to={`/events/${event.id}/performance`}>
                      <Button variant="outline" size="sm" className="text-purple-600 hover:text-purple-700">
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => duplicateEvent(event.id)}
                    className="text-blue-600 hover:text-blue-700"
                    title="Duplicate Event"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => deleteEvent(event.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
              {promoterMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => isFeatured ? removeFromFeatured(event.id) : addToFeatured(event.id)}
                  className={isFeatured ? "text-yellow-600 hover:text-yellow-700" : "text-purple-600 hover:text-purple-700"}
                >
                  <Star className={`w-4 h-4 ${isFeatured ? 'fill-current' : ''}`} />
                </Button>
              )}
            </div>
            {(() => {
              // Format pricing for event card display
              const pricingStatus = event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'));
              if (pricingStatus === 'free') {
                return (
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    Free
                  </Badge>
                );
              }

              if (pricingStatus === 'unspecified') {
                return null;
              }
              
              const pricing_type = event.pricing_type || 'single';
              const price_min = event.price_min;
              const price_max = event.price_max;
              const vip_price = event.vip_price;
              
              if (pricing_type === 'two_tier' && price_min && vip_price) {
                return (
                  <div className="flex flex-col gap-1">
                    <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                      GA: ${price_min.toFixed(2)}
                    </Badge>
                    <Badge variant="outline" className="text-purple-600 border-purple-300 text-xs">
                      VIP: ${vip_price.toFixed(2)}
                    </Badge>
                  </div>
                );
              } else if (pricing_type === 'range' && price_min && price_max && price_min !== price_max) {
                return (
                  <Badge variant="outline" className="text-blue-600 border-blue-300">
                    ${price_min.toFixed(2)} - ${price_max.toFixed(2)}
                  </Badge>
                );
              } else if (price_min) {
                return (
                  <Badge variant="outline" className="text-blue-600 border-blue-300">
                    ${price_min.toFixed(2)}
                  </Badge>
                );
              }
              
              return null;
            })()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Bulk Import Section Component
const BulkImportSection = ({ hosts, selectedOrganizer, onBulkImportComplete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();

  const handleFileSelect = (file) => {
    if (file && file.name.endsWith('.csv')) {
      setCsvFile(file);
      setValidationResult(null);
    } else {
      toast({
        title: "Error",
        description: "Please select a CSV file",
        variant: "destructive",
      });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const downloadTemplate = async () => {
    if (!selectedOrganizer) {
      toast({
        title: "Error",
        description: "Please select an organizer first",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await axios.get(`${API}/events/csv-template/${selectedOrganizer}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `event_bulk_import_template_${selectedOrganizer.substring(0, 8)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: "CSV template downloaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    }
  };

  const validateCsv = async () => {
    if (!csvFile || !selectedOrganizer) {
      toast({
        title: "Error",
        description: "Please select a CSV file and organizer",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('organizer_id', selectedOrganizer);

      const response = await axios.post(`${API}/events/bulk-validate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setValidationResult(response.data);
      
      if (response.data.error_count === 0) {
        toast({
          title: "Validation Successful",
          description: `${response.data.valid_count} events ready to import`,
        });
      } else {
        toast({
          title: "Validation Issues Found",
          description: `${response.data.valid_count} valid, ${response.data.error_count} errors`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to validate CSV",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const importEvents = async () => {
    if (!validationResult || validationResult.valid_count === 0) {
      toast({
        title: "Error",
        description: "No valid events to import",
        variant: "destructive",
      });
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(`${API}/events/bulk-create`, {
        organizer_id: selectedOrganizer,
        events_data: validationResult.preview_data
      });

      toast({
        title: "Import Successful",
        description: `${response.data.created_count} events created successfully`,
      });

      // Reset state
      setCsvFile(null);
      setValidationResult(null);
      setIsExpanded(false);
      
      // Call completion callback
      if (onBulkImportComplete) {
        onBulkImportComplete();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to import events",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadErrorReport = () => {
    if (!validationResult?.errors.length) return;

    const csvContent = [
      ['Row', 'Field', 'Value', 'Error'],
      ...validationResult.errors.map(err => [err.row, err.field, err.value, err.error])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk_import_errors.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="border-t pt-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Bulk Import Events</h3>
          <p className="text-sm text-gray-600">Import multiple events from a CSV file (max 100 events)</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2"
        >
          <Upload className="w-4 h-4" />
          <span>{isExpanded ? 'Hide' : 'Show'} Bulk Import</span>
        </Button>
      </div>

      {isExpanded && (
        <div className="space-y-6 p-6 border rounded-lg bg-gray-50">
          {/* Step 1: Download Template */}
          <div className="space-y-3">
            <h4 className="font-medium">Step 1: Download CSV Template</h4>
            <p className="text-sm text-gray-600">
              Download the template with all standard and custom fields for your organization.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={downloadTemplate}
              disabled={!selectedOrganizer}
              className="flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download Template</span>
            </Button>
          </div>

          {/* Step 2: Upload CSV */}
          <div className="space-y-3">
            <h4 className="font-medium">Step 2: Upload Completed CSV</h4>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-400 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {csvFile ? (
                <div className="space-y-2">
                  <FileText className="w-8 h-8 mx-auto text-green-600" />
                  <p className="font-medium">{csvFile.name}</p>
                  <p className="text-sm text-gray-600">
                    {(csvFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCsvFile(null)}
                  >
                    Remove File
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-8 h-8 mx-auto text-gray-400" />
                  <p className="text-gray-600">
                    Drag and drop your CSV file here, or{' '}
                    <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                      browse to upload
                      <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => handleFileSelect(e.target.files[0])}
                      />
                    </label>
                  </p>
                  <p className="text-xs text-gray-500">CSV files only, max 100 events</p>
                </div>
              )}
            </div>
          </div>

          {/* Step 3: Validate */}
          {csvFile && (
            <div className="space-y-3">
              <h4 className="font-medium">Step 3: Validate Data</h4>
              <Button
                type="button"
                onClick={validateCsv}
                disabled={loading || !selectedOrganizer}
                className="flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                <span>{loading ? 'Validating...' : 'Validate CSV'}</span>
              </Button>
            </div>
          )}

          {/* Validation Results */}
          {validationResult && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Validation Results</h4>
                {validationResult.errors.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadErrorReport}
                    className="flex items-center space-x-1"
                  >
                    <Download className="w-3 h-3" />
                    <span>Download Errors</span>
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">
                      {validationResult.valid_count} Valid Events
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-800">
                      {validationResult.error_count} Errors
                    </span>
                  </div>
                </div>
              </div>

              {/* Preview Table */}
              {validationResult.preview_data.length > 0 && (
                <div className="space-y-2">
                  <h5 className="font-medium">Preview (First 10 Events)</h5>
                  <div className="max-h-64 overflow-y-auto border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Title</th>
                          <th className="px-3 py-2 text-left">Start Date</th>
                          <th className="px-3 py-2 text-left">Type</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.preview_data.slice(0, 10).map((event, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-3 py-2">{event.title}</td>
                            <td className="px-3 py-2">
                              {new Date(event.start_at).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-2">{event.event_type}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                event.status === 'published' 
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {event.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Import Button */}
              {validationResult.valid_count > 0 && (
                <div className="pt-4 border-t">
                  <Button
                    type="button"
                    onClick={importEvents}
                    disabled={importing}
                    className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-2"
                  >
                    {importing ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <span>
                      {importing 
                        ? 'Importing...' 
                        : `Import ${validationResult.valid_count} Events`
                      }
                    </span>
                  </Button>
                  <p className="text-xs text-gray-600 mt-2">
                    This will create {validationResult.valid_count} new events in your calendar.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Individual Event Analytics Component
const analyticsCache = new Map();
const ANALYTICS_TTL_MS = 2 * 60 * 1000;

const fetchAnalyticsBatch = async (eventIds) => {
  if (!eventIds || eventIds.length === 0) return {};
  const response = await axios.post(`${API}/analytics/events/batch`, {
    event_ids: eventIds
  });
  return response.data || {};
};

const EventAnalytics = ({ eventId, inline = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (eventId) {
      fetchEventAnalytics();
    }
  }, [eventId]);

  const fetchEventAnalytics = async (force = false) => {
    try {
      const cacheKey = String(eventId);
      const cached = analyticsCache.get(cacheKey);
      const isFresh = cached && (Date.now() - cached.timestamp < ANALYTICS_TTL_MS);
      if (!force && isFresh) {
        setAnalytics(cached.data);
        return;
      }
      const response = await axios.get(`${API}/analytics/events/${eventId}`);
      analyticsCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch event analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
        <div className="h-3 bg-gray-100 rounded w-16"></div>
      </div>
    );
  }

  if (!analytics) return null;

  if (inline) {
    return (
      <div className="flex items-center space-x-4 text-sm text-gray-600">
        <div className="flex items-center space-x-1">
          <Eye className="w-3 h-3" />
          <span>{analytics.visibility_views} views</span>
        </div>
        <div className="flex items-center space-x-1">
          <MousePointer className="w-3 h-3" />
          <span>{analytics.click_views} clicks</span>
        </div>
        {analytics.ticket_clicks > 0 && (
          <div className="flex items-center space-x-1">
            <TrendingUp className="w-3 h-3" />
            <span>{analytics.ticket_clicks} tickets</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="p-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Event Analytics</CardTitle>
        <CardDescription>Performance metrics for this event</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Visibility Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Visibility</span>
            </div>
            <div className="text-2xl font-bold text-blue-900">{analytics.visibility_views}</div>
            <div className="text-xs text-blue-600">Times seen in calendar</div>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <MousePointer className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Clicks</span>
            </div>
            <div className="text-2xl font-bold text-green-900">{analytics.click_views}</div>
            <div className="text-xs text-green-600">Detail views opened</div>
          </div>
        </div>

        {/* Engagement & Conversion */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-purple-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <BarChart3 className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">Engagement</span>
            </div>
            <div className="text-2xl font-bold text-purple-900">{analytics.engagement_rate}%</div>
            <div className="text-xs text-purple-600">Click-through rate</div>
          </div>
          
          <div className="bg-orange-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <TrendingUp className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">Tickets</span>
            </div>
            <div className="text-2xl font-bold text-orange-900">{analytics.ticket_clicks}</div>
            <div className="text-xs text-orange-600">{analytics.ticket_conversion_rate}% conversion</div>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="pt-3 border-t">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold">{analytics.unique_views}</div>
              <div className="text-xs text-gray-600">Unique Views</div>
            </div>
            <div>
              <div className="text-lg font-semibold">{analytics.calendar_deployments}</div>
              <div className="text-xs text-gray-600">Calendar Adds</div>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {analytics.last_updated ? new Date(analytics.last_updated).toLocaleDateString() : 'N/A'}
              </div>
              <div className="text-xs text-gray-600">Last Updated</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EventAnalyticsBatch = ({ eventIds, onLoad }) => {
  useEffect(() => {
    let isActive = true;
    const load = async () => {
      try {
        const analyticsById = await fetchAnalyticsBatch(eventIds);
        const now = Date.now();
        Object.entries(analyticsById).forEach(([id, data]) => {
          analyticsCache.set(String(id), { data, timestamp: now });
        });
        if (isActive && onLoad) {
          onLoad(analyticsById);
        }
      } catch (error) {
        console.error('Failed to fetch analytics batch:', error);
      }
    };
    if (eventIds && eventIds.length > 0) {
      load();
    }
    const intervalId = setInterval(() => {
      if (eventIds && eventIds.length > 0) {
        load();
      }
    }, ANALYTICS_TTL_MS);
    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [eventIds, onLoad]);

  return null;
};

// Global Analytics Dashboard Component
const GlobalAnalyticsDashboard = ({ analytics }) => {
  const formatNumber = (num) => {
    // Handle undefined, null, or non-numeric values
    if (num === undefined || num === null || isNaN(num)) {
      return '0';
    }
    const numValue = Number(num);
    if (numValue >= 1000) {
      return (numValue / 1000).toFixed(1) + 'k';
    }
    return numValue.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Total Visibility</span>
            </div>
            <div className="text-3xl font-bold text-blue-900 mt-2">{formatNumber(analytics.visibility_views)}</div>
            <div className="text-xs text-gray-500 mt-1">Events seen in calendars</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <MousePointer className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-600">Total Clicks</span>
            </div>
            <div className="text-3xl font-bold text-green-900 mt-2">{formatNumber(analytics.click_views)}</div>
            <div className="text-xs text-gray-500 mt-1">Event details opened</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-medium text-gray-600">Ticket Clicks</span>
            </div>
            <div className="text-3xl font-bold text-orange-900 mt-2">{formatNumber(analytics.ticket_clicks)}</div>
            <div className="text-xs text-gray-500 mt-1">Registration attempts</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-600">Engagement Rate</span>
            </div>
            <div className="text-3xl font-bold text-purple-900 mt-2">{analytics.engagement_rate}%</div>
            <div className="text-xs text-gray-500 mt-1">Clicks per visibility</div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600">Unique Visitors</div>
                <div className="text-2xl font-bold text-gray-900">{formatNumber(analytics.unique_views)}</div>
              </div>
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <div className="text-xs text-gray-500 mt-2">Distinct event viewers</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600">Active Events</div>
                <div className="text-2xl font-bold text-gray-900">{analytics.tracked_events}</div>
              </div>
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <div className="text-xs text-gray-500 mt-2">Published & tracked</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600">Conversion Rate</div>
                <div className="text-2xl font-bold text-gray-900">{analytics.ticket_conversion_rate}%</div>
              </div>
              <Star className="w-8 h-8 text-gray-400" />
            </div>
            <div className="text-xs text-gray-500 mt-2">Tickets per click</div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Insights</CardTitle>
          <CardDescription>Key metrics and trends analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Engagement Funnel</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                  <span className="text-sm">Events Visible</span>
                  <span className="font-medium">{formatNumber(analytics.visibility_views)}</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                  <span className="text-sm">Events Clicked</span>
                  <span className="font-medium">{formatNumber(analytics.click_views)} ({analytics.engagement_rate}%)</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                  <span className="text-sm">Tickets Clicked</span>
                  <span className="font-medium">{formatNumber(analytics.ticket_clicks)} ({analytics.ticket_conversion_rate}%)</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Key Ratios</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-sm">Avg Views per Event</span>
                  <span className="font-medium">
                    {analytics.tracked_events > 0 ? Math.round(analytics.visibility_views / analytics.tracked_events) : 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-sm">Avg Clicks per Event</span>
                  <span className="font-medium">
                    {analytics.tracked_events > 0 ? Math.round(analytics.click_views / analytics.tracked_events) : 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-sm">Return Visitor Rate</span>
                  <span className="font-medium">
                    {analytics.visibility_views > 0 ? Math.round((1 - analytics.unique_views / analytics.visibility_views) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Add Venue Modal Component
const AddVenueModal = ({ onVenueAdded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [venueData, setVenueData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'US',
    postal_code: '',
    latitude: null,
    longitude: null
  });
  const { toast } = useToast();
  const { token } = useAuth();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVenueData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/venues`, venueData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Venue added successfully",
      });
      onVenueAdded(response.data);
      setIsOpen(false);
      // Reset form
      setVenueData({
        name: '',
        address: '',
        city: '',
        state: '',
        country: 'US',
        postal_code: '',
        latitude: null,
        longitude: null
      });
    } catch (error) {
      console.error('Error adding venue:', error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to add venue";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="h-auto p-0 text-blue-600"
      >
        + Add New Venue
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Venue</DialogTitle>
            <DialogDescription>
              Enter the venue details including address information
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="venue_name">Venue Name *</Label>
              <Input
                id="venue_name"
                name="name"
                value={venueData.name}
                onChange={handleInputChange}
                placeholder="Enter venue name"
                required
              />
            </div>

            <div>
              <Label htmlFor="venue_address">Street Address *</Label>
              <Input
                id="venue_address"
                name="address"
                value={venueData.address}
                onChange={handleInputChange}
                placeholder="123 Main Street"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="venue_city">City *</Label>
                <Input
                  id="venue_city"
                  name="city"
                  value={venueData.city}
                  onChange={handleInputChange}
                  placeholder="San Francisco"
                  required
                />
              </div>

              <div>
                <Label htmlFor="venue_state">State/Province</Label>
                <Input
                  id="venue_state"
                  name="state"
                  value={venueData.state}
                  onChange={handleInputChange}
                  placeholder="CA"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="venue_country">Country *</Label>
                <Input
                  id="venue_country"
                  name="country"
                  value={venueData.country}
                  onChange={handleInputChange}
                  placeholder="US"
                  required
                />
              </div>

              <div>
                <Label htmlFor="venue_postal_code">Postal Code</Label>
                <Input
                  id="venue_postal_code"
                  name="postal_code"
                  value={venueData.postal_code}
                  onChange={handleInputChange}
                  placeholder="94102"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="venue_latitude">Latitude (optional)</Label>
                <Input
                  id="venue_latitude"
                  name="latitude"
                  type="number"
                  step="any"
                  value={venueData.latitude || ''}
                  onChange={(e) => setVenueData(prev => ({
                    ...prev, 
                    latitude: e.target.value ? parseFloat(e.target.value) : null
                  }))}
                  placeholder="37.7749"
                />
              </div>

              <div>
                <Label htmlFor="venue_longitude">Longitude (optional)</Label>
                <Input
                  id="venue_longitude"
                  name="longitude"
                  type="number"
                  step="any"
                  value={venueData.longitude || ''}
                  onChange={(e) => setVenueData(prev => ({
                    ...prev, 
                    longitude: e.target.value ? parseFloat(e.target.value) : null
                  }))}
                  placeholder="-122.4194"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Adding..." : "Add Venue"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Add Host/Organizer Modal Component
const AddHostModal = ({ onHostAdded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hostData, setHostData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    description: ''
  });
  const { toast } = useToast();
  const { token } = useAuth();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setHostData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/hosts`, hostData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Organizer added successfully",
      });
      onHostAdded(response.data);
      setIsOpen(false);
      // Reset form
      setHostData({
        name: '',
        email: '',
        phone: '',
        website: '',
        description: ''
      });
    } catch (error) {
      console.error('Error adding host:', error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to add organizer";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="h-auto p-0 text-blue-600"
      >
        + Add New Organizer
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Organizer</DialogTitle>
            <DialogDescription>
              Enter the organizer/host details
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="host_name">Organizer Name *</Label>
              <Input
                id="host_name"
                name="name"
                value={hostData.name}
                onChange={handleInputChange}
                placeholder="Enter organizer name"
                required
              />
            </div>

            <div>
              <Label htmlFor="host_email">Email *</Label>
              <Input
                id="host_email"
                name="email"
                type="email"
                value={hostData.email}
                onChange={handleInputChange}
                placeholder="contact@example.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="host_phone">Phone</Label>
              <Input
                id="host_phone"
                name="phone"
                value={hostData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <Label htmlFor="host_website">Website</Label>
              <Input
                id="host_website"
                name="website"
                type="url"
                value={hostData.website}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <Label htmlFor="host_description">Description</Label>
              <Textarea
                id="host_description"
                name="description"
                value={hostData.description}
                onChange={handleInputChange}
                placeholder="Brief description about the organizer"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Adding..." : "Add Organizer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Create Event Form
const CreateEvent = () => {
  const LOCALSTORAGE_KEY = 'whb_event_form_data';
  const LOCALSTORAGE_IMAGES_KEY = 'whb_event_images';

  const initialFormState = {
    title: "",
    description: "",
    organizer_id: undefined,
    venue_id: undefined,
    category_ids: [],
    tags: [],
    start_at: "",
    end_at: "",
    timezone: "America/New_York",
    event_type: "in-person",
    ticket_url: "",
    price_min: "",
    price_max: "",
    max_attendees: "",
    is_free: true,
    created_by: "admin",
    recurrence_type: "none",
    is_all_day: false,
    is_multi_day: false,
    recurrence_data: {
      interval: 1,
      end_type: 'never',
      count: 10,
      until: '',
      days_of_week: [],
      monthly_type: 'same_date',
      month_day: 1,
      week_occurrence: 1,
      week_occurrences: [1],
      weekday: 1
    }
  };

  const [formData, setFormData] = useState(initialFormState);
  const recurrenceData = normalizeRecurrenceData(formData.recurrence_data);
  
  const [hosts, setHosts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customFieldTemplates, setCustomFieldTemplates] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);
  const [featuredImageIndex, setFeaturedImageIndex] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { token } = useAuth();

  // On mount: either pre-fill from a duplicate action (router state) or restore a
  // saved draft from localStorage.  These two cases are mutually exclusive.
  useEffect(() => {
    const prefillData = location.state?.prefillData;

    if (prefillData) {
      // --- Duplicate flow ---
      // Clear any stale draft so the autosave effect doesn't overwrite this data.
      localStorage.removeItem(LOCALSTORAGE_KEY);
      localStorage.removeItem(LOCALSTORAGE_IMAGES_KEY);

      setFormData({
        ...initialFormState,
        title:         prefillData.title        ?? "",
        description:   prefillData.description  ?? "",
        organizer_id:  prefillData.organizer_id ?? undefined,
        venue_id:      prefillData.venue_id     ?? undefined,
        category_ids:  prefillData.category_ids ?? [],
        tags:          prefillData.tags         ?? [],
        timezone:      prefillData.timezone     ?? "UTC",
        event_type:    prefillData.event_type   ?? "in-person",
        ticket_url:    prefillData.ticket_url   ?? "",
        price_min:     prefillData.price_min    != null ? String(prefillData.price_min) : "",
        price_max:     prefillData.price_max    != null ? String(prefillData.price_max) : "",
        max_attendees: prefillData.max_attendees != null ? String(prefillData.max_attendees) : "",
        is_free:       prefillData.is_free      ?? true,
        // start_at, end_at, recurrence left at initialFormState defaults — user must set new dates
      });

      toast({
        title: "Event Loaded",
        description: `Duplicating "${prefillData.title}". Fill in the dates to continue.`,
      });
    } else {
      // --- Regular create flow — restore saved draft ---
      const savedFormData = localStorage.getItem(LOCALSTORAGE_KEY);
      if (savedFormData) {
        try {
          const parsedData = JSON.parse(savedFormData);
          setFormData(parsedData);
          console.log('✅ Form data restored from localStorage');
        } catch (error) {
          console.error('Error parsing saved form data:', error);
          localStorage.removeItem(LOCALSTORAGE_KEY);
        }
      }
    }
  }, []); // intentional: only run on mount

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // Helper function for ordinal suffixes
  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  useEffect(() => {
    fetchDropdownData();
    loadTemplate();
  }, []);

  const loadTemplate = () => {
    const template = localStorage.getItem('selected_template');
    if (template) {
      const templateData = JSON.parse(template);
      setFormData(prev => ({
        ...prev,
        event_type: templateData.event_type,
        tags: templateData.tags || [],
        is_free: templateData.is_free,
        timezone: templateData.timezone
      }));
      localStorage.removeItem('selected_template');
    }
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template || !template.default_event_data) return;

    const data = template.default_event_data;
    
    setFormData(prev => ({
      ...prev,
      title: data.default_title || prev.title,
      description: data.default_description || prev.description,
      organizer_id: data.default_host_id || prev.organizer_id,
      venue_id: data.default_venue_id || prev.venue_id,
      category_ids: data.default_category_ids || prev.category_ids,
      tags: data.tags || prev.tags,
      event_type: data.event_type || prev.event_type,
      timezone: data.timezone || prev.timezone,
      is_free: data.is_free !== undefined ? data.is_free : prev.is_free,
      price_min: data.default_price_min || prev.price_min,
      price_max: data.default_price_max || prev.price_max,
      max_attendees: data.default_max_attendees || prev.max_attendees,
    }));

    toast({
      title: "Template Applied",
      description: `"${template.name}" template has been applied to this event`,
    });
  };

  const fetchDropdownData = async () => {
    try {
      const [organizersRes, venuesRes, categoriesRes, templatesRes, customFieldsRes] = await Promise.all([
        axios.get(`${API}/hosts`),
        axios.get(`${API}/venues`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/event-templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/custom-field-templates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Remove duplicates based on name (since there are many duplicate entries with different IDs)
      const uniqueOrganizers = organizersRes.data.filter((org, index, self) => 
        index === self.findIndex(o => o.name === org.name && o.email === org.email)
      );
      const uniqueVenues = venuesRes.data.filter((venue, index, self) => 
        index === self.findIndex(v => v.name === venue.name && v.city === venue.city)
      );
      const uniqueCategories = categoriesRes.data.filter((cat, index, self) => 
        index === self.findIndex(c => c.name === cat.name)
      );
      
      setHosts(uniqueOrganizers);
      setVenues(uniqueVenues);
      setCategories(uniqueCategories);
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setCustomFieldTemplates(Array.isArray(customFieldsRes.data) ? customFieldsRes.data.filter(f => f.active) : []);
      
      console.log('Loaded data:', {
        hosts: uniqueOrganizers.length,
        venues: uniqueVenues.length,
        categories: uniqueCategories.length,
        customFields: customFieldsRes.data?.length || 0
      });
      
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch form data",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Image handling functions
  const handleImageSelection = (files) => {
    const validFiles = [];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    Array.from(files).forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: `${file.name} is not a valid image type. Please use JPEG, PNG, or WebP.`,
          variant: "destructive",
        });
        return;
      }
      
      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: `${file.name} is too large. Maximum size is 10MB.`,
          variant: "destructive",
        });
        return;
      }
      
      validFiles.push(file);
    });
    
    if (validFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...validFiles]);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      
      // Adjust featured image index if needed
      if (featuredImageIndex === index) {
        setFeaturedImageIndex(null);
      } else if (featuredImageIndex > index) {
        setFeaturedImageIndex(featuredImageIndex - 1);
      }
      
      return newImages;
    });
  };

  const setFeaturedImage = (index) => {
    setFeaturedImageIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleImageSelection(e.dataTransfer.files);
  };

  const uploadImages = async (eventId) => {
    console.log("uploadImages called with eventId:", eventId);
    console.log("selectedImages:", selectedImages);
    console.log("featuredImageIndex:", featuredImageIndex);
    
    if (selectedImages.length === 0) {
      console.log("No images to upload");
      return;
    }
    
    setImageUploading(true);
    
    try {
      const formData = new FormData();
      selectedImages.forEach((image, index) => {
        console.log(`Appending image ${index}:`, image.name, image.type, image.size);
        formData.append('files', image);
      });
      
      if (featuredImageIndex !== null) {
        formData.append('featured_image_index', featuredImageIndex.toString());
      }
      
      console.log("Sending upload request to:", `${API}/events/${eventId}/images`);
      const response = await axios.post(`${API}/events/${eventId}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log("Upload response:", response.data);
      
      if (response.data.successful_uploads > 0) {
        toast({
          title: "Images Uploaded",
          description: `Successfully uploaded ${response.data.successful_uploads} image(s)`,
        });
        
        // The event has already been persisted by the upload endpoint. Avoid calling
        // dashboard-only refresh helpers here; this create page navigates away after save.
      }
      
      if (response.data.failed_uploads > 0) {
        toast({
          title: "Some Images Failed",
          description: `${response.data.failed_uploads} image(s) failed to upload`,
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error("Image upload error:", error);
      console.error("Error response:", error.response?.data);
      toast({
        title: "Upload Error",
        description: error.response?.data?.detail || "Failed to upload images",
        variant: "destructive",
      });
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate dates (only if end_at is provided)
      const startDate = new Date(formData.start_at);
      let endDate = null;
      
      if (formData.end_at) {
        endDate = new Date(formData.end_at);
        
        if (endDate <= startDate) {
          toast({
            title: "Invalid Dates",
            description: "End time must be after start time",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }
      
      const parsedPriceMin = formData.price_min ? parseFloat(formData.price_min) : null;
      const parsedPriceMax = formData.price_max ? parseFloat(formData.price_max) : null;
      const pricingStatus = formData.is_free ? 'free' : ((parsedPriceMin != null || parsedPriceMax != null) ? 'paid' : 'unspecified');

      const eventData = {
        ...formData,
        start_at: startDate.toISOString(),
        end_at: endDate ? endDate.toISOString() : null,
        price_min: parsedPriceMin,
        price_max: parsedPriceMax,
        pricing_status: pricingStatus,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        custom_fields: customFieldValues
      };

      const response = await axios.post(`${API}/events`, eventData);
      const eventId = response.data.id;
      
      // Upload images if any are selected
      if (selectedImages.length > 0) {
        await uploadImages(eventId);
      }
      
      // Generate recurring event instances if this is a recurring event
      if (formData.recurrence_type !== 'none') {
        try {
          const recurringResponse = await axios.post(`${API}/events/${eventId}/generate-recurring`);
          toast({
            title: "Success",
            description: `Event created successfully with ${recurringResponse.data.created_instances?.length || 0} recurring instances`,
          });
        } catch (recurringError) {
          console.error('Error generating recurring instances:', recurringError);
          toast({
            title: "Partial Success",
            description: "Event created but recurring instances could not be generated",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Success",
          description: "Event created successfully",
        });
      }
      
      // Clear localStorage after successful submission
      localStorage.removeItem(LOCALSTORAGE_KEY);
      localStorage.removeItem(LOCALSTORAGE_IMAGES_KEY);
      console.log('✅ Form data cleared from localStorage after submission');
      
      navigate("/");
    } catch (error) {
      console.error('Error creating event:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = "Failed to create event";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors
          errorMessage = error.response.data.detail.map(err => 
            `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
          ).join('; ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/" className="text-brand-primary hover:text-brand-primary">
              <Calendar className="w-8 h-8" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Create New Event</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <Card className="bg-white/70 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Event Details</CardTitle>
                <CardDescription>Fill in the information below to create your event</CardDescription>
              </div>
              <div className="w-64">
                <Label className="text-sm mb-1">Use Template</Label>
                <SearchableSelect
                  options={templates}
                  value=""
                  onChange={(templateId) => applyTemplate(templateId)}
                  placeholder="Select a template..."
                  displayKey="name"
                  valueKey="id"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter event title"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your event"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="organizer_id">Organizer *</Label>
                    <AddHostModal onHostAdded={(newHost) => {
                      setHosts(prev => [...prev, newHost]);
                      setFormData(prev => ({...prev, organizer_id: newHost.id}));
                    }} />
                  </div>
                  <SearchableSelect
                    options={hosts}
                    value={formData.organizer_id}
                    onChange={(value) => {
                      console.log('Organizer selected:', value);
                      setFormData(prev => ({...prev, organizer_id: value}));
                    }}
                    placeholder="Search organizers..."
                    displayKey="name"
                    valueKey="id"
                  />
                  {formData.organizer_id && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {hosts.find(o => o.id === formData.organizer_id)?.name || 'Unknown'}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="venue_id">Venue</Label>
                    <AddVenueModal onVenueAdded={(newVenue) => {
                      setVenues(prev => [...prev, newVenue]);
                      setFormData(prev => ({...prev, venue_id: newVenue.id}));
                    }} />
                  </div>
                  <SearchableSelect
                    options={venues}
                    value={formData.venue_id}
                    onChange={(value) => {
                      console.log('Venue selected:', value);
                      setFormData(prev => ({...prev, venue_id: value}));
                    }}
                    placeholder="Search venues..."
                    displayKey="name"
                    valueKey="id"
                    getDisplayText={(venue) => `${venue.name} - ${venue.city}`}
                  />
                  {formData.venue_id && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {venues.find(v => v.id === formData.venue_id)?.name || 'Unknown'}
                    </p>
                  )}
                </div>

                {/* Event Duration Options */}
                <div className="md:col-span-2 flex flex-wrap gap-6 p-3 bg-gray-50 rounded-lg border">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_all_day}
                      onChange={(e) => {
                        const isAllDay = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          is_all_day: isAllDay,
                          // When toggling all-day, adjust times
                          start_at: isAllDay && prev.start_at ? prev.start_at.split('T')[0] + 'T00:00' : prev.start_at,
                          end_at: isAllDay && prev.end_at ? prev.end_at.split('T')[0] + 'T23:59' : prev.end_at
                        }));
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">All-Day Event</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_multi_day}
                      onChange={(e) => setFormData(prev => ({...prev, is_multi_day: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Multi-Day Event</span>
                  </label>
                </div>

                <div className={formData.is_multi_day ? "" : "md:col-span-2"}>
                  <Label htmlFor="start_at">{formData.is_all_day ? 'Start Date *' : 'Start Date & Time *'}</Label>
                  <Input
                    id="start_at"
                    name="start_at"
                    type={formData.is_all_day ? "date" : "datetime-local"}
                    value={formData.is_all_day && formData.start_at ? formData.start_at.split('T')[0] : formData.start_at}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        start_at: prev.is_all_day ? val + 'T00:00' : val
                      }));
                    }}
                    required
                  />
                  {formData.start_at && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Event starts:</p>
                        <p className="text-sm text-green-700">
                          {new Date(formData.start_at).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className={formData.is_multi_day ? "" : "md:col-span-2"}>
                  <Label htmlFor="end_at">{formData.is_all_day ? 'End Date' : 'End Date & Time'}{formData.is_multi_day ? ' *' : ''}</Label>
                  <Input
                    id="end_at"
                    name="end_at"
                    type={formData.is_all_day ? "date" : "datetime-local"}
                    value={formData.is_all_day && formData.end_at ? formData.end_at.split('T')[0] : formData.end_at}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        end_at: prev.is_all_day ? val + 'T23:59' : val
                      }));
                    }}
                    {...(formData.is_multi_day ? { required: true } : {})}
                  />
                  {!formData.is_multi_day && <p className="text-xs text-gray-500 mt-1">Leave empty if no specific end time</p>}
                  {formData.is_multi_day && <p className="text-xs text-purple-600 mt-1">End date required for multi-day events</p>}
                  {formData.end_at && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Event ends:</p>
                        <p className="text-sm text-blue-700">
                          {new Date(formData.end_at).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                  {formData.start_at && formData.end_at && (
                    <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-md">
                      <p className="text-sm font-medium text-purple-800">Duration:</p>
                      <p className="text-sm text-purple-700">
                        {(() => {
                          const start = new Date(formData.start_at);
                          const end = new Date(formData.end_at);
                          const diffMs = end - start;
                          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                          const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                          const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                          
                          if (diffDays > 0) {
                            return `${diffDays} day${diffDays !== 1 ? 's' : ''}${diffHours > 0 ? ` ${diffHours}h` : ''}${diffMinutes > 0 ? ` ${diffMinutes}m` : ''}`;
                          } else if (diffHours > 0) {
                            return `${diffHours}h ${diffMinutes}m`;
                          } else {
                            return `${diffMinutes}m`;
                          }
                        })()}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="event_type">Event Type</Label>
                  <Select name="event_type" value={formData.event_type} onValueChange={(value) => setFormData(prev => ({...prev, event_type: value}))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="virtual">Virtual</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="ticket_url">Ticket URL</Label>
                  <Input
                    id="ticket_url"
                    name="ticket_url"
                    value={formData.ticket_url}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      id="is_free"
                      name="is_free"
                      checked={formData.is_free}
                      onChange={handleInputChange}
                      className="rounded"
                    />
                    <Label htmlFor="is_free">This is a free event</Label>
                  </div>

                  {!formData.is_free && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="pricing_type">Pricing Type</Label>
                        <select
                          id="pricing_type"
                          name="pricing_type"
                          value={formData.pricing_type || 'single'}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="single">Single Price</option>
                          <option value="range">Price Range</option>
                          <option value="two_tier">General & VIP Admission</option>
                        </select>
                      </div>
                      
                      {formData.pricing_type === 'two_tier' ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="price_min">General Admission ($)</Label>
                            <Input
                              id="price_min"
                              name="price_min"
                              type="number"
                              step="0.01"
                              value={formData.price_min}
                              onChange={handleInputChange}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label htmlFor="vip_price">VIP Admission ($)</Label>
                            <Input
                              id="vip_price"
                              name="vip_price"
                              type="number"
                              step="0.01"
                              value={formData.vip_price}
                              onChange={handleInputChange}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      ) : formData.pricing_type === 'range' ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="price_min">Min Price ($)</Label>
                            <Input
                              id="price_min"
                              name="price_min"
                              type="number"
                              step="0.01"
                              value={formData.price_min}
                              onChange={handleInputChange}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label htmlFor="price_max">Max Price ($)</Label>
                            <Input
                              id="price_max"
                              name="price_max"
                              type="number"
                              step="0.01"
                              value={formData.price_max}
                              onChange={handleInputChange}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="price_min">Price ($)</Label>
                          <Input
                            id="price_min"
                            name="price_min"
                            type="number"
                            step="0.01"
                            value={formData.price_min}
                            onChange={handleInputChange}
                            placeholder="0.00"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="max_attendees">Max Attendees</Label>
                  <Input
                    id="max_attendees"
                    name="max_attendees"
                    type="number"
                    value={formData.max_attendees}
                    onChange={handleInputChange}
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                {/* Categories Selector */}
                <div className="md:col-span-2">
                  <Label>Categories</Label>
                  <HierarchicalCategorySelector
                    selectedCategories={formData.category_ids}
                    onChange={(categoryIds) => setFormData(prev => ({ ...prev, category_ids: categoryIds }))}
                    multiple={true}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add a tag"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    />
                    <Button type="button" onClick={handleAddTag} variant="outline">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                        {tag} ×
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Recurring Event Settings */}
                <div className="md:col-span-2">
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center space-x-2">
                      <RefreshCw className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">Recurring Event</h3>
                    </div>
                    
                    <div>
                      <Label htmlFor="recurrence_type">Recurrence Pattern</Label>
                      <Select 
                        name="recurrence_type" 
                        value={formData.recurrence_type} 
                        onValueChange={(value) => {
                          setFormData(prev => ({
                            ...prev, 
                            recurrence_type: value,
                            recurrence_data: value === 'none' ? {} : normalizeRecurrenceData(prev.recurrence_data)
                          }))
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select recurrence pattern" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No recurrence</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.recurrence_type !== 'none' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="interval">Repeat every</Label>
                            <div className="flex items-center space-x-2">
                              <Input
                                id="interval"
                                type="number"
                                min="1"
                                max="30"
                                value={recurrenceData.interval}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  recurrence_data: {
                                    ...prev.recurrence_data,
                                    interval: parseInt(e.target.value) || 1
                                  }
                                }))}
                                className="w-20"
                              />
                              <span className="text-sm text-gray-600">
                                {formData.recurrence_type === 'daily' && 'day(s)'}
                                {formData.recurrence_type === 'weekly' && 'week(s)'}
                                {formData.recurrence_type === 'monthly' && 'month(s)'}
                                {formData.recurrence_type === 'yearly' && 'year(s)'}
                              </span>
                            </div>
                          </div>

                          <div>
                            <Label>End recurrence</Label>
                            <Select 
                              value={recurrenceData.end_type} 
                              onValueChange={(value) => setFormData(prev => ({
                                ...prev,
                                recurrence_data: {
                                  ...prev.recurrence_data,
                                  end_type: value
                                }
                              }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="never">Never</SelectItem>
                                <SelectItem value="count">After number of occurrences</SelectItem>
                                <SelectItem value="date">On specific date</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Days of Week Selection for Weekly Recurrence */}
                        {formData.recurrence_type === 'weekly' && (
                          <div>
                            <Label>Repeat on days</Label>
                            <div className="grid grid-cols-7 gap-1 mt-2">
                              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                                const isSelected = recurrenceData.days_of_week.includes(index);
                                return (
                                  <div key={day} className="text-center">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentDays = recurrenceData.days_of_week;
                                        const newDays = isSelected 
                                          ? currentDays.filter(d => d !== index)
                                          : [...currentDays, index].sort();
                                        
                                        setFormData(prev => ({
                                          ...prev,
                                          recurrence_data: {
                                            ...prev.recurrence_data,
                                            days_of_week: newDays
                                          }
                                        }));
                                      }}
                                      className={`w-10 h-10 rounded-full text-xs font-medium transition-colors ${
                                        isSelected 
                                          ? 'bg-blue-600 text-white' 
                                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                      }`}
                                    >
                                      {day.substring(0, 2)}
                                    </button>
                                    <div className="text-xs text-gray-500 mt-1">{day.substring(0, 3)}</div>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              {recurrenceData.days_of_week.length === 0
                                ? "Select days of the week for the event"
                                : `Selected: ${recurrenceData.days_of_week.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}`
                              }
                            </p>
                          </div>
                        )}

                        {/* Monthly Recurrence Options */}
                        {formData.recurrence_type === 'monthly' && (
                          <div className="space-y-4">
                            <div>
                              <Label>Monthly pattern</Label>
                              <Select 
                                value={recurrenceData.monthly_type} 
                                onValueChange={(value) => setFormData(prev => ({
                                  ...prev,
                                  recurrence_data: {
                                    ...prev.recurrence_data,
                                    monthly_type: value,
                                    // Reset specific settings when pattern changes
                                    month_day: value === 'same_date' ? (prev.recurrence_data?.month_day || 1) : undefined,
                                    week_occurrence: value === 'same_weekday' ? (prev.recurrence_data?.week_occurrence || 1) : undefined,
                                    week_occurrences: value === 'same_weekday'
                                      ? (prev.recurrence_data?.week_occurrences || [prev.recurrence_data?.week_occurrence || 1])
                                      : undefined,
                                    weekday: (value === 'same_weekday' || value === 'last_weekday') ? (prev.recurrence_data?.weekday || 1) : undefined
                                  }
                                }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="same_date">Same date each month</SelectItem>
                                  <SelectItem value="same_weekday">Same weekday each month</SelectItem>
                                  <SelectItem value="last_weekday">Last occurrence of weekday</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Same Date Configuration */}
                            {recurrenceData.monthly_type === 'same_date' && (
                              <div>
                                <Label htmlFor="month_day">Day of the month</Label>
                                <div className="flex items-center space-x-2">
                                  <Select 
                                    value={recurrenceData.month_day.toString()} 
                                    onValueChange={(value) => setFormData(prev => ({
                                      ...prev,
                                      recurrence_data: {
                                        ...prev.recurrence_data,
                                        month_day: parseInt(value)
                                      }
                                    }))}
                                  >
                                    <SelectTrigger className="w-24">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                                        <SelectItem key={day} value={day.toString()}>
                                          {day}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <span className="text-sm text-gray-600">
                                    (e.g., {recurrenceData.month_day}{getOrdinalSuffix(recurrenceData.month_day)} of every month)
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Same Weekday Configuration */}
                            {recurrenceData.monthly_type === 'same_weekday' && (
                              <div className="space-y-4">
                                <div>
                                  <Label>Week occurrences (choose one or more)</Label>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {[1, 2, 3, 4].map((occ) => {
                                      const current = recurrenceData.week_occurrences;
                                      const selected = current.includes(occ);
                                      return (
                                        <button
                                          key={occ}
                                          type="button"
                                          onClick={() => {
                                            const updated = selected
                                              ? current.filter((x) => x !== occ)
                                              : [...current, occ].sort((a, b) => a - b);
                                            const safe = updated.length ? updated : [1];
                                            setFormData(prev => ({
                                              ...prev,
                                              recurrence_data: {
                                                ...prev.recurrence_data,
                                                week_occurrences: safe,
                                                week_occurrence: safe[0]
                                              }
                                            }));
                                          }}
                                          className={`px-3 py-1 rounded-full text-sm font-medium border ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                        >
                                          {occ}{getOrdinalSuffix(occ)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Example: select 2nd and 4th for twice-monthly events.
                                  </p>
                                </div>

                                <div>
                                  <Label>Day of the week</Label>
                                  <Select
                                    value={recurrenceData.weekday.toString()}
                                    onValueChange={(value) => setFormData(prev => ({
                                      ...prev,
                                      recurrence_data: {
                                        ...prev.recurrence_data,
                                        weekday: parseInt(value)
                                      }
                                    }))}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="0">Sunday</SelectItem>
                                      <SelectItem value="1">Monday</SelectItem>
                                      <SelectItem value="2">Tuesday</SelectItem>
                                      <SelectItem value="3">Wednesday</SelectItem>
                                      <SelectItem value="4">Thursday</SelectItem>
                                      <SelectItem value="5">Friday</SelectItem>
                                      <SelectItem value="6">Saturday</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            )}

                            {/* Last Weekday Configuration */}
                            {recurrenceData.monthly_type === 'last_weekday' && (
                              <div>
                                <Label>Last occurrence of</Label>
                                <Select 
                                  value={recurrenceData.weekday.toString()} 
                                  onValueChange={(value) => setFormData(prev => ({
                                    ...prev,
                                    recurrence_data: {
                                      ...prev.recurrence_data,
                                      weekday: parseInt(value)
                                    }
                                  }))}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="0">Sunday</SelectItem>
                                    <SelectItem value="1">Monday</SelectItem>
                                    <SelectItem value="2">Tuesday</SelectItem>
                                    <SelectItem value="3">Wednesday</SelectItem>
                                    <SelectItem value="4">Thursday</SelectItem>
                                    <SelectItem value="5">Friday</SelectItem>
                                    <SelectItem value="6">Saturday</SelectItem>
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-500 mt-1">
                                  Event will occur on the last {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][recurrenceData.weekday]} of each month
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {formData.recurrence_data?.end_type === 'count' && (
                          <div>
                            <Label htmlFor="count">Number of occurrences</Label>
                            <Input
                              id="count"
                              type="number"
                              min="1"
                              max="365"
                              value={recurrenceData.count}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                recurrence_data: {
                                  ...prev.recurrence_data,
                                  count: parseInt(e.target.value) || 10
                                }
                              }))}
                              className="w-32"
                            />
                          </div>
                        )}

                        {formData.recurrence_data?.end_type === 'date' && (
                          <div>
                            <Label htmlFor="until">End date</Label>
                            <Input
                              id="until"
                              type="date"
                              value={recurrenceData.until}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                recurrence_data: {
                                  ...prev.recurrence_data,
                                  until: e.target.value
                                }
                              }))}
                            />
                          </div>
                        )}

                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <Repeat className="w-4 h-4 text-blue-600 mt-0.5" />
                            <div className="text-sm text-blue-800">
                              <p className="font-medium">Recurring Event Preview:</p>
                              <p>
                                {(() => {
                                  const interval = recurrenceData.interval;
                                  const type = formData.recurrence_type;
                                  let pattern = "";

                                  if (type === 'daily') {
                                    pattern = `every ${interval === 1 ? '' : interval + ' '}day${interval > 1 ? 's' : ''}`;
                                  } else if (type === 'weekly') {
                                    const daysOfWeek = recurrenceData.days_of_week;
                                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                    if (daysOfWeek.length > 0) {
                                      const selectedDays = daysOfWeek.map(d => dayNames[d]).join(', ');
                                      pattern = `every ${interval === 1 ? '' : interval + ' '}week${interval > 1 ? 's' : ''} on ${selectedDays}`;
                                    } else {
                                      pattern = `every ${interval === 1 ? '' : interval + ' '}week${interval > 1 ? 's' : ''}`;
                                    }
                                  } else if (type === 'monthly') {
                                    const monthlyType = recurrenceData.monthly_type;
                                    if (monthlyType === 'same_date') {
                                      const monthDay = recurrenceData.month_day;
                                      pattern = `every ${interval === 1 ? '' : interval + ' '}month${interval > 1 ? 's' : ''} on the ${monthDay}${getOrdinalSuffix(monthDay)}`;
                                    } else if (monthlyType === 'same_weekday') {
                                      const weekOccurrences = recurrenceData.week_occurrences;
                                      const weekday = recurrenceData.weekday;
                                      const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      const occurrenceNames = ['1st', '2nd', '3rd', '4th'];
                                      const occLabel = weekOccurrences
                                        .map((n) => occurrenceNames[n - 1])
                                        .filter(Boolean)
                                        .join(' and ');
                                      pattern = `every ${interval === 1 ? '' : interval + ' '}month${interval > 1 ? 's' : ''} on the ${occLabel} ${weekdayNames[weekday]}`;
                                    } else {
                                      const weekday = recurrenceData.weekday;
                                      const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      pattern = `every ${interval === 1 ? '' : interval + ' '}month${interval > 1 ? 's' : ''} on the last ${weekdayNames[weekday]}`;
                                    }
                                  } else if (type === 'yearly') {
                                    pattern = `every ${interval === 1 ? '' : interval + ' '}year${interval > 1 ? 's' : ''}`;
                                  }

                                  let endCondition = "";
                                  if (formData.recurrence_data?.end_type === 'count') {
                                    endCondition = ` for ${formData.recurrence_data?.count || 10} occurrences`;
                                  } else if (formData.recurrence_data?.end_type === 'date' && formData.recurrence_data?.until) {
                                    endCondition = ` until ${new Date(formData.recurrence_data.until).toLocaleDateString()}`;
                                  } else {
                                    endCondition = ' indefinitely';
                                  }

                                  return `This event will repeat ${pattern}${endCondition}`;
                                })()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="md:col-span-2">
                  <Label>Event Images</Label>
                  <div className="space-y-4">
                    {/* Drag & Drop Area */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        dragActive 
                          ? 'border-brand-primary bg-purple-50' 
                          : 'border-gray-300 hover:border-brand-primary hover:bg-gray-50'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="space-y-2">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div>
                          <p className="text-gray-600">
                            Drag and drop images here, or{' '}
                            <label className="text-brand-primary cursor-pointer hover:underline">
                              browse
                              <input
                                type="file"
                                multiple
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={(e) => handleImageSelection(e.target.files)}
                                className="hidden"
                              />
                            </label>
                          </p>
                          <p className="text-sm text-gray-500">JPEG, PNG, WebP up to 10MB each</p>
                        </div>
                      </div>
                    </div>

                    {/* Selected Images Preview */}
                    {selectedImages.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Selected Images ({selectedImages.length})</h4>
                          <p className="text-sm text-gray-500">Click an image to set as featured</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {selectedImages.map((image, index) => (
                            <div
                              key={index}
                              className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                featuredImageIndex === index 
                                  ? 'border-brand-primary shadow-lg' 
                                  : 'border-gray-200 hover:border-brand-primary'
                              }`}
                              onClick={() => setFeaturedImage(index)}
                            >
                              <img
                                src={URL.createObjectURL(image)}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-24 object-cover"
                              />
                              {featuredImageIndex === index && (
                                <div className="absolute top-2 left-2">
                                  <Badge className="bg-brand-primary text-white text-xs">Featured</Badge>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(index);
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                                <p className="text-xs truncate">{image.name}</p>
                                <p className="text-xs text-gray-300">{(image.size / 1024 / 1024).toFixed(2)} MB</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {featuredImageIndex !== null && (
                          <p className="text-sm text-gray-600 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Featured: {selectedImages[featuredImageIndex]?.name}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Custom Fields Section */}
              {customFieldTemplates.length > 0 && (
                <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {customFieldTemplates.map((field) => (
                      <div key={field.id} className={field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                        <Label htmlFor={field.field_key}>
                          {field.field_name}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        
                        {field.field_type === 'text' && (
                          <Input
                            id={field.field_key}
                            type="text"
                            placeholder={field.placeholder || ''}
                            value={customFieldValues[field.field_key] || ''}
                            onChange={(e) => setCustomFieldValues(prev => ({
                              ...prev,
                              [field.field_key]: e.target.value
                            }))}
                            required={field.required}
                          />
                        )}
                        
                        {field.field_type === 'textarea' && (
                          <textarea
                            id={field.field_key}
                            placeholder={field.placeholder || ''}
                            value={customFieldValues[field.field_key] || ''}
                            onChange={(e) => setCustomFieldValues(prev => ({
                              ...prev,
                              [field.field_key]: e.target.value
                            }))}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-[100px]"
                          />
                        )}
                        
                        {field.field_type === 'number' && (
                          <Input
                            id={field.field_key}
                            type="number"
                            placeholder={field.placeholder || ''}
                            value={customFieldValues[field.field_key] || ''}
                            onChange={(e) => setCustomFieldValues(prev => ({
                              ...prev,
                              [field.field_key]: e.target.value
                            }))}
                            required={field.required}
                          />
                        )}
                        
                        {field.field_type === 'select' && (
                          <Select
                            value={customFieldValues[field.field_key] || ''}
                            onValueChange={(value) => setCustomFieldValues(prev => ({
                              ...prev,
                              [field.field_key]: value
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={field.placeholder || 'Select an option'} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        
                        {field.field_type === 'checkbox' && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={field.field_key}
                              checked={customFieldValues[field.field_key] === 'true' || customFieldValues[field.field_key] === true}
                              onChange={(e) => setCustomFieldValues(prev => ({
                                ...prev,
                                [field.field_key]: e.target.checked.toString()
                              }))}
                              className="w-4 h-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                            />
                            <label htmlFor={field.field_key} className="text-sm text-gray-700">
                              {field.placeholder || field.help_text}
                            </label>
                          </div>
                        )}
                        
                        {field.help_text && field.field_type !== 'checkbox' && (
                          <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bulk Import Section */}
              <BulkImportSection 
                organizers={hosts}
                selectedOrganizer={formData.organizer_id}
                onBulkImportComplete={() => {
                  toast({
                    title: "Success",
                    description: "Events imported successfully! Redirecting to dashboard...",
                  });
                  setTimeout(() => navigate("/"), 2000);
                }}
              />

              <div className="flex justify-end space-x-4 pt-6">
                <Button type="button" variant="outline" onClick={() => navigate("/")}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="bg-brand-primary hover:bg-brand-primary">
                  {loading ? "Creating..." : "Create Event"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Public Event Submission Component
const PublicEventSubmission = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_at: "",
    end_at: "",
    timezone: "America/New_York",
    venue_name: "",
    venue_address: "",
    city: "",
    state: "",
    country: "",
    event_type: "in-person",
    is_free: true,
    price_info: "",
    max_attendees: "",
    submitter_name: "",
    submitter_email: "",
    submitter_phone: "",
    organization_name: "",
    website_url: "",
    registration_url: "",
    tags: [],
    category_ids: [],
    additional_info: "",
    target_organizer_id: ""
  });

  const [tagInput, setTagInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hosts, setHosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizers();
    fetchCategories();
  }, []);

  useEffect(() => {
    // Handle URL parameters for calendar context
    const urlParams = new URLSearchParams(window.location.search);
    const calendarId = urlParams.get('calendar');
    const promoterId = urlParams.get('promoter');
    const hostId = urlParams.get('host');
    const organizationId = urlParams.get('organization');
    
    if (hostId && hosts && hosts.length > 0 && !formData.target_organizer_id) {
      // Pre-select the specific host passed in the URL
      setFormData(prev => ({
        ...prev,
        target_organizer_id: hostId
      }));
    } else if (organizationId && hosts && hosts.length > 0 && !formData.target_organizer_id) {
      // Calendar submit buttons pass organization context. Pick a host from that org,
      // not the first global host, so submissions route back to the correct tenant.
      const organizationHost = hosts.find(host => String(host.organization_id) === String(organizationId));
      if (organizationHost) {
        setFormData(prev => ({
          ...prev,
          target_organizer_id: organizationHost.id
        }));
      }
    } else if ((calendarId || promoterId) && hosts && hosts.length > 0 && !formData.target_organizer_id) {
      // Legacy fallback: pre-select the first host only when no org/host context exists.
      setFormData(prev => ({
        ...prev,
        target_organizer_id: hosts[0].id
      }));
    }
  }, [hosts]);

  const fetchOrganizers = async () => {
    try {
      const response = await axios.get(`${API}/hosts`);
      // Remove duplicates based on name and email
      const uniqueOrganizers = response.data.filter((org, index, self) => 
        index === self.findIndex(o => o.name === org.name && o.email === org.email)
      );
      setHosts(uniqueOrganizers);
    } catch (error) {
      console.error('Error fetching hosts:', error);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  };


  const fetchCategories = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Basic validation
      if (!formData.title || !formData.submitter_name || !formData.submitter_email) {
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      // Prepare submission data
      const submissionData = {
        ...formData,
        start_at: formData.start_at ? new Date(formData.start_at).toISOString() : null,
        end_at: formData.end_at ? new Date(formData.end_at).toISOString() : null,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
      };

      const response = await axios.post(`${API}/event-submissions`, submissionData);

      toast({
        title: "Success",
        description: "Your event has been submitted for review! We'll get back to you soon.",
      });

      // Redirect to main page after successful submission
      setTimeout(() => {
        navigate("/");
      }, 2000);

    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit event. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-8">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-white/70 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Plus className="w-6 h-6 mr-2 text-blue-600" />
              Submit an Event
            </CardTitle>
            <CardDescription>
              Submit your event for consideration to be featured on our calendar. All submissions are reviewed before being published.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Event Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    placeholder="Enter event title"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="description">Event Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                    placeholder="Describe your event"
                    rows={4}
                  />
                </div>

                <div>
                  <Label htmlFor="start_at">Start Date & Time</Label>
                  <Input
                    id="start_at"
                    type="datetime-local"
                    value={formData.start_at}
                    onChange={(e) => setFormData(prev => ({...prev, start_at: e.target.value}))}
                  />
                </div>

                <div>
                  <Label htmlFor="end_at">End Date & Time</Label>
                  <Input
                    id="end_at"
                    type="datetime-local"
                    value={formData.end_at}
                    onChange={(e) => setFormData(prev => ({...prev, end_at: e.target.value}))}
                  />
                </div>

                <div>
                  <Label>Event Type</Label>
                  <Select value={formData.event_type} onValueChange={(value) => setFormData(prev => ({...prev, event_type: value}))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="max_attendees">Maximum Attendees</Label>
                  <Input
                    id="max_attendees"
                    type="number"
                    value={formData.max_attendees}
                    onChange={(e) => setFormData(prev => ({...prev, max_attendees: e.target.value}))}
                    placeholder="Optional"
                  />
                </div>
              </div>

              {/* Location Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Location Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="venue_name">Venue Name</Label>
                    <Input
                      id="venue_name"
                      value={formData.venue_name}
                      onChange={(e) => setFormData(prev => ({...prev, venue_name: e.target.value}))}
                      placeholder="Event venue name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({...prev, city: e.target.value}))}
                      placeholder="City"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="venue_address">Venue Address</Label>
                    <Textarea
                      id="venue_address"
                      value={formData.venue_address}
                      onChange={(e) => setFormData(prev => ({...prev, venue_address: e.target.value}))}
                      placeholder="Full venue address"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="state">State/Province</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({...prev, state: e.target.value}))}
                      placeholder="State or Province"
                    />
                  </div>

                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData(prev => ({...prev, country: e.target.value}))}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing & Registration */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Pricing & Registration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_free"
                      checked={formData.is_free}
                      onChange={(e) => setFormData(prev => ({...prev, is_free: e.target.checked}))}
                    />
                    <Label htmlFor="is_free">This is a free event</Label>
                  </div>

                  {!formData.is_free && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="pricing_type">Pricing Type</Label>
                        <select
                          id="pricing_type"
                          value={formData.pricing_type || 'single'}
                          onChange={(e) => setFormData(prev => ({...prev, pricing_type: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="single">Single Price</option>
                          <option value="range">Price Range</option>
                          <option value="two_tier">General & VIP Admission</option>
                        </select>
                      </div>
                      
                      {formData.pricing_type === 'two_tier' ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="price_min">General Admission ($)</Label>
                            <Input
                              id="price_min"
                              type="number"
                              step="0.01"
                              value={formData.price_min || ''}
                              onChange={(e) => setFormData(prev => ({...prev, price_min: parseFloat(e.target.value) || null}))}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label htmlFor="vip_price">VIP Admission ($)</Label>
                            <Input
                              id="vip_price"
                              type="number"
                              step="0.01"
                              value={formData.vip_price || ''}
                              onChange={(e) => setFormData(prev => ({...prev, vip_price: parseFloat(e.target.value) || null}))}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      ) : formData.pricing_type === 'range' ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="price_min">Min Price ($)</Label>
                            <Input
                              id="price_min"
                              type="number"
                              step="0.01"
                              value={formData.price_min || ''}
                              onChange={(e) => setFormData(prev => ({...prev, price_min: parseFloat(e.target.value) || null}))}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label htmlFor="price_max">Max Price ($)</Label>
                            <Input
                              id="price_max"
                              type="number"
                              step="0.01"
                              value={formData.price_max || ''}
                              onChange={(e) => setFormData(prev => ({...prev, price_max: parseFloat(e.target.value) || null}))}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="price_min">Price ($)</Label>
                          <Input
                            id="price_min"
                            type="number"
                            step="0.01"
                            value={formData.price_min || ''}
                            onChange={(e) => setFormData(prev => ({...prev, price_min: parseFloat(e.target.value) || null}))}
                            placeholder="0.00"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="registration_url">Registration/Ticket URL</Label>
                    <Input
                      id="registration_url"
                      type="url"
                      value={formData.registration_url}
                      onChange={(e) => setFormData(prev => ({...prev, registration_url: e.target.value}))}
                      placeholder="https://..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="website_url">Event Website</Label>
                    <Input
                      id="website_url"
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData(prev => ({...prev, website_url: e.target.value}))}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="submitter_name">Your Name *</Label>
                    <Input
                      id="submitter_name"
                      value={formData.submitter_name}
                      onChange={(e) => setFormData(prev => ({...prev, submitter_name: e.target.value}))}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="submitter_email">Your Email *</Label>
                    <Input
                      id="submitter_email"
                      type="email"
                      value={formData.submitter_email}
                      onChange={(e) => setFormData(prev => ({...prev, submitter_email: e.target.value}))}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="submitter_phone">Phone Number</Label>
                    <Input
                      id="submitter_phone"
                      type="tel"
                      value={formData.submitter_phone}
                      onChange={(e) => setFormData(prev => ({...prev, submitter_phone: e.target.value}))}
                      placeholder="Optional"
                    />
                  </div>

                  <div>
                    <Label htmlFor="organization_name">Organization</Label>
                    <Input
                      id="organization_name"
                      value={formData.organization_name}
                      onChange={(e) => setFormData(prev => ({...prev, organization_name: e.target.value}))}
                      placeholder="Your organization name"
                    />
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label>Event Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <Label htmlFor="additional_info">Additional Information</Label>
                <Textarea
                  id="additional_info"
                  value={formData.additional_info}
                  onChange={(e) => setFormData(prev => ({...prev, additional_info: e.target.value}))}
                  placeholder="Any additional details you'd like to share"
                  rows={3}
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-between pt-6">
                <Button type="button" variant="outline" onClick={() => navigate("/")}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Event'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Helper to format ISO datetime string for datetime-local input.
// Be defensive: bad legacy/null date values should not crash the edit modal.
function formatLocalDateTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

const getOrganizationTimezone = (organization) => (
  organization?.timezone || organization?.settings?.timezone || organization?.default_timezone || 'America/New_York'
);

// Edit Event Modal Component - Full Inline Editor
const EditEventModal = ({ event, onEventUpdated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    organizer_id: undefined,
    venue_id: undefined,
    category_ids: [],
    tags: [],
    start_at: "",
    end_at: "",
    timezone: "America/New_York",
    event_type: "in-person",
    ticket_url: "",
    price_min: "",
    price_max: "",
    max_attendees: "",
    is_free: true,
    recurrence_type: "none",
    recurrence_data: {},
  });
  
  const [hosts, setHosts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customFieldTemplates, setCustomFieldTemplates] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recurringUpdateType, setRecurringUpdateType] = useState("all");
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);
  const [featuredImageIndex, setFeaturedImageIndex] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  
  const { toast } = useToast();
  const { token, currentOrganization } = useAuth();
  const defaultTimezone = getOrganizationTimezone(currentOrganization);

  // Load event data when modal opens
  useEffect(() => {
    if (isOpen && event) {
      // Pre-populate form with event data
      setFormData({
        title: event.title || "",
        description: event.description || "",
        organizer_id: event.organizer_id || "",
        venue_id: event.venue_id || "",
        category_ids: Array.isArray(event.category_ids) ? event.category_ids : [],
        tags: Array.isArray(event.tags) ? event.tags : [],
        start_at: formatLocalDateTime(event.start_at),
        end_at: formatLocalDateTime(event.end_at),
        timezone: event.timezone && event.timezone !== "UTC" ? event.timezone : defaultTimezone,
        event_type: event.event_type || "in-person",
        ticket_url: event.ticket_url || "",
        price_min: event.price_min ? event.price_min.toString() : "",
        price_max: event.price_max ? event.price_max.toString() : "",
        max_attendees: event.max_attendees ? event.max_attendees.toString() : "",
        is_free: (event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free',
        is_all_day: event.is_all_day || false,
        is_multi_day: event.is_multi_day || false,
        recurrence_type: event.recurrence_type || "none",
        recurrence_data: event.recurrence_data || {},
      });
      
      // Load custom field values from the event
      setCustomFieldValues(event.custom_fields || {});
      
      // Load existing images
      loadExistingImages();
      loadFormData();
    }
  }, [isOpen, event]);

  const loadExistingImages = async () => {
    if (!event?.id) return;
    
    try {
      const response = await axios.get(`${API}/events/${event.id}/images`);
      setExistingImages(response.data.images || []);
    } catch (error) {
      console.error("Failed to load existing images:", error);
    }
  };

  // Load hosts, venues, categories
  const loadFormData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [organizersRes, venuesRes, categoriesRes, customFieldsRes] = await Promise.all([
        axios.get(`${API}/hosts`, { headers }),
        axios.get(`${API}/venues`, { headers }),
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/custom-field-templates`, { headers })
      ]);
      
      setHosts(organizersRes.data);
      setVenues(venuesRes.data);
      setCategories(categoriesRes.data);
      setCustomFieldTemplates(Array.isArray(customFieldsRes.data) ? customFieldsRes.data.filter(f => f.active) : []);
    } catch (error) {
      console.error("Failed to load form data:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleRemoveExistingImage = async (imageIndex) => {
    if (!window.confirm('Are you sure you want to remove this image?')) {
      return;
    }
    
    try {
      const imageToRemove = existingImages[imageIndex];
      console.log('Deleting image:', imageToRemove);
      console.log('Delete URL:', `${API}/events/${event.id}/images/${imageToRemove.filename}`);
      
      // Remove from backend
      const response = await axios.delete(`${API}/events/${event.id}/images/${imageToRemove.filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Delete response:', response.data);
      
      // Update local state only after successful deletion
      const updatedImages = existingImages.filter((_, idx) => idx !== imageIndex);
      setExistingImages(updatedImages);
      
      toast({
        title: "Image Removed",
        description: "Image has been deleted successfully",
      });
      
      // Don't close modal - just update in background
    } catch (error) {
      console.error("Error removing image:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to remove image",
        variant: "destructive",
      });
    }
  };

  const handleSetFeaturedImage = async (imageIndex) => {
    try {
      const imageToFeature = existingImages[imageIndex];
      
      // Update featured image on backend
      await axios.put(`${API}/events/${event.id}/featured-image`, {
        filename: imageToFeature.filename
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update local state - mark this as featured, unmark others
      const updatedImages = existingImages.map((img, idx) => ({
        ...img,
        is_featured: idx === imageIndex
      }));
      setExistingImages(updatedImages);
      
      toast({
        title: "Featured Image Updated",
        description: "Featured image has been set successfully",
      });
      
      // Don't close modal - just update in background
    } catch (error) {
      console.error("Error setting featured image:", error);
      toast({
        title: "Error",
        description: "Failed to set featured image",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If this is a recurring event instance, show the update type dialog
    const isRecurringInstance = event?.recurring_master_id && !event?.is_recurring_master;
    const isRecurringMaster = event?.is_recurring_master;
    if ((isRecurringInstance || isRecurringMaster) && !showRecurringDialog) {
      setShowRecurringDialog(true);
      return;
    }
    setShowRecurringDialog(false);
    setLoading(true);

    try {
      // Validate dates (only if end_at is provided)
      const startDate = new Date(formData.start_at);
      if (Number.isNaN(startDate.getTime())) {
        toast({
          title: "Invalid Start Date",
          description: "Please enter a valid start date and time.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      let endDate = null;
      
      if (formData.end_at) {
        endDate = new Date(formData.end_at);
        if (Number.isNaN(endDate.getTime())) {
          toast({
            title: "Invalid End Date",
            description: "Please enter a valid end date and time.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        
        if (endDate <= startDate) {
          toast({
            title: "Invalid Dates",
            description: "End time must be after start time",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }
      
      const parsedPriceMin = formData.price_min ? parseFloat(formData.price_min) : null;
      const parsedPriceMax = formData.price_max ? parseFloat(formData.price_max) : null;
      const pricingStatus = formData.is_free ? 'free' : ((parsedPriceMin != null || parsedPriceMax != null) ? 'paid' : 'unspecified');

      const eventData = {
        ...formData,
        start_at: startDate.toISOString(),
        end_at: endDate ? endDate.toISOString() : null,
        price_min: parsedPriceMin,
        price_max: parsedPriceMax,
        pricing_status: pricingStatus,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        custom_fields: customFieldValues
      };

      // Handle recurring event updates
      if (isRecurringInstance || isRecurringMaster) {
        const masterId = isRecurringMaster ? event.id : event.recurring_master_id;
        await axios.put(`${API}/recurring-events/${masterId}?update_type=${recurringUpdateType}${
          recurringUpdateType !== 'all' && event.recurring_instance_date 
            ? `&instance_date=${event.recurring_instance_date}` 
            : ''
        }`, eventData);
      } else {
        await axios.put(`${API}/events/${event.id}`, eventData);
      }
      
      // If recurrence settings changed and this is a new recurring setup, generate instances
      if (formData.recurrence_type !== 'none' && (!event.recurrence_type || event.recurrence_type === 'none')) {
        try {
          await axios.post(`${API}/events/${event.id}/generate-recurring`);
        } catch (recurringError) {
          console.error('Error generating recurring instances:', recurringError);
        }
      }
      
      // Upload new images if any are selected
      if (selectedImages.length > 0) {
        const imageFormData = new FormData();
        selectedImages.forEach((image, index) => {
          imageFormData.append('files', image);
        });
        
        if (featuredImageIndex !== null) {
          imageFormData.append('featured_image_index', featuredImageIndex.toString());
        }
        
        await axios.post(`${API}/events/${event.id}/images`, imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast({
        title: "Success",
        description: "Event updated successfully",
      });
      
      // Close modal and refresh
      setIsOpen(false);
      onEventUpdated();
      
      // Reset form
      setSelectedImages([]);
      setFeaturedImageIndex(null);
      
    } catch (error) {
      console.error('Error updating event:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = "Failed to update event";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors
          errorMessage = error.response.data.detail.map(err => 
            `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
          ).join('; ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button 
        variant="outline" 
        size="sm" 
        className="text-blue-600 hover:text-blue-700"
        onClick={() => setIsOpen(true)}
      >
        <Edit className="w-4 h-4" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
            <DialogDescription>Update event information and settings</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Event Title */}
              <div className="md:col-span-2">
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter event title"
                  required
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your event"
                  rows={4}
                  required
                />
              </div>

              {/* Organizer */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="organizer_id">Organizer *</Label>
                  <AddHostModal onHostAdded={(newHost) => {
                    setHosts(prev => [...prev, newHost]);
                    setFormData(prev => ({...prev, organizer_id: newHost.id}));
                  }} />
                </div>
                <SearchableSelect
                  options={hosts}
                  value={formData.organizer_id}
                  onChange={(value) => setFormData(prev => ({...prev, organizer_id: value}))}
                  placeholder="Search organizers..."
                  displayKey="name"
                  valueKey="id"
                />
              </div>

              {/* Venue */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="venue_id">Venue</Label>
                  <AddVenueModal onVenueAdded={(newVenue) => {
                    setVenues(prev => [...prev, newVenue]);
                    setFormData(prev => ({...prev, venue_id: newVenue.id}));
                  }} />
                </div>
                <SearchableSelect
                  options={venues}
                  value={formData.venue_id}
                  onChange={(value) => setFormData(prev => ({...prev, venue_id: value}))}
                  placeholder="Search venues..."
                  displayKey="name"
                  valueKey="id"
                  getDisplayText={(venue) => `${venue.name} - ${venue.city}`}
                />
              </div>

              {/* Event Duration Options */}
              <div className="md:col-span-2 flex flex-wrap gap-6 p-3 bg-gray-50 rounded-lg border">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_all_day}
                    onChange={(e) => {
                      const isAllDay = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        is_all_day: isAllDay,
                        start_at: isAllDay && prev.start_at ? prev.start_at.split('T')[0] + 'T00:00' : prev.start_at,
                        end_at: isAllDay && prev.end_at ? prev.end_at.split('T')[0] + 'T23:59' : prev.end_at
                      }));
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-gray-700">All-Day Event</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_multi_day}
                    onChange={(e) => setFormData(prev => ({...prev, is_multi_day: e.target.checked}))}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Multi-Day Event</span>
                </label>
              </div>

              {/* Start Date & Time */}
              <div>
                <Label htmlFor="start_at">{formData.is_all_day ? 'Start Date *' : 'Start Date & Time *'}</Label>
                <Input
                  id="start_at"
                  name="start_at"
                  type={formData.is_all_day ? "date" : "datetime-local"}
                  value={formData.is_all_day && formData.start_at ? formData.start_at.split('T')[0] : formData.start_at}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      start_at: prev.is_all_day ? val + 'T00:00' : val
                    }));
                  }}
                  required
                />
              </div>

              {/* End Date & Time */}
              <div>
                <Label htmlFor="end_at">{formData.is_all_day ? 'End Date' : 'End Date & Time'}{formData.is_multi_day ? ' *' : ''}</Label>
                <Input
                  id="end_at"
                  name="end_at"
                  type={formData.is_all_day ? "date" : "datetime-local"}
                  value={formData.is_all_day && formData.end_at ? formData.end_at.split('T')[0] : formData.end_at}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      end_at: prev.is_all_day ? val + 'T23:59' : val
                    }));
                  }}
                  {...(formData.is_multi_day ? { required: true } : {})}
                />
                {!formData.is_multi_day && <p className="text-xs text-gray-500 mt-1">Leave empty if no specific end time</p>}
                {formData.is_multi_day && <p className="text-xs text-purple-600 mt-1">End date required for multi-day events</p>}
              </div>

              {/* Event Type */}
              <div>
                <Label htmlFor="event_type">Event Type</Label>
                <Select name="event_type" value={formData.event_type} onValueChange={(value) => setFormData(prev => ({...prev, event_type: value}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in-person">In-Person</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Ticket URL */}
              <div>
                <Label htmlFor="ticket_url">Ticket URL</Label>
                <Input
                  id="ticket_url"
                  name="ticket_url"
                  value={formData.ticket_url}
                  onChange={handleInputChange}
                  placeholder="https://..."
                />
              </div>

              {/* Free Event Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_free"
                  name="is_free"
                  checked={formData.is_free}
                  onChange={handleInputChange}
                  className="rounded"
                />
                <Label htmlFor="is_free">Free Event</Label>
              </div>

              {/* Pricing (if not free) */}
              {!formData.is_free && (
                <>
                  <div>
                    <Label htmlFor="price_min">Min Price ($)</Label>
                    <Input
                      id="price_min"
                      name="price_min"
                      type="number"
                      step="0.01"
                      value={formData.price_min}
                      onChange={handleInputChange}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="price_max">Max Price ($)</Label>
                    <Input
                      id="price_max"
                      name="price_max"
                      type="number"
                      step="0.01"
                      value={formData.price_max}
                      onChange={handleInputChange}
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}

              {/* Max Attendees */}
              <div>
                <Label htmlFor="max_attendees">Max Attendees</Label>
                <Input
                  id="max_attendees"
                  name="max_attendees"
                  type="number"
                  value={formData.max_attendees}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              {/* Categories Selector */}
              <div className="md:col-span-2">
                <Label>Categories</Label>
                <HierarchicalCategorySelector
                  selectedCategories={formData.category_ids}
                  onChange={(categoryIds) => setFormData(prev => ({ ...prev, category_ids: categoryIds }))}
                  multiple={true}
                />
              </div>

              {/* Tags */}
              <div className="md:col-span-2">
                <Label>Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  />
                  <Button type="button" onClick={handleAddTag} variant="outline">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="md:col-span-2">
                  <Label>Current Images</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {existingImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={(() => {
                            const url = image.url || image.public_url;
                            // Handle malformed URLs (http//localhost)
                            if (url?.includes('http//localhost')) {
                              // Extract the path after localhost:3000
                              const match = url.match(/http\/\/localhost:\d+(\/.*)/);
                              return match ? `${BACKEND_URL}${match[1]}` : `${BACKEND_URL}${url}`;
                            }
                            // Handle proper external URLs
                            if (url?.startsWith('https://') || url?.startsWith('http://')) {
                              return url;
                            }
                            // Handle relative URLs
                            if (url?.startsWith('/')) {
                              return `${BACKEND_URL}${url}`;
                            }
                            // Fallback
                            return `${BACKEND_URL}${url}`;
                          })()}
                          alt={image.original_filename}
                          className="w-full h-24 object-cover rounded border"
                        />
                        {image.is_featured && (
                          <div className="absolute top-2 left-2">
                            <Badge className="bg-brand-primary text-white text-xs">Featured</Badge>
                          </div>
                        )}
                        
                        {/* Image management buttons */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!image.is_featured && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetFeaturedImage(index);
                              }}
                              className="bg-blue-500 hover:bg-blue-600 text-white p-1 rounded text-xs"
                              title="Set as featured"
                              type="button"
                            >
                              ⭐
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveExistingImage(index);
                            }}
                            className="bg-red-500 hover:bg-red-600 text-white p-1 rounded text-xs"
                            title="Remove image"
                            type="button"
                          >
                            ✕
                          </button>
                        </div>
                        
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-1">
                          <p className="text-xs truncate">{image.original_filename}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add New Images */}
              <div className="md:col-span-2">
                <Label>Upload New Images</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setSelectedImages(prev => [...prev, ...files]);
                      if (files.length > 0 && featuredImageIndex === null) {
                        setFeaturedImageIndex(0);
                      }
                    }}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />
                </div>
                
                {/* Preview new images to be uploaded */}
                {selectedImages.length > 0 && (
                  <div className="mt-4">
                    <Label className="text-sm text-gray-600">New Images to Upload ({selectedImages.length})</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                      {selectedImages.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-full h-24 object-cover rounded border"
                          />
                          {featuredImageIndex === index && (
                            <div className="absolute top-2 left-2">
                              <Badge className="bg-brand-primary text-white text-xs">Featured</Badge>
                            </div>
                          )}
                          <div className="absolute top-2 right-2 flex gap-1">
                            {featuredImageIndex !== index && (
                              <button
                                onClick={() => setFeaturedImageIndex(index)}
                                className="bg-blue-500 hover:bg-blue-600 text-white p-1 rounded text-xs"
                                title="Set as featured"
                              >
                                ⭐
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const newImages = selectedImages.filter((_, i) => i !== index);
                                setSelectedImages(newImages);
                                if (featuredImageIndex === index) {
                                  setFeaturedImageIndex(newImages.length > 0 ? 0 : null);
                                }
                              }}
                              className="bg-red-500 hover:bg-red-600 text-white p-1 rounded text-xs"
                              title="Remove"
                            >
                              ✕
                            </button>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-1">
                            <p className="text-xs truncate">{file.name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recurring Event Settings */}
            <div className="md:col-span-2">
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">Recurring Event</h3>
                  {(event?.is_recurring_master || event?.recurring_master_id) && (
                    <Badge variant="secondary" className="text-xs">
                      {event?.is_recurring_master ? 'Master Event' : 'Recurring Instance'}
                    </Badge>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="edit_recurrence_type">Recurrence Pattern</Label>
                  <Select 
                    name="recurrence_type" 
                    value={formData.recurrence_type} 
                    onValueChange={(value) => {
                      setFormData(prev => ({
                        ...prev, 
                        recurrence_type: value,
                        recurrence_data: value === 'none' ? {} : {
                          interval: prev.recurrence_data?.interval || 1,
                          end_type: prev.recurrence_data?.end_type || 'never',
                          count: prev.recurrence_data?.count || 10,
                          until: prev.recurrence_data?.until || '',
                          days_of_week: prev.recurrence_data?.days_of_week || [],
                          monthly_type: prev.recurrence_data?.monthly_type || 'same_date',
                          month_day: prev.recurrence_data?.month_day || 1,
                          week_occurrence: prev.recurrence_data?.week_occurrence || 1,
                          week_occurrences: prev.recurrence_data?.week_occurrences || [prev.recurrence_data?.week_occurrence || 1],
                          weekday: prev.recurrence_data?.weekday || 1
                        }
                      }))
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select recurrence pattern" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No recurrence</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.recurrence_type !== 'none' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Repeat every</Label>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={formData.recurrence_data?.interval || 1}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              recurrence_data: {
                                ...prev.recurrence_data,
                                interval: parseInt(e.target.value) || 1
                              }
                            }))}
                            className="w-20"
                          />
                          <span className="text-sm text-gray-600">
                            {formData.recurrence_type === 'daily' && 'day(s)'}
                            {formData.recurrence_type === 'weekly' && 'week(s)'}
                            {formData.recurrence_type === 'monthly' && 'month(s)'}
                            {formData.recurrence_type === 'yearly' && 'year(s)'}
                          </span>
                        </div>
                      </div>

                      <div>
                        <Label>End recurrence</Label>
                        <Select 
                          value={formData.recurrence_data?.end_type || 'never'} 
                          onValueChange={(value) => setFormData(prev => ({
                            ...prev,
                            recurrence_data: {
                              ...prev.recurrence_data,
                              end_type: value
                            }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="never">Never</SelectItem>
                            <SelectItem value="count">After number of occurrences</SelectItem>
                            <SelectItem value="date">On specific date</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {formData.recurrence_data?.end_type === 'count' && (
                      <div>
                        <Label>Number of occurrences</Label>
                        <Input
                          type="number"
                          min="2"
                          max="365"
                          value={formData.recurrence_data?.count || 10}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            recurrence_data: {
                              ...prev.recurrence_data,
                              count: parseInt(e.target.value) || 10
                            }
                          }))}
                          className="w-32"
                        />
                      </div>
                    )}

                    {formData.recurrence_data?.end_type === 'date' && (
                      <div>
                        <Label>End date</Label>
                        <Input
                          type="date"
                          value={formData.recurrence_data?.until || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            recurrence_data: {
                              ...prev.recurrence_data,
                              until: e.target.value
                            }
                          }))}
                        />
                      </div>
                    )}

                    {/* Days of Week for Weekly */}
                    {formData.recurrence_type === 'weekly' && (
                      <div>
                        <Label>Repeat on days</Label>
                        <div className="grid grid-cols-7 gap-1 mt-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                            const isSelected = formData.recurrence_data?.days_of_week?.includes(index) || false;
                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() => {
                                  const currentDays = formData.recurrence_data?.days_of_week || [];
                                  const newDays = isSelected 
                                    ? currentDays.filter(d => d !== index)
                                    : [...currentDays, index].sort();
                                  setFormData(prev => ({
                                    ...prev,
                                    recurrence_data: {
                                      ...prev.recurrence_data,
                                      days_of_week: newDays
                                    }
                                  }));
                                }}
                                className={`w-10 h-10 rounded-full text-xs font-medium transition-colors ${
                                  isSelected 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Monthly Options */}
                    {formData.recurrence_type === 'monthly' && (
                      <div className="space-y-4">
                        <div>
                          <Label>Monthly pattern</Label>
                          <Select 
                            value={formData.recurrence_data?.monthly_type || 'same_date'} 
                            onValueChange={(value) => setFormData(prev => ({
                              ...prev,
                              recurrence_data: {
                                ...prev.recurrence_data,
                                monthly_type: value,
                                month_day: value === 'same_date' ? (prev.recurrence_data?.month_day || 1) : undefined,
                                week_occurrence: value === 'same_weekday' ? (prev.recurrence_data?.week_occurrence || 1) : undefined,
                                week_occurrences: value === 'same_weekday'
                                  ? (prev.recurrence_data?.week_occurrences || [prev.recurrence_data?.week_occurrence || 1])
                                  : undefined,
                                weekday: (value === 'same_weekday' || value === 'last_weekday') ? (prev.recurrence_data?.weekday || 1) : undefined
                              }
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="same_date">Same date each month</SelectItem>
                              <SelectItem value="same_weekday">Same weekday each month</SelectItem>
                              <SelectItem value="last_weekday">Last occurrence of weekday</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {formData.recurrence_data?.monthly_type === 'same_date' && (
                          <div>
                            <Label htmlFor="edit_month_day">Day of the month</Label>
                            <div className="flex items-center space-x-2">
                              <Select 
                                value={formData.recurrence_data?.month_day?.toString() || '1'} 
                                onValueChange={(value) => setFormData(prev => ({
                                  ...prev,
                                  recurrence_data: {
                                    ...prev.recurrence_data,
                                    month_day: parseInt(value)
                                  }
                                }))}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                                    <SelectItem key={day} value={day.toString()}>
                                      {day}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-sm text-gray-600">
                                (e.g., {formData.recurrence_data?.month_day || 1}{getOrdinalSuffix(formData.recurrence_data?.month_day || 1)} of every month)
                              </span>
                            </div>
                          </div>
                        )}

                        {formData.recurrence_data?.monthly_type === 'same_weekday' && (
                          <div className="space-y-4">
                            <div>
                              <Label>Week occurrences (choose one or more)</Label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {[1, 2, 3, 4].map((occ) => {
                                  const current = formData.recurrence_data?.week_occurrences || [formData.recurrence_data?.week_occurrence || 1];
                                  const selected = current.includes(occ);
                                  return (
                                    <button
                                      key={occ}
                                      type="button"
                                      onClick={() => {
                                        const updated = selected
                                          ? current.filter((x) => x !== occ)
                                          : [...current, occ].sort((a, b) => a - b);
                                        const safe = updated.length ? updated : [1];
                                        setFormData(prev => ({
                                          ...prev,
                                          recurrence_data: {
                                            ...prev.recurrence_data,
                                            week_occurrences: safe,
                                            week_occurrence: safe[0]
                                          }
                                        }));
                                      }}
                                      className={`px-3 py-1 rounded-full text-sm font-medium border ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                    >
                                      {occ}{getOrdinalSuffix(occ)}
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Example: select 2nd and 4th for twice-monthly events.
                              </p>
                            </div>

                            <div>
                              <Label>Day of the week</Label>
                              <Select
                                value={formData.recurrence_data?.weekday?.toString() || '1'}
                                onValueChange={(value) => setFormData(prev => ({
                                  ...prev,
                                  recurrence_data: {
                                    ...prev.recurrence_data,
                                    weekday: parseInt(value)
                                  }
                                }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="0">Sunday</SelectItem>
                                  <SelectItem value="1">Monday</SelectItem>
                                  <SelectItem value="2">Tuesday</SelectItem>
                                  <SelectItem value="3">Wednesday</SelectItem>
                                  <SelectItem value="4">Thursday</SelectItem>
                                  <SelectItem value="5">Friday</SelectItem>
                                  <SelectItem value="6">Saturday</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}

                        {formData.recurrence_data?.monthly_type === 'last_weekday' && (
                          <div>
                            <Label>Last occurrence of</Label>
                            <Select 
                              value={formData.recurrence_data?.weekday?.toString() || '1'} 
                              onValueChange={(value) => setFormData(prev => ({
                                ...prev,
                                recurrence_data: {
                                  ...prev.recurrence_data,
                                  weekday: parseInt(value)
                                }
                              }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">Sunday</SelectItem>
                                <SelectItem value="1">Monday</SelectItem>
                                <SelectItem value="2">Tuesday</SelectItem>
                                <SelectItem value="3">Wednesday</SelectItem>
                                <SelectItem value="4">Thursday</SelectItem>
                                <SelectItem value="5">Friday</SelectItem>
                                <SelectItem value="6">Saturday</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-1">
                              Event will occur on the last {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][formData.recurrence_data?.weekday || 1]} of each month
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Recurring Update Type Dialog */}
            {showRecurringDialog && (
              <div className="md:col-span-2 border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <h4 className="font-semibold text-blue-900 mb-3">How would you like to update this recurring event?</h4>
                <div className="space-y-2 mb-4">
                  {event?.is_recurring_master ? (
                    <>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="recurringUpdate" value="all" 
                          checked={recurringUpdateType === 'all'} 
                          onChange={() => setRecurringUpdateType('all')} />
                        <span className="text-sm"><strong>All events</strong> — Update the master and all instances</span>
                      </label>
                    </>
                  ) : (
                    <>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="recurringUpdate" value="this_only" 
                          checked={recurringUpdateType === 'this_only'} 
                          onChange={() => setRecurringUpdateType('this_only')} />
                        <span className="text-sm"><strong>This event only</strong></span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="recurringUpdate" value="this_and_future" 
                          checked={recurringUpdateType === 'this_and_future'} 
                          onChange={() => setRecurringUpdateType('this_and_future')} />
                        <span className="text-sm"><strong>This and future events</strong></span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" name="recurringUpdate" value="all" 
                          checked={recurringUpdateType === 'all'} 
                          onChange={() => setRecurringUpdateType('all')} />
                        <span className="text-sm"><strong>All events in series</strong></span>
                      </label>
                    </>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                    Update
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowRecurringDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Custom Fields Section */}
            {customFieldTemplates.length > 0 && (
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {customFieldTemplates.map((field) => (
                    <div key={field.id} className={field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                      <Label htmlFor={field.field_key}>
                        {field.field_name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      
                      {field.field_type === 'text' && (
                        <Input
                          id={field.field_key}
                          type="text"
                          placeholder={field.placeholder || ''}
                          value={customFieldValues[field.field_key] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.field_key]: e.target.value
                          }))}
                          required={field.required}
                        />
                      )}
                      
                      {field.field_type === 'textarea' && (
                        <textarea
                          id={field.field_key}
                          placeholder={field.placeholder || ''}
                          value={customFieldValues[field.field_key] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.field_key]: e.target.value
                          }))}
                          required={field.required}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-[100px]"
                        />
                      )}
                      
                      {field.field_type === 'number' && (
                        <Input
                          id={field.field_key}
                          type="number"
                          placeholder={field.placeholder || ''}
                          value={customFieldValues[field.field_key] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.field_key]: e.target.value
                          }))}
                          required={field.required}
                        />
                      )}
                      
                      {field.field_type === 'select' && (
                        <Select
                          value={customFieldValues[field.field_key] || ''}
                          onValueChange={(value) => setCustomFieldValues(prev => ({
                            ...prev,
                            [field.field_key]: value
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={field.placeholder || 'Select an option'} />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options?.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {field.field_type === 'checkbox' && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={field.field_key}
                            checked={customFieldValues[field.field_key] === 'true' || customFieldValues[field.field_key] === true}
                            onChange={(e) => setCustomFieldValues(prev => ({
                              ...prev,
                              [field.field_key]: e.target.checked.toString()
                            }))}
                            className="w-4 h-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                          />
                          <label htmlFor={field.field_key} className="text-sm text-gray-700">
                            {field.placeholder || field.help_text}
                          </label>
                        </div>
                      )}
                      
                      {field.help_text && field.field_type !== 'checkbox' && (
                        <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-6">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-brand-primary hover:bg-brand-primary">
                {loading ? "Updating..." : "Update Event"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Duplicate Event Modal — pre-fills a create-event form with original event data.
// The user edits it to set new dates / tweak details, then submits to create a
// brand-new event. No phantom draft is created on the backend first.
const DuplicateEventModal = ({ event, isOpen, onClose, onEventCreated }) => {
  const initialFormState = {
    title: "",
    description: "",
    organizer_id: undefined,
    venue_id: undefined,
    category_ids: [],
    tags: [],
    start_at: "",
    end_at: "",
    timezone: "America/New_York",
    event_type: "in-person",
    ticket_url: "",
    price_min: "",
    price_max: "",
    max_attendees: "",
    is_free: true,
    recurrence_type: "none",
    is_all_day: false,
    is_multi_day: false,
    recurrence_data: {
      interval: 1,
      end_type: "never",
      count: 10,
      until: "",
      days_of_week: [],
      monthly_type: "same_date",
      month_day: 1,
      week_occurrence: 1,
      week_occurrences: [1],
      weekday: 1,
    },
  };

  const [formData, setFormData] = useState(initialFormState);
  const [hosts, setHosts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customFieldTemplates, setCustomFieldTemplates] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [featuredImageIndex, setFeaturedImageIndex] = useState(null);

  const { toast } = useToast();
  const { token } = useAuth();

  // When the modal opens, pre-fill form from the original event
  useEffect(() => {
    if (isOpen && event) {
      setFormData({
        title: `${event.title} (Copy)`,
        description: event.description || "",
        organizer_id: event.organizer_id || undefined,
        venue_id: event.venue_id || undefined,
        category_ids: event.category_ids || [],
        tags: event.tags || [],
        // Dates intentionally cleared — user must choose new dates
        start_at: "",
        end_at: "",
        timezone: event.timezone && event.timezone !== "UTC" ? event.timezone : "America/New_York",
        event_type: event.event_type || "in-person",
        ticket_url: event.ticket_url || "",
        price_min: event.price_min ? event.price_min.toString() : "",
        price_max: event.price_max ? event.price_max.toString() : "",
        max_attendees: event.max_attendees ? event.max_attendees.toString() : "",
        is_free: (event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free',
        is_all_day: event.is_all_day || false,
        is_multi_day: event.is_multi_day || false,
        // Recurrence cleared — user sets up fresh if needed
        recurrence_type: "none",
        recurrence_data: {
          interval: 1,
          end_type: "never",
          count: 10,
          until: "",
          days_of_week: [],
          monthly_type: "same_date",
          month_day: 1,
          week_occurrence: 1,
          week_occurrences: [1],
          weekday: 1,
        },
      });
      setCustomFieldValues(event.custom_fields || {});
      setSelectedImages([]);
      setFeaturedImageIndex(null);
      loadFormData();
    }
  }, [isOpen, event]);

  const loadFormData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [organizersRes, venuesRes, categoriesRes, customFieldsRes] = await Promise.all([
        axios.get(`${API}/hosts`, { headers }),
        axios.get(`${API}/venues`, { headers }),
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/custom-field-templates`, { headers }),
      ]);
      setHosts(organizersRes.data);
      setVenues(venuesRes.data);
      setCategories(categoriesRes.data);
      setCustomFieldTemplates(
        Array.isArray(customFieldsRes.data)
          ? customFieldsRes.data.filter((f) => f.active)
          : []
      );
    } catch (error) {
      console.error("Failed to load form data for duplicate modal:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const startDate = new Date(formData.start_at);
      let endDate = null;

      if (formData.end_at) {
        endDate = new Date(formData.end_at);
        if (endDate <= startDate) {
          toast({
            title: "Invalid Dates",
            description: "End time must be after start time",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }

      const parsedPriceMin = formData.price_min ? parseFloat(formData.price_min) : null;
      const parsedPriceMax = formData.price_max ? parseFloat(formData.price_max) : null;
      const pricingStatus = formData.is_free ? 'free' : ((parsedPriceMin != null || parsedPriceMax != null) ? 'paid' : 'unspecified');

      const eventData = {
        ...formData,
        start_at: startDate.toISOString(),
        end_at: endDate ? endDate.toISOString() : null,
        price_min: parsedPriceMin,
        price_max: parsedPriceMax,
        pricing_status: pricingStatus,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        custom_fields: customFieldValues,
        status: "draft",
      };

      const response = await axios.post(`${API}/events`, eventData);
      const newEventId = response.data.id;

      // Upload any images the user selected
      if (selectedImages.length > 0) {
        const imageFormData = new FormData();
        selectedImages.forEach((img) => imageFormData.append("files", img));
        if (featuredImageIndex !== null) {
          imageFormData.append("featured_image_index", featuredImageIndex.toString());
        }
        await axios.post(`${API}/events/${newEventId}/images`, imageFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Generate recurring instances if requested
      if (formData.recurrence_type !== "none") {
        try {
          await axios.post(`${API}/events/${newEventId}/generate-recurring`);
        } catch (err) {
          console.error("Error generating recurring instances:", err);
        }
      }

      toast({
        title: "Event Created",
        description: `"${formData.title}" has been created as a draft.`,
      });

      onEventCreated();
    } catch (error) {
      console.error("Error creating duplicated event:", error);
      let errorMessage = "Failed to create event";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map((err) => `${err.loc?.join(" > ") || "Field"}: ${err.msg}`)
            .join("; ");
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Duplicate Event</DialogTitle>
          <DialogDescription>
            Review and edit the details below, then click "Create Event" to save a new copy.
            <span className="block mt-1 text-yellow-600 font-medium">
              ⚠️ Please set new event dates — the original dates have been cleared.
            </span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Event Title */}
            <div className="md:col-span-2">
              <Label htmlFor="dup-title">Event Title *</Label>
              <Input
                id="dup-title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter event title"
                required
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <Label htmlFor="dup-description">Description *</Label>
              <Textarea
                id="dup-description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your event"
                rows={4}
                required
              />
            </div>

            {/* Organizer */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="dup-organizer_id">Organizer *</Label>
                <AddHostModal
                  onHostAdded={(newHost) => {
                    setHosts((prev) => [...prev, newHost]);
                    setFormData((prev) => ({ ...prev, organizer_id: newHost.id }));
                  }}
                />
              </div>
              <SearchableSelect
                options={hosts}
                value={formData.organizer_id}
                onChange={(value) => setFormData((prev) => ({ ...prev, organizer_id: value }))}
                placeholder="Search organizers..."
                displayKey="name"
                valueKey="id"
              />
            </div>

            {/* Venue */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="dup-venue_id">Venue</Label>
                <AddVenueModal
                  onVenueAdded={(newVenue) => {
                    setVenues((prev) => [...prev, newVenue]);
                    setFormData((prev) => ({ ...prev, venue_id: newVenue.id }));
                  }}
                />
              </div>
              <SearchableSelect
                options={venues}
                value={formData.venue_id}
                onChange={(value) => setFormData((prev) => ({ ...prev, venue_id: value }))}
                placeholder="Search venues..."
                displayKey="name"
                valueKey="id"
                getDisplayText={(venue) => `${venue.name} - ${venue.city}`}
              />
            </div>

            {/* Event Duration Options */}
            <div className="md:col-span-2 flex flex-wrap gap-6 p-3 bg-gray-50 rounded-lg border">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_all_day}
                  onChange={(e) => {
                    const isAllDay = e.target.checked;
                    setFormData((prev) => ({
                      ...prev,
                      is_all_day: isAllDay,
                      start_at:
                        isAllDay && prev.start_at
                          ? prev.start_at.split("T")[0] + "T00:00"
                          : prev.start_at,
                      end_at:
                        isAllDay && prev.end_at
                          ? prev.end_at.split("T")[0] + "T23:59"
                          : prev.end_at,
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">All-Day Event</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_multi_day}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, is_multi_day: e.target.checked }))
                  }
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">Multi-Day Event</span>
              </label>
            </div>

            {/* Start Date & Time */}
            <div>
              <Label htmlFor="dup-start_at">
                {formData.is_all_day ? "Start Date *" : "Start Date & Time *"}
              </Label>
              <Input
                id="dup-start_at"
                name="start_at"
                type={formData.is_all_day ? "date" : "datetime-local"}
                value={
                  formData.is_all_day && formData.start_at
                    ? formData.start_at.split("T")[0]
                    : formData.start_at
                }
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    start_at: prev.is_all_day ? val + "T00:00" : val,
                  }));
                }}
                required
              />
            </div>

            {/* End Date & Time */}
            <div>
              <Label htmlFor="dup-end_at">
                {formData.is_all_day ? "End Date" : "End Date & Time"}
                {formData.is_multi_day ? " *" : ""}
              </Label>
              <Input
                id="dup-end_at"
                name="end_at"
                type={formData.is_all_day ? "date" : "datetime-local"}
                value={
                  formData.is_all_day && formData.end_at
                    ? formData.end_at.split("T")[0]
                    : formData.end_at
                }
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    end_at: prev.is_all_day ? val + "T23:59" : val,
                  }));
                }}
                {...(formData.is_multi_day ? { required: true } : {})}
              />
              {!formData.is_multi_day && (
                <p className="text-xs text-gray-500 mt-1">Leave empty if no specific end time</p>
              )}
            </div>

            {/* Event Type */}
            <div>
              <Label htmlFor="dup-event_type">Event Type</Label>
              <Select
                name="event_type"
                value={formData.event_type}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, event_type: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Timezone */}
            <div>
              <Label htmlFor="dup-timezone">Timezone</Label>
              <Select
                name="timezone"
                value={formData.timezone}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, timezone: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  <SelectItem value="America/Anchorage">Alaska Time</SelectItem>
                  <SelectItem value="Pacific/Honolulu">Hawaii Time</SelectItem>
                  <SelectItem value="Europe/London">London</SelectItem>
                  <SelectItem value="Europe/Paris">Paris / Berlin</SelectItem>
                  <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                  <SelectItem value="Australia/Sydney">Sydney</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ticket URL */}
            <div>
              <Label htmlFor="dup-ticket_url">Ticket URL</Label>
              <Input
                id="dup-ticket_url"
                name="ticket_url"
                value={formData.ticket_url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </div>

            {/* Free Event Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="dup-is_free"
                name="is_free"
                checked={formData.is_free}
                onChange={handleInputChange}
                className="rounded"
              />
              <Label htmlFor="dup-is_free">Free Event</Label>
            </div>

            {/* Pricing */}
            {!formData.is_free && (
              <>
                <div>
                  <Label htmlFor="dup-price_min">Min Price ($)</Label>
                  <Input
                    id="dup-price_min"
                    name="price_min"
                    type="number"
                    step="0.01"
                    value={formData.price_min}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="dup-price_max">Max Price ($)</Label>
                  <Input
                    id="dup-price_max"
                    name="price_max"
                    type="number"
                    step="0.01"
                    value={formData.price_max}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                </div>
              </>
            )}

            {/* Max Attendees */}
            <div>
              <Label htmlFor="dup-max_attendees">Max Attendees</Label>
              <Input
                id="dup-max_attendees"
                name="max_attendees"
                type="number"
                value={formData.max_attendees}
                onChange={handleInputChange}
                placeholder="Leave empty for unlimited"
              />
            </div>

            {/* Categories */}
            <div className="md:col-span-2">
              <Label>Categories</Label>
              <HierarchicalCategorySelector
                selectedCategories={formData.category_ids}
                onChange={(categoryIds) =>
                  setFormData((prev) => ({ ...prev, category_ids: categoryIds }))
                }
                multiple={true}
              />
            </div>

            {/* Tags */}
            <div className="md:col-span-2">
              <Label>Tags</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleAddTag())
                  }
                />
                <Button type="button" onClick={handleAddTag} variant="outline">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    {tag} ×
                  </Badge>
                ))}
              </div>
            </div>

            {/* Image Upload */}
            <div className="md:col-span-2">
              <Label>Upload Images (optional)</Label>
              <p className="text-xs text-gray-500 mb-2">
                Original images are not copied. Upload new images for this event.
              </p>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setSelectedImages((prev) => [...prev, ...files]);
                  if (files.length > 0 && featuredImageIndex === null) {
                    setFeaturedImageIndex(0);
                  }
                }}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
              {selectedImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                  {selectedImages.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-24 object-cover rounded border"
                      />
                      {featuredImageIndex === index && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-brand-primary text-white text-xs">Featured</Badge>
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex gap-1">
                        {featuredImageIndex !== index && (
                          <button
                            type="button"
                            onClick={() => setFeaturedImageIndex(index)}
                            className="bg-blue-500 hover:bg-blue-600 text-white p-1 rounded text-xs"
                            title="Set as featured"
                          >
                            ⭐
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = selectedImages.filter((_, i) => i !== index);
                            setSelectedImages(updated);
                            if (featuredImageIndex === index) {
                              setFeaturedImageIndex(updated.length > 0 ? 0 : null);
                            }
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white p-1 rounded text-xs"
                          title="Remove"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Fields */}
            {customFieldTemplates.length > 0 && (
              <div className="md:col-span-2 space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {customFieldTemplates.map((field) => (
                    <div
                      key={field.id}
                      className={field.field_type === "textarea" ? "md:col-span-2" : ""}
                    >
                      <Label htmlFor={`dup-cf-${field.field_key}`}>
                        {field.field_name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {field.field_type === "text" && (
                        <Input
                          id={`dup-cf-${field.field_key}`}
                          type="text"
                          placeholder={field.placeholder || ""}
                          value={customFieldValues[field.field_key] || ""}
                          onChange={(e) =>
                            setCustomFieldValues((prev) => ({
                              ...prev,
                              [field.field_key]: e.target.value,
                            }))
                          }
                          required={field.required}
                        />
                      )}
                      {field.field_type === "textarea" && (
                        <textarea
                          id={`dup-cf-${field.field_key}`}
                          placeholder={field.placeholder || ""}
                          value={customFieldValues[field.field_key] || ""}
                          onChange={(e) =>
                            setCustomFieldValues((prev) => ({
                              ...prev,
                              [field.field_key]: e.target.value,
                            }))
                          }
                          required={field.required}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-[100px]"
                        />
                      )}
                      {field.field_type === "number" && (
                        <Input
                          id={`dup-cf-${field.field_key}`}
                          type="number"
                          placeholder={field.placeholder || ""}
                          value={customFieldValues[field.field_key] || ""}
                          onChange={(e) =>
                            setCustomFieldValues((prev) => ({
                              ...prev,
                              [field.field_key]: e.target.value,
                            }))
                          }
                          required={field.required}
                        />
                      )}
                      {field.field_type === "select" && (
                        <Select
                          value={customFieldValues[field.field_key] || ""}
                          onValueChange={(value) =>
                            setCustomFieldValues((prev) => ({
                              ...prev,
                              [field.field_key]: value,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={field.placeholder || "Select an option"}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options?.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {field.help_text && field.field_type !== "checkbox" && (
                        <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-brand-primary hover:bg-brand-primary"
            >
              {loading ? "Creating..." : "Create Event"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Calendar Preview Page Component (Full-screen preview)

// Quick Search Filter Buttons Component for Public Calendar
const QuickSearchFilterButtons = ({ template, onFilterClick, onClearFilters, activeFilterId }) => {
  
  // Default filters if none configured
  const defaultFilters = [
    { id: '1', label: "Today's Events", emoji: '📅', enabled: true, filters: { dateRange: 'today' } },
    { id: '2', label: 'This Weekend', emoji: '🎉', enabled: true, filters: { dateRange: 'weekend' } },
    { id: '3', label: 'Free Events', emoji: '🆓', enabled: true, filters: { isFree: true } },
    { id: '4', label: 'Music Events', emoji: '🎵', enabled: true, filters: { categoryName: 'Music' } },
    { id: '5', label: 'Food & Drink', emoji: '🍕', enabled: true, filters: { categoryName: 'Food & Drink' } }
  ];
  
  const quickSearchFilters = template?.calendar_config?.quickSearchFilters || defaultFilters;
  const enabledFilters = quickSearchFilters.filter(f => f.enabled);
  const showClearButton = template?.calendar_config?.showClearFiltersButton !== false;
  
  // Color schemes for different button types
  const getButtonColors = (index) => {
    const colorSchemes = [
      'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
      'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
      'from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700',
      'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
      'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
      'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700',
      'from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700',
      'from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700',
      'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
    ];
    return colorSchemes[index % colorSchemes.length];
  };

  if (enabledFilters.length === 0 && !showClearButton) {
    return null;
  }

  return (
    <div className="text-center">
      <p className="text-sm text-gray-500 mb-4">Popular searches:</p>
      <div className="flex flex-wrap justify-center gap-3">
        {enabledFilters.map((filter, index) => {
          const isActive = activeFilterId === filter.id;
          return (
            <button
              key={filter.id}
              className={`px-6 py-3 bg-gradient-to-r ${getButtonColors(index)} text-white rounded-full text-sm font-medium transition-all transform hover:scale-105 shadow-lg ${
                isActive ? 'ring-4 ring-white ring-opacity-50' : ''
              }`}
              onClick={() => onFilterClick(filter)}
              title={`Filter by: ${Object.entries(filter.filters).map(([k, v]) => `${k}: ${v}`).join(', ')}`}
            >
              {filter.emoji} {filter.label}
            </button>
          );
        })}
        
        {/* Clear Filters Button */}
        {showClearButton && activeFilterId && (
          <button
            className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-full text-sm font-medium transition-all transform hover:scale-105 shadow-lg"
            onClick={onClearFilters}
          >
            ✕ Clear Filters
          </button>
        )}
      </div>
    </div>
  );
};


const CalendarPreviewPage = ({ templateOverride = null, eventsOverride = null }) => {
  const { templateId } = useParams();
  const [template, setTemplate] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);

  useEffect(() => {
    if (templateOverride) {
      const overrideEvents = Array.isArray(eventsOverride) ? eventsOverride : [];
      setTemplate(templateOverride);
      setAllEvents(overrideEvents);
      setFilteredEvents(overrideEvents);
      setLoading(false);
      return;
    }
    loadTemplateAndEvents();
  }, [templateId, templateOverride, eventsOverride]);

  useEffect(() => {
    // Filter events whenever search query or active filter changes
    applyFilters();
  }, [searchQuery, activeFilter, allEvents]);

  const loadTemplateAndEvents = async () => {
    try {
      if (templateOverride) return;
      // Load the actual template from the API
      console.log('🔍 Loading calendar template:', templateId);
      const templateRes = await axios.get(`${API}/calendar-templates/${templateId}`);
      const loadedTemplate = templateRes.data;
      console.log('✅ Template loaded successfully:', {
        id: loadedTemplate.id,
        name: loadedTemplate.name,
        hasHeaderConfig: !!loadedTemplate.header_config,
        hasCalendarConfig: !!loadedTemplate.calendar_config,
        hasThemeConfig: !!loadedTemplate.theme_config,
        headerText: loadedTemplate.calendar_config?.headerText,
        primaryColor: loadedTemplate.theme_config?.colors?.primary
      });
      
      // Set template first (don't wait for events)
      setTemplate(loadedTemplate);
      
      // Load events separately - don't fail if this errors
      try {
        const eventsRes = await axios.get(`${API}/events`);
        const publishedEvents = eventsRes.data.filter(e => e.status === 'published');
        setAllEvents(publishedEvents);
        setFilteredEvents(publishedEvents);
        console.log(`✅ Loaded ${publishedEvents.length} published events`);
      } catch (eventsError) {
        console.warn('⚠️ Could not load events (may require authentication):', eventsError.response?.status);
        // Set empty events array - template will still work
        setAllEvents([]);
        setFilteredEvents([]);
      }
      
      setLoading(false);
      
    } catch (error) {
      console.error('❌ Error loading template preview:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      console.warn('⚠️ Falling back to mock template due to error');
      // Fallback to basic template if loading fails
      const mockTemplate = {
        id: templateId,
        name: "Public Calendar",
        header_config: {
          showHeader: true,
          headerText: "What's Happening Events",
          heroText: "Discover amazing events happening near you",
          backgroundColor: "#6366F1",
          textColor: "#FFFFFF"
        },
        calendar_config: {
          viewType: "month",
          showImages: true,
          showPrices: true,
          showVenues: true,
          showOrganizers: true,
          backgroundColor: "#F9FAFB",
          enableDayHover: false,
          dayHoverDelay: 300,
          enableEventHover: false,
          eventHoverDelay: 300
        },
        footer_config: {
          showFooter: true,
          footerText: "Stay connected with the best events in your area",
          backgroundColor: "#1F2937",
          textColor: "#FFFFFF",
          showBranding: true
        },
        theme_config: {
          colors: {
            primary: "#6366F1",
            secondary: "#4F46E5",  
            accent: "#F59E0B",
            background: "#FFFFFF",
            surface: "#F9FAFB",
            text: "#1F2937",
            textSecondary: "#6B7280",
            border: "#E5E7EB"
          }
        }
      };
      
      setTemplate(mockTemplate);
      setAllEvents([]);
      setFilteredEvents([]);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allEvents];
    
    // Apply search query filter
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event => {
        return (
          event.title?.toLowerCase().includes(query) ||
          event.description?.toLowerCase().includes(query) ||
          event.host_name?.toLowerCase().includes(query) ||
          event.organizer_name?.toLowerCase().includes(query) ||
          event.venue_name?.toLowerCase().includes(query)
        );
      });
    }
    
    // Apply active filter criteria
    if (activeFilter) {
      const { filters } = activeFilter;
      
      // Date range filter
      if (filters.dateRange) {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        
        filtered = filtered.filter(event => {
          const eventDate = new Date(event.start_at);
          eventDate.setHours(0, 0, 0, 0);
          
          switch (filters.dateRange) {
            case 'today':
              return eventDate.getTime() === now.getTime();
            case 'tomorrow':
              const tomorrow = new Date(now);
              tomorrow.setDate(tomorrow.getDate() + 1);
              return eventDate.getTime() === tomorrow.getTime();
            case 'weekend':
              const dayOfWeek = eventDate.getDay();
              const isThisWeekend = eventDate >= now && (dayOfWeek === 0 || dayOfWeek === 6);
              return isThisWeekend;
            case 'week':
              const weekEnd = new Date(now);
              weekEnd.setDate(weekEnd.getDate() + 7);
              return eventDate >= now && eventDate <= weekEnd;
            case 'month':
              const monthEnd = new Date(now);
              monthEnd.setMonth(monthEnd.getMonth() + 1);
              return eventDate >= now && eventDate <= monthEnd;
            case 'next_week':
              const nextWeekStart = new Date(now);
              nextWeekStart.setDate(nextWeekStart.getDate() + 7);
              const nextWeekEnd = new Date(nextWeekStart);
              nextWeekEnd.setDate(nextWeekEnd.getDate() + 7);
              return eventDate >= nextWeekStart && eventDate <= nextWeekEnd;
            case 'next_month':
              const nextMonthStart = new Date(now);
              nextMonthStart.setMonth(nextMonthStart.getMonth() + 1);
              const nextMonthEnd = new Date(nextMonthStart);
              nextMonthEnd.setMonth(nextMonthEnd.getMonth() + 1);
              return eventDate >= nextMonthStart && eventDate <= nextMonthEnd;
            default:
              return true;
          }
        });
      }
      
      // Free events filter (tri-state aware)
      if (filters.isFree) {
        filtered = filtered.filter(event => (event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free');
      }
      
      // Category filter
      if (filters.categoryName) {
        filtered = filtered.filter(event => {
          return event.category_names?.some(cat => 
            cat.toLowerCase().includes(filters.categoryName.toLowerCase())
          );
        });
      }
      
      // Organizer filter
      if (filters.organizerName) {
        filtered = filtered.filter(event => {
          const organizerName = event.host_name || event.organizer_name || '';
          return organizerName.toLowerCase().includes(filters.organizerName.toLowerCase());
        });
      }
      
      // Venue filter
      if (filters.venueName) {
        filtered = filtered.filter(event => {
          return event.venue_name?.toLowerCase().includes(filters.venueName.toLowerCase());
        });
      }
      
      // Event type filter
      if (filters.eventType) {
        filtered = filtered.filter(event => event.event_type === filters.eventType);
      }
      
      // Price range filter (tri-state aware)
      if (filters.priceRange) {
        filtered = filtered.filter(event => {
          const pricingStatus = event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'));
          if (filters.priceRange === 'free') return pricingStatus === 'free';
          if (pricingStatus !== 'paid') return false;

          const minPrice = event.price_min || 0;
          const maxPrice = event.price_max || minPrice;
          
          switch (filters.priceRange) {
            case 'under-20':
              return maxPrice < 20;
            case 'under-50':
              return maxPrice < 50;
            case 'under-100':
              return maxPrice < 100;
            case 'over-100':
              return minPrice > 100;
            default:
              return true;
          }
        });
      }
    }
    
    setFilteredEvents(filtered);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  const handleQuickFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveFilter(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p>Loading calendar preview...</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Preview Not Found</h1>
          <p className="text-gray-600">The calendar template could not be loaded.</p>
        </div>
      </div>
    );
  }

  const headerStyle = {
    backgroundColor: template.header_config.backgroundColor,
    color: template.header_config.textColor,
  };

  const footerStyle = {
    backgroundColor: template.footer_config.backgroundColor,
    color: template.footer_config.textColor,
  };

  // Get metadata from template or use defaults
  const metadata = template.calendar_config.metadata || {};
  const pageTitle = metadata.title || template.header_config.headerText || 'What\'s Happening Events';
  const pageDescription = metadata.description || template.header_config.heroText || 'Discover amazing events in your community';
  const pageKeywords = metadata.keywords || 'events, calendar, community events';
  const ogImage = metadata.ogImage || '';
  const twitterCard = metadata.twitterCard || 'summary_large_image';
  const faviconUrl = metadata.faviconUrl ? resolveAssetUrl(metadata.faviconUrl) : '/favicon.svg';

  return (
    <div className="min-h-screen" style={{ backgroundColor: template.theme_config.colors.background }}>
      {/* Dynamic Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        
        {/* Favicon */}
        <link rel="icon" type="image/svg+xml" href={faviconUrl} />
        <link rel="alternate icon" href={faviconUrl} />
        <link rel="apple-touch-icon" href={faviconUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>

      {/* Header */}
      {(template.header_config.showHeader !== false) && (
        <header style={headerStyle} className="py-8 md:py-12 px-4 md:px-6 relative">
          <div className="max-w-6xl mx-auto">
            {/* Logo + text: stacked on mobile, side-by-side on desktop */}
            <div className="flex flex-col md:flex-row items-center gap-4">
              {template.header_config.logoUrl && (
                <div className="flex-shrink-0">
                  <img 
                    src={resolveAssetUrl(template.header_config.logoUrl)}
                    alt="Calendar logo" 
                    className="w-auto object-contain"
                    style={{ 
                      imageRendering: 'crisp-edges',
                      height: template.header_config.logoSize === 'small' ? '48px' 
                        : template.header_config.logoSize === 'large' ? '96px'
                        : template.header_config.logoSize === 'xlarge' ? '128px'
                        : '64px' // medium (default)
                    }}
                  />
                </div>
              )}
              
              {/* Header and Hero text */}
              <div className="text-center flex-1">
                {template.header_config.headerText && (
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-2 md:mb-4">{template.header_config.headerText}</h1>
                )}
                {template.header_config.heroText && (
                  <p className="text-lg md:text-xl lg:text-2xl opacity-90 max-w-2xl mx-auto">{template.header_config.heroText}</p>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Calendar Content */}
      <main className="py-12 px-6" style={{ backgroundColor: template.theme_config.colors.background }}>
        <div className="max-w-7xl mx-auto">
          {/* SEARCH BAR FOR END USERS */}
          <div 
            className="mb-12 backdrop-blur-sm rounded-2xl shadow-xl p-6 border" 
            style={{ 
              backgroundColor: `${template.theme_config.colors.surface || '#FFFFFF'}F0`, // surface with opacity
              borderColor: template.theme_config.colors.border || '#E5E7EB'
            }}
          >
            <div className="text-center mb-6">
              <h2 
                className="text-2xl font-semibold mb-2"
                style={{ color: template.theme_config.colors.text }}
              >
                {template.calendar_config.searchSectionTitle || '🔍 Find Your Perfect Event'}
              </h2>
              <p 
                className="text-base"
                style={{ color: template.theme_config.colors.textSecondary }}
              >
                {template.calendar_config.searchSectionSubtitle || 'Search through all events by name, category, venue, or date'}
              </p>
            </div>
            
            {/* Search bar */}
            <div className="max-w-4xl mx-auto mb-6">
              {/* Search Input for Filtering */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="🔍 Search events by name, category, venue, organizer..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full px-5 py-3 text-base border-2 rounded-xl shadow-lg focus:outline-none transition-colors"
                  style={{
                    borderColor: template.theme_config.colors.primary,
                    color: template.theme_config.colors.text,
                    backgroundColor: '#FFFFFF'
                  }}
                  onFocus={(e) => e.target.style.borderColor = template.theme_config.colors.secondary}
                  onBlur={(e) => e.target.style.borderColor = template.theme_config.colors.primary}
                />
                {searchQuery && (
                  <button
                    onClick={() => handleSearchChange('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 hover:opacity-70 text-xl"
                    style={{ color: template.theme_config.colors.textSecondary }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            {/* Quick filter buttons - now customizable from calendar settings */}
            <QuickSearchFilterButtons 
              template={template}
              onFilterClick={handleQuickFilterClick}
              onClearFilters={handleClearFilters}
              activeFilterId={activeFilter?.id}
            />
          </div>
          
          <InteractiveCalendar template={template} events={filteredEvents} />
          
          {/* Event Count Display */}
          {(searchQuery || activeFilter) && (
            <div className="mt-4 text-center" style={{ color: template.theme_config.colors.textSecondary }}>
              Showing {filteredEvents.length} of {allEvents.length} events
            </div>
          )}
          
          {/* PROMINENT Submit Event Button Below Calendar */}
          <div className="mt-12">
            <CalendarSubmitEventButton template={template} prominent={true} />
          </div>
          
          {/* Subscribe to Calendar Button */}
          <div className="mt-8 text-center">
            <SubscribeToCalendarButton template={template} />
          </div>
        </div>
      </main>

      {/* Footer */}
      {template.footer_config.showFooter && (
        <footer style={footerStyle} className="py-12 px-6">
          <div className="max-w-6xl mx-auto text-center">
            {template.footer_config.footerText && (
              <p className="mb-4 text-lg">{template.footer_config.footerText}</p>
            )}
            {template.footer_config.showBranding && (
              <p className="text-sm opacity-75">Powered by What's Happening Events</p>
            )}
          </div>
        </footer>
      )}
    </div>
  );
};

// Week View Component
const WeekView = ({ currentDate, events, template, hoveredEvent, onEventHover, onEventHoverExit, onEventClick }) => {
  const colors = template?.theme_config?.colors || {};

  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      
      const dayEvents = events.filter(event => eventOccursOnDay(event, date)).sort((a, b) => {
        if (a.featured_image && !b.featured_image) return -1;
        if (!a.featured_image && b.featured_image) return 1;
        return new Date(a.start_at) - new Date(b.start_at);
      });

      weekDays.push({
        date,
        events: dayEvents,
        isToday: date.toDateString() === new Date().toDateString()
      });
    }
    return weekDays;
  };

  const weekDays = getWeekDays();

  return (
    <>
      <div className="grid grid-cols-7 border-b" style={{ borderColor: colors.border || '#E5E7EB' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
          <div key={day} className="p-4 text-center" style={{ backgroundColor: colors.surface || '#F9FAFB' }}>
            <div className="font-semibold" style={{ color: colors.text || '#1F2937' }}>{day}</div>
            <div 
              className={`text-2xl font-bold mt-1 ${weekDays[index]?.isToday ? 'text-white p-2 rounded-full' : ''}`}
              style={{ 
                color: weekDays[index]?.isToday ? 'white' : colors.text || '#1F2937',
                backgroundColor: weekDays[index]?.isToday ? colors.primary || '#9275A8' : 'transparent'
              }}
            >
              {weekDays[index]?.date.getDate()}
            </div>
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 min-h-96">
        {weekDays.map((day, index) => (
          <div 
            key={index} 
            className="border-r p-3 space-y-2"
            style={{ borderColor: colors.border || '#E5E7EB', backgroundColor: colors.background || '#FFFFFF' }}
          >
            {day.events.map(event => (
              <div
                key={event.id}
                className={`p-2 rounded cursor-pointer transition-all duration-200 ${
                  hoveredEvent?.id === event.id ? 'transform scale-105 shadow-md' : ''
                }`}
                style={{
                  backgroundColor: (event.featured_image || event.image_url) ? colors.primary : colors.secondary,
                  color: 'white'
                }}
                onMouseEnter={() => onEventHover(event)}
                onMouseLeave={onEventHoverExit}
                onClick={() => onEventClick(event)}
              >
                <div className="font-medium text-sm truncate">{event.title}</div>
                <div className="text-xs opacity-75">
                  {isEventContinuation(event, day.date) ? (
                    <span className="italic">Ongoing</span>
                  ) : (
                    new Date(event.start_at).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })
                  )}
                </div>
                {((event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free') && <div className="text-xs opacity-75">Free</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

// Weekend View Component (Thu-Sun)
const WeekendView = ({ currentDate, events, template, hoveredEvent, onEventHover, onEventHoverExit, onEventClick }) => {
  const colors = template?.theme_config?.colors || {};

  const getWeekendDays = () => {
    const thursday = new Date(currentDate);
    const day = thursday.getDay();
    const diff = thursday.getDate() - day + 4; // Thursday is day 4
    thursday.setDate(diff);

    const weekendDays = [];
    const dayNames = ['Thu', 'Fri', 'Sat', 'Sun'];
    
    for (let i = 0; i < 4; i++) {
      const date = new Date(thursday);
      date.setDate(thursday.getDate() + i);
      
      const dayEvents = events.filter(event => eventOccursOnDay(event, date)).sort((a, b) => {
        if (a.featured_image && !b.featured_image) return -1;
        if (!a.featured_image && b.featured_image) return 1;
        return new Date(a.start_at) - new Date(b.start_at);
      });

      weekendDays.push({
        date,
        events: dayEvents,
        dayName: dayNames[i],
        isToday: date.toDateString() === new Date().toDateString()
      });
    }
    return weekendDays;
  };

  const weekendDays = getWeekendDays();

  return (
    <>
      <div className="grid grid-cols-4 border-b" style={{ borderColor: colors.border || '#E5E7EB' }}>
        {weekendDays.map((day, index) => (
          <div key={index} className="p-4 text-center" style={{ backgroundColor: colors.surface || '#F9FAFB' }}>
            <div className="font-semibold" style={{ color: colors.text || '#1F2937' }}>{day.dayName}</div>
            <div 
              className={`text-2xl font-bold mt-1 ${day.isToday ? 'text-white p-2 rounded-full' : ''}`}
              style={{ 
                color: day.isToday ? 'white' : colors.text || '#1F2937',
                backgroundColor: day.isToday ? colors.primary || '#9275A8' : 'transparent'
              }}
            >
              {day.date.getDate()}
            </div>
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-4 min-h-96">
        {weekendDays.map((day, index) => (
          <div 
            key={index} 
            className="border-r p-3 space-y-2"
            style={{ borderColor: colors.border || '#E5E7EB', backgroundColor: colors.background || '#FFFFFF' }}
          >
            {day.events.map(event => (
              <div
                key={event.id}
                className={`p-2 rounded cursor-pointer transition-all duration-200 ${
                  hoveredEvent?.id === event.id ? 'transform scale-105 shadow-md' : ''
                }`}
                style={{
                  backgroundColor: (event.featured_image || event.image_url) ? colors.primary : colors.secondary,
                  color: 'white'
                }}
                onMouseEnter={() => onEventHover(event)}
                onMouseLeave={onEventHoverExit}
                onClick={() => onEventClick(event)}
              >
                <div className="font-medium text-sm truncate">{event.title}</div>
                <div className="text-xs opacity-75">
                  {isEventContinuation(event, day.date) ? (
                    <span className="italic">Ongoing</span>
                  ) : (
                    new Date(event.start_at).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })
                  )}
                </div>
                {((event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free') && <div className="text-xs opacity-75">Free</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

// Day View Component
const DayView = ({ currentDate, events, template, hoveredEvent, onEventHover, onEventHoverExit, onEventClick }) => {
  const colors = template?.theme_config?.colors || {};

  const dayEvents = events.filter(event => eventOccursOnDay(event, currentDate)).sort((a, b) => {
    if (a.featured_image && !b.featured_image) return -1;
    if (!a.featured_image && b.featured_image) return 1;
    return new Date(a.start_at) - new Date(b.start_at);
  });

  const featuredEvents = dayEvents.filter(e => e.featured_image);
  const otherEvents = dayEvents.filter(e => !e.featured_image);

  return (
    <div className="p-6" style={{ backgroundColor: colors.background || '#FFFFFF' }}>
      {featuredEvents.length > 0 && (
        <div className="mb-8">
          <h3 
            className="text-lg font-semibold mb-4 flex items-center"
            style={{ color: colors.primary || '#9275A8' }}
          >
            <Star className="w-5 h-5 mr-2" />
            Featured Events
          </h3>
          <div className="space-y-4">
            {featuredEvents.map(event => (
              <CalendarEventCard 
                key={event.id} 
                event={event} 
                template={template}
                isHovered={hoveredEvent?.id === event.id}
                onHover={() => onEventHover(event)}
                onHoverExit={onEventHoverExit}
                onClick={() => onEventClick(event)}
              />
            ))}
          </div>
        </div>
      )}

      {otherEvents.length > 0 && (
        <div>
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: colors.text || '#1F2937' }}
          >
            All Events
          </h3>
          <div className="space-y-4">
            {otherEvents.map(event => (
              <CalendarEventCard 
                key={event.id} 
                event={event} 
                template={template}
                isHovered={hoveredEvent?.id === event.id}
                onHover={() => onEventHover(event)}
                onHoverExit={onEventHoverExit}
                onClick={() => onEventClick(event)}
              />
            ))}
          </div>
        </div>
      )}

      {dayEvents.length === 0 && (
        <div className="text-center py-16">
          <Calendar className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textSecondary || '#6B7280' }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: colors.text || '#1F2937' }}>
            No events scheduled
          </h3>
          <p style={{ color: colors.textSecondary || '#6B7280' }}>
            No events are scheduled for {currentDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      )}
    </div>
  );
};

// List View Component
const ListView = ({ currentDate, events, template, hoveredEvent, onEventHover, onEventHoverExit, onEventClick }) => {
  const colors = template?.theme_config?.colors || {};

  // Get events for the next 30 days from current date
  const endDate = new Date(currentDate);
  endDate.setDate(endDate.getDate() + 30);

  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.start_at);
    return eventDate >= currentDate && eventDate <= endDate;
  }).sort((a, b) => {
    if (a.featured_image && !b.featured_image) return -1;
    if (!a.featured_image && b.featured_image) return 1;
    return new Date(a.start_at) - new Date(b.start_at);
  });

  const featuredEvents = upcomingEvents.filter(e => e.featured_image);
  const otherEvents = upcomingEvents.filter(e => !e.featured_image);

  return (
    <div className="p-6" style={{ backgroundColor: colors.background || '#FFFFFF' }}>
      {featuredEvents.length > 0 && (
        <div className="mb-8">
          <h3 
            className="text-lg font-semibold mb-4 flex items-center"
            style={{ color: colors.primary || '#9275A8' }}
          >
            <Star className="w-5 h-5 mr-2" />
            Featured Events
          </h3>
          <div className="space-y-4">
            {featuredEvents.map(event => (
              <CalendarEventCard 
                key={event.id} 
                event={event} 
                template={template}
                isHovered={hoveredEvent?.id === event.id}
                onHover={() => onEventHover(event)}
                onHoverExit={onEventHoverExit}
                onClick={() => onEventClick(event)}
                showDate={true}
              />
            ))}
          </div>
        </div>
      )}

      {otherEvents.length > 0 && (
        <div>
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: colors.text || '#1F2937' }}
          >
            Upcoming Events
          </h3>
          <div className="space-y-4">
            {otherEvents.map(event => (
              <CalendarEventCard 
                key={event.id} 
                event={event} 
                template={template}
                isHovered={hoveredEvent?.id === event.id}
                onHover={() => onEventHover(event)}
                onHoverExit={onEventHoverExit}
                onClick={() => onEventClick(event)}
                showDate={true}
              />
            ))}
          </div>
        </div>
      )}

      {upcomingEvents.length === 0 && (
        <div className="text-center py-16">
          <Calendar className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textSecondary || '#6B7280' }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: colors.text || '#1F2937' }}>
            No upcoming events
          </h3>
          <p style={{ color: colors.textSecondary || '#6B7280' }}>
            No events are scheduled for the next 30 days
          </p>
        </div>
      )}
    </div>
  );
};

// Calendar Event Card Component (for calendar views)
const CalendarEventCard = ({ event, template, isHovered, onHover, onHoverExit, onClick, showDate = false }) => {
  const colors = template?.theme_config?.colors || {};

  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
        isHovered ? 'transform scale-102 shadow-lg' : 'hover:shadow-md'
      }`}
      style={{ 
        borderColor: colors.border || '#E5E7EB',
        backgroundColor: colors.background || '#FFFFFF'
      }}
      onMouseEnter={onHover}
      onMouseLeave={onHoverExit}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        {(() => {
          const imageUrl = getEventImage(event);
          return imageUrl && template?.calendar_config?.showImages && (
            <img
              src={resolveEventImageSrc(imageUrl)}
              alt={event.title}
              className="w-20 h-20 object-cover rounded"
            />
          );
        })()}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <h4 className="font-semibold text-lg truncate" style={{ color: colors.text || '#1F2937' }}>
              {event.title}
            </h4>
            {(event.featured_image || event.image_url) && (
              <Star className="w-4 h-4 ml-2 flex-shrink-0" style={{ color: colors.primary || '#9275A8' }} />
            )}
          </div>
          
          <p className="text-sm mt-1 line-clamp-2" style={{ color: colors.textSecondary || '#6B7280' }}>
            {event.description}
          </p>
          
          <div className="flex items-center flex-wrap gap-4 mt-3 text-sm">
            {showDate && (
              <span className="flex items-center gap-1" style={{ color: colors.textSecondary || '#6B7280' }}>
                <Calendar className="w-4 h-4" />
                {new Date(event.start_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                {event.end_at && new Date(event.end_at).toDateString() !== new Date(event.start_at).toDateString() && (
                  <> – {new Date(event.end_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}</>
                )}
              </span>
            )}
            
            <span className="flex items-center gap-1" style={{ color: colors.textSecondary || '#6B7280' }}>
              <Clock className="w-4 h-4" />
              {new Date(event.start_at).toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit'
              })}
            </span>
            
            {template?.calendar_config?.showVenues && event.venue_id && (
              <span className="flex items-center gap-1" style={{ color: colors.textSecondary || '#6B7280' }}>
                <MapPin className="w-4 h-4" />
                Venue
              </span>
            )}
            
            {template?.calendar_config?.showPrices && ((event.pricing_status || (event.is_free ? 'free' : ((event.price_min != null || event.price_max != null || event.vip_price != null) ? 'paid' : 'unspecified'))) === 'free') && (
              <span 
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ 
                  backgroundColor: colors.accent + '20' || '#F59E0B20',
                  color: colors.accent || '#F59E0B'
                }}
              >
                Free
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Accept Invitation Page Component
const AcceptInvitationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchInvitationDetails();
    }
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch invitation details without password (to check if user exists)
      const response = await axios.post(`${API}/auth/accept-invitation/${token}`);

      if (response.data.status === 'pending_auth') {
        // Show password creation form and pre-fill names if available
        setInvitationData(response.data);
        if (response.data.first_name) {
          setFirstName(response.data.first_name);
        }
        if (response.data.last_name) {
          setLastName(response.data.last_name);
        }
        setLoading(false);
      } else if (response.data.status === 'success' || response.data.status === 'already_member') {
        // Already processed (logged in user)
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      console.error('Invitation fetch error:', err);
      setError(err.response?.data?.detail || 'Failed to load invitation');
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Submit name and password to create account and accept invitation
      const response = await axios.post(`${API}/auth/accept-invitation/${token}`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password: password
      });

      if (response.data.status === 'success' && response.data.access_token) {
        // Auto-login with the returned tokens (store correctly for AuthContext)
        localStorage.setItem('access_token', response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        
        setSuccess(true);
        setTimeout(() => {
          window.location.href = '/'; // Full page reload to update auth context
        }, 1500);
      } else {
        setError('Failed to create account. Please try again.');
      }
    } catch (err) {
      console.error('Password set error:', err);
      setError(err.response?.data?.detail || 'Failed to create account');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">Welcome!</h1>
          <p className="text-gray-600 mb-4">Account created successfully! Taking you to the dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">Invitation Error</h1>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-brand-primary text-white px-6 py-2 rounded-md hover:bg-brand-primary-dark"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (invitationData) {
    // If user already exists *and* has an existing password, show login prompt
    const requiresLogin = invitationData.user_exists && invitationData.allow_password_setup === false;
    if (requiresLogin) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
            <h1 className="text-2xl font-bold mb-4">You've Been Invited!</h1>
            <p className="text-gray-600 mb-6">
              You've been invited to join an organization with the role of <strong>{invitationData.role}</strong>.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Email:</strong> {invitationData.email}
              </p>
            </div>
            <p className="text-gray-600 mb-6">
              An account with this email already exists. Please log in to accept this invitation.
            </p>
            <button
              onClick={() => {
                localStorage.setItem('pendingInvitationToken', token);
                navigate('/?login=true');
              }}
              className="w-full bg-brand-primary text-white px-6 py-3 rounded-md hover:bg-brand-primary-dark mb-3"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    // For new users, show password creation form
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold mb-4">Welcome! Create Your Account</h1>
          <p className="text-gray-600 mb-6">
            You've been invited to join an organization with the role of <strong>{invitationData.role}</strong>.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Email:</strong> {invitationData.email}
            </p>
          </div>
          
          <form onSubmit={handleSetPassword}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="firstName">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter your first name"
                required
                disabled={submitting}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="lastName">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter your last name"
                required
                disabled={submitting}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter your password (min 8 characters)"
                required
                minLength={8}
                disabled={submitting}
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPassword">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Confirm your password"
                required
                minLength={8}
                disabled={submitting}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-primary text-white px-6 py-3 rounded-md hover:bg-brand-primary-dark mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating Account...' : 'Create Account & Join'}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/')}
              disabled={submitting}
              className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

// =============================================================================
// CUSTOM DOMAIN CALENDAR - Public landing page for custom domains
// =============================================================================
const CustomDomainCalendar = ({ domainData }) => {
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadCalendarData();
  }, [domainData.domain]);
  
  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/calendar/${domainData.domain}`);
      setCalendarData(response.data);
    } catch (err) {
      console.error('Error loading calendar:', err);
      setError('Unable to load calendar. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ 
        backgroundColor: domainData.theme_config?.colors?.background || '#F9FAFB' 
      }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" 
               style={{ borderColor: domainData.theme_config?.colors?.primary || '#6366F1' }}></div>
          <p style={{ color: domainData.theme_config?.colors?.text || '#1F2937' }}>
            Loading {domainData.organization_name || 'Calendar'}...
          </p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={loadCalendarData}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  if (!calendarData) return null;
  
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/events/:eventId"
          element={<PublicEventDetailsPage templateOverride={calendarData.template} eventsOverride={calendarData.events} />}
        />
        <Route
          path="*"
          element={<CalendarPreviewPage templateOverride={calendarData.template} eventsOverride={calendarData.events} />}
        />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId="511172052648-v5pgukjce86f1qmbkriljnhe8okquh8p.apps.googleusercontent.com">
        <AuthProvider>
          <Suspense fallback={
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Loading...</p>
              </div>
            </div>
          }>
            <AppContent />
          </Suspense>
        </AuthProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

// Main app content with authentication and public routes
function AppContent() {
  const { isAuthenticated, loading, currentOrganization } = useAuth();
  const [customDomainData, setCustomDomainData] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(true);
  
  // Check if we're on a custom domain
  useEffect(() => {
    checkCustomDomain();
  }, []);
  
  const checkCustomDomain = async () => {
    try {
      const currentDomain = window.location.hostname;
      const response = await axios.get(`${API}/resolve-domain`, {
        params: { domain: currentDomain }
      });
      if (response.data.found) {
        console.log('🌐 Custom domain detected:', response.data);
        setCustomDomainData(response.data);
      }
    } catch (error) {
      console.log('Domain resolution check failed (normal for main app domain)');
    } finally {
      setCheckingDomain(false);
    }
  };
  
  // Check if current route is public (calendar previews, public event submission, etc.)
  const isPublicRoute = () => {
    const path = window.location.pathname;
    return path.startsWith('/calendar-preview/') || 
           path.startsWith('/submit-event') ||
           path.startsWith('/search') ||
           path.startsWith('/accept-invitation/') ||
           path.startsWith('/reset-password') ||
           path.startsWith('/super-admin') ||
           path.startsWith('/privacy') ||
           path.startsWith('/terms');
  };

  // Show loading while checking domain and auth
  if (checkingDomain || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Allow explicit public routes before the custom-domain calendar catch-all.
  // Otherwise custom domains re-render the calendar for /submit-event and the
  // submit button appears to do nothing after navigation.
  if (isPublicRoute()) {
    return <PublicApp />;
  }

  // If we're on a custom domain with a calendar, show the public calendar
  if (customDomainData && customDomainData.calendar_template_id) {
    return <CustomDomainCalendar domainData={customDomainData} />;
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  // Check if user needs to set up organization
  if (!currentOrganization) {
    return <OrganizationSetup />;
  }

  return <AuthenticatedApp />;
}

// Public application (no authentication required)
function PublicApp() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
          <Route path="/calendar-preview/:templateId/events/:eventId" element={<PublicEventDetailsPage />} />
          <Route path="/calendar-preview/:templateId" element={<CalendarPreviewPage />} />
          <Route path="/submit-event" element={<PublicEventSubmission />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/super-admin" element={<SuperAdminPortal />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="*" element={<AuthPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

// Main authenticated application
function AuthenticatedApp() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/events/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
          <Route path="/events/:eventId/performance" element={<ProtectedRoute><EventPerformancePage /></ProtectedRoute>} />
          <Route path="/submit-event" element={<PublicEventSubmission />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/super-admin" element={<ProtectedRoute><SuperAdminPortal /></ProtectedRoute>} />
          <Route path="/event-templates" element={<ProtectedRoute><EventTemplateManager /></ProtectedRoute>} />
          <Route path="/calendar-preview/:templateId/events/:eventId" element={<PublicEventDetailsPage />} />
          <Route path="/calendar-preview/:templateId" element={<CalendarPreviewPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

// Conditional Share Event Button Component
const ConditionalShareEventButton = () => {
  const [showButton, setShowButton] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkIfSubmissionsEnabled();
  }, []);

  const checkIfSubmissionsEnabled = async () => {
    try {
      // Get all hosts and check if any have submissions enabled
      const organizersResponse = await axios.get(`${API}/hosts`);
      const hosts = organizersResponse.data;
      
      let anyEnabled = false;
      for (const organizer of hosts) {
        try {
          const settingsResponse = await axios.get(`${API}/public-submission-settings/${organizer.id}`);
          const settings = settingsResponse.data;
          
          if (settings.submissions_enabled && settings.show_share_button) {
            anyEnabled = true;
            break;
          }
        } catch (error) {
          // Skip if settings don't exist for this organizer
          continue;
        }
      }
      
      setShowButton(anyEnabled);
    } catch (error) {
      console.error('Error checking submission settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !showButton) {
    return null;
  }

  return (
    <Link to="/submit-event">
      <Button
        variant="outline"
        size="sm"
        className="text-green-600 hover:text-green-700 hover:bg-green-50"
      >
        <Share2 className="w-4 h-4 mr-1" />
        Share An Event
      </Button>
    </Link>
  );
};

// Helper function to adjust color brightness
const adjustColorBrightness = (hex, percent) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Adjust brightness
  const newR = Math.max(0, Math.min(255, r + (r * percent / 100)));
  const newG = Math.max(0, Math.min(255, g + (g * percent / 100)));
  const newB = Math.max(0, Math.min(255, b + (b * percent / 100)));
  
  // Convert back to hex
  const toHex = (c) => {
    const hex = Math.round(c).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// Subscribe to Calendar Button Component
const SubscribeToCalendarButton = ({ template }) => {
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();
  
  if (!template) return null;
  
  const calendarFeedUrl = `${API}/calendar-feed/${template.id}`;
  
  const handleSubscribeClick = () => {
    setShowModal(true);
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: "Calendar URL copied to clipboard" });
  };
  
  const subscriptionLinks = [
    {
      name: "Google Calendar",
      url: `https://calendar.google.com/calendar/u/0/r/settings/addbyurl?url=${encodeURIComponent(calendarFeedUrl)}`,
      icon: "🗓️"
    },
    {
      name: "Apple Calendar", 
      url: `webcal://${calendarFeedUrl.replace('https://', '').replace('http://', '')}`,
      icon: "🍎"
    },
    {
      name: "Outlook",
      url: `https://outlook.live.com/calendar/0/addcalendar?url=${encodeURIComponent(calendarFeedUrl)}`,
      icon: "📅"
    }
  ];
  
  return (
    <>
      <Button
        onClick={handleSubscribeClick}
        className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
        style={{
          backgroundColor: template?.theme_config?.colors?.accent || '#2563eb',
        }}
        onMouseEnter={(e) => {
          const hoverColor = adjustColorBrightness(template?.theme_config?.colors?.accent || '#2563eb', -20);
          e.target.style.backgroundColor = hoverColor;
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = template?.theme_config?.colors?.accent || '#2563eb';
        }}
      >
        <Calendar className="w-4 h-4 mr-2" />
        Subscribe to Calendar
      </Button>
      
      {/* Subscription Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Subscribe to Calendar</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Choose your preferred calendar app to subscribe and automatically receive updates about new events.
            </p>
            
            <div className="space-y-3 mb-6">
              {subscriptionLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-xl mr-3">{link.icon}</span>
                  <span className="font-medium">{link.name}</span>
                </a>
              ))}
              
              {/* Direct download option */}
              <a
                href={calendarFeedUrl}
                download="calendar.ics"
                className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors border-dashed"
              >
                <span className="text-xl mr-3">📥</span>
                <div>
                  <span className="font-medium">Download Calendar File</span>
                  <p className="text-xs text-gray-500">Download .ics file to import manually</p>
                </div>
              </a>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-xs text-gray-500 mb-2">
                Or copy the calendar URL for other calendar apps:
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={calendarFeedUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded text-xs bg-gray-50"
                />
                <Button
                  onClick={() => copyToClipboard(calendarFeedUrl)}
                  size="sm"
                  variant="outline"
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Calendar Submit Event Button Component (for public calendar pages)
const CalendarSubmitEventButton = ({ template, prominent = false }) => {
  const [showButton, setShowButton] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (template && template.organization_id) {
      checkSubmissionSettings(template.organization_id);
    }
  }, [template]);

  const checkSubmissionSettings = async (organizationId) => {
    try {
      console.log('🔍 Checking submission settings for organization:', organizationId);
      
      // Get public submission settings for the organization
      const settingsResponse = await axios.get(`${API}/public-submission-settings/${organizationId}`);
      const settings = settingsResponse.data;
      
      console.log('✅ Public submission settings:', {
        organizationId: organizationId,
        submissions_enabled: settings.submissions_enabled,
        show_on_calendar: settings.show_on_calendar,
        show_share_button: settings.show_share_button
      });
      
      if (settings.submissions_enabled && settings.show_on_calendar) {
        setShowButton(true);
        console.log('✅ Submit button will be shown');
      } else {
        console.log('⚠️ Button not shown:', {
          reason: !settings.submissions_enabled ? 'submissions disabled' : 'calendar button hidden'
        });
      }
    } catch (error) {
      // If settings don't exist or error occurs, don't show button
      console.log('❌ Error checking submission settings:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEvent = () => {
    // Navigate to submit event page with organization context
    navigate(`/submit-event?calendar=${template.id}&organization=${template.organization_id}`);
  };

  if (loading || !showButton) {
    return null;
  }

  // Get customizable settings
  const buttonText = template?.calendar_config?.submitButtonText || 'Submit Your Event';
  const subtitleText = template?.calendar_config?.submitButtonSubtitle || "Share your event with our community - it's free and easy!";
  const showSubtitle = template?.calendar_config?.showSubmitSubtitle !== false;
  const buttonSize = template?.calendar_config?.submitButtonSize || 'large';
  const buttonPosition = template?.calendar_config?.submitButtonPosition || 'center';
  
  // Get button colors from template settings (fallback to accent color or defaults)
  const buttonBgColor = template?.calendar_config?.submitButtonBgColor || template?.theme_config?.colors?.accent || '#9275A8';
  const buttonTextColor = template?.calendar_config?.submitButtonTextColor || '#FFFFFF';
  
  // Generate hover color (slightly darker)
  const hoverColor = adjustColorBrightness(buttonBgColor, -20);

  if (prominent) {
    // Size configurations
    const sizeClasses = {
      small: 'px-6 py-3 text-lg',
      medium: 'px-10 py-4 text-xl',
      large: 'px-12 py-6 text-2xl'
    };
    
    const iconSizes = {
      small: 'h-5 w-5',
      medium: 'h-6 w-6',
      large: 'h-8 w-8'
    };
    
    const subtitleSizes = {
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-lg'
    };
    
    // Position configurations
    const positionClasses = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right'
    };
    
    const justifyClasses = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end'
    };
    
    // Large prominent button below calendar
    return (
      <div className={positionClasses[buttonPosition]}>
        <button
          onClick={handleSubmitEvent}
          className={`${sizeClasses[buttonSize]} font-bold rounded-2xl shadow-2xl transition-all transform hover:scale-105 hover:shadow-3xl`}
          style={{
            backgroundColor: buttonBgColor,
            color: buttonTextColor,
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = hoverColor;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = buttonBgColor;
          }}
        >
          <span className={`flex items-center ${justifyClasses[buttonPosition]} gap-3`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={iconSizes[buttonSize]} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {buttonText}
          </span>
        </button>
        {showSubtitle && (
          <p className={`mt-4 text-gray-600 ${subtitleSizes[buttonSize]}`}>
            {subtitleText}
          </p>
        )}
      </div>
    );
  }

  return (
    <Button
      onClick={handleSubmitEvent}
      className="text-white shadow-lg transition-colors"
      style={{
        backgroundColor: accentColor,
      }}
      onMouseEnter={(e) => {
        e.target.style.backgroundColor = hoverColor;
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = accentColor;
      }}
      size="sm"
    >
      <Share2 className="w-4 h-4 mr-2" />
      Submit Event
    </Button>
  );
};

// Featured Events Manager Component
const FeaturedEventsManager = ({ promoters, hosts, categories }) => {
  const [settings, setSettings] = useState({});
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (promoters && promoters.length > 0) {
      fetchSettings();
      fetchRules();
    } else {
      setLoading(false);
    }
  }, [promoters]);

  const fetchSettings = async () => {
    if (!promoters[0]?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/promoters/${promoters[0].id}/featured-event-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching featured event settings:', error);
      // Set default settings if none exist
      setSettings({
        promoter_id: promoters[0].id,
        max_featured_events: 10,
        auto_feature_own_events: true,
        featured_event_style: { highlight_color: "#FFD700", border_style: "solid", show_badge: true },
        pricing_enabled: false,
        featured_price_per_day: 0.00
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    if (!promoters[0]?.id) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/promoters/${promoters[0].id}/featured-event-rules`);
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching featured event rules:', error);
      setRules([]);
    }
  };

  const updateSettings = async (updatedSettings) => {
    if (!promoters[0]?.id) return;
    
    try {
      await axios.put(`${API}/promoters/${promoters[0].id}/featured-event-settings`, updatedSettings);
      setSettings(prev => ({ ...prev, ...updatedSettings }));
      toast({
        title: "Success",
        description: "Featured event settings updated successfully",
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update settings",
        variant: "destructive",
      });
    }
  };

  const createRule = async (ruleData) => {
    if (!promoters[0]?.id) {
      toast({
        title: "Error",
        description: "No promoter found. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const response = await axios.post(`${API}/promoters/${promoters[0].id}/featured-event-rules`, ruleData);
      console.log('Rule created successfully:', response.data);
      
      await fetchRules();
      setShowCreateRule(false);
      toast({
        title: "Success", 
        description: "Featured event rule created successfully",
      });
    } catch (error) {
      console.error('Error creating rule:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMsg = "Failed to create rule";
      if (error.response?.data?.detail) {
        // Handle array of errors (Pydantic validation)
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else {
          errorMsg = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    }
  };

  const deleteRule = async (ruleId) => {
    if (!promoters[0]?.id) return;
    
    try {
      await axios.delete(`${API}/promoters/${promoters[0].id}/featured-event-rules/${ruleId}`);
      await fetchRules();
      toast({
        title: "Success",
        description: "Featured event rule deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete rule",
        variant: "destructive",
      });
    }
  };

  const toggleRule = async (ruleId, active) => {
    if (!promoters[0]?.id) return;
    
    try {
      await axios.put(`${API}/promoters/${promoters[0].id}/featured-event-rules/${ruleId}`, { active });
      await fetchRules();
      toast({
        title: "Success",
        description: `Rule ${active ? 'enabled' : 'disabled'} successfully`,
      });
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update rule",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Featured Events Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Featured Events Configuration</CardTitle>
            <CardDescription>
              Control which events appear as premium/featured listings on your calendar
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* General Settings */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">General Settings</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <Label htmlFor="highlight_color">Highlight Color</Label>
              <Input
                id="highlight_color"
                type="color"
                value={settings.featured_event_style?.highlight_color || "#FFD700"}
                onChange={(e) => updateSettings({ 
                  featured_event_style: { 
                    ...settings.featured_event_style, 
                    highlight_color: e.target.value 
                  }
                })}
                className="mt-1 h-10"
              />
              <p className="text-xs text-gray-500 mt-1">
                Color used to highlight featured events
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="auto_feature_own"
              checked={settings.auto_feature_own_events || false}
              onChange={(e) => updateSettings({ auto_feature_own_events: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="auto_feature_own" className="text-sm">
              Automatically feature events from my organization
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show_badge"
              checked={settings.featured_event_style?.show_badge || false}
              onChange={(e) => updateSettings({ 
                featured_event_style: { 
                  ...settings.featured_event_style, 
                  show_badge: e.target.checked 
                }
              })}
              className="rounded"
            />
            <Label htmlFor="show_badge" className="text-sm">
              Show "Featured" badge on featured events
            </Label>
          </div>
        </div>

        {/* Featured Event Rules */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Featured Event Rules</h4>
            <Button
              onClick={() => setShowCreateRule(true)}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          </div>

          {/* Maximum Featured Events - moved here */}
          <div className="bg-gray-50 p-3 rounded-md">
            <Label htmlFor="max_featured">Maximum Featured Events</Label>
            <Input
              id="max_featured"
              type="number"
              min="1"
              max="50"
              value={settings.max_featured_events || 10}
              onChange={(e) => updateSettings({ max_featured_events: parseInt(e.target.value) })}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum number of events that can be featured at once
            </p>
          </div>

          {rules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No featured event rules configured</p>
              <p className="text-sm">Create rules to automatically feature specific events</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div key={rule.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={rule.active}
                        onChange={(e) => toggleRule(rule.id, e.target.checked)}
                        className="rounded"
                      />
                      <div>
                        <p className="font-medium">{rule.rule_name}</p>
                        <p className="text-sm text-gray-500">
                          {rule.rule_type.charAt(0).toUpperCase() + rule.rule_type.slice(1)} Rule • Priority: {rule.priority}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteRule(rule.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Rule Modal */}
        {showCreateRule && (
          <CreateFeaturedEventRuleModal
            isOpen={showCreateRule}
            onClose={() => setShowCreateRule(false)}
            onSave={createRule}
            organizers={hosts}
            categories={categories}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Create Featured Event Rule Modal Component
const CreateFeaturedEventRuleModal = ({ isOpen, onClose, onSave, organizers, categories }) => {
  const hosts = organizers || [];
  const [ruleData, setRuleData] = useState({
    rule_name: '',
    rule_type: 'category',
    rule_value: {},
    priority: 1,
    active: true
  });

  const [selectedItems, setSelectedItems] = useState([]);

  const ruleTypes = [
    { value: 'category', label: 'By Category', description: 'Feature events from specific categories' },
    { value: 'organizer', label: 'By Organizer', description: 'Feature events from specific hosts' },
    { value: 'organization', label: 'By Organization', description: 'Feature events from your organization' },
    { value: 'event_type', label: 'By Event Type', description: 'Feature free or paid events' },
    { value: 'tags', label: 'By Tags', description: 'Feature events with specific tags' }
  ];

  const handleSave = () => {
    console.log('handleSave called');
    console.log('ruleData:', ruleData);
    console.log('selectedItems:', selectedItems);
    
    if (!ruleData.rule_name.trim()) {
      console.log('Validation failed: rule name is empty');
      alert('Please enter a rule name');
      return;
    }

    const ruleValue = {};
    
    switch (ruleData.rule_type) {
      case 'category':
        if (selectedItems.length === 0) {
          console.log('Validation failed: no categories selected');
          alert('Please select at least one category');
          return;
        }
        ruleValue.category_ids = selectedItems;
        break;
      case 'organizer':
        if (selectedItems.length === 0) {
          console.log('Validation failed: no organizers selected');
          alert('Please select at least one organizer');
          return;
        }
        ruleValue.organizer_ids = selectedItems;
        break;
      case 'organization':
        ruleValue.feature_own_events = true;
        break;
      case 'event_type':
        ruleValue.event_type = selectedItems[0] || 'free';
        break;
      case 'tags':
        if (selectedItems.length === 0) {
          console.log('Validation failed: no tags entered');
          alert('Please enter at least one tag');
          return;
        }
        ruleValue.tags = selectedItems;
        break;
    }

    console.log('Saving rule with ruleValue:', {
      ...ruleData,
      rule_value: ruleValue
    });

    onSave({
      ...ruleData,
      rule_value: ruleValue
    });
  };

  const renderRuleConfiguration = () => {
    switch (ruleData.rule_type) {
      case 'category':
        return (
          <div>
            <Label>Select Categories to Feature</Label>
            <HierarchicalCategorySelector
              selectedCategories={selectedItems}
              onChange={setSelectedItems}
              multiple={true}
            />
          </div>
        );
      
      case 'organizer':
        return (
          <div>
            <Label>Select Organizers to Feature</Label>
            <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
              {hosts.map(organizer => (
                <div key={organizer.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(organizer.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems([...selectedItems, organizer.id]);
                      } else {
                        setSelectedItems(selectedItems.filter(id => id !== organizer.id));
                      }
                    }}
                    className="rounded"
                  />
                  <Label className="text-sm">{organizer.name}</Label>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'event_type':
        return (
          <div>
            <Label>Event Type to Feature</Label>
            <select
              value={selectedItems[0] || 'free'}
              onChange={(e) => setSelectedItems([e.target.value])}
              className="mt-2 w-full p-2 border rounded-md"
            >
              <option value="free">Free Events</option>
              <option value="paid">Paid Events</option>
              <option value="all">All Events</option>
            </select>
          </div>
        );
      
      case 'tags':
        return (
          <div>
            <Label>Tags to Feature (comma-separated)</Label>
            <Input
              placeholder="e.g. music, festival, outdoor"
              value={selectedItems.join(', ')}
              onChange={(e) => {
                const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                setSelectedItems(tags);
              }}
              className="mt-2"
            />
          </div>
        );
      
      default:
        return (
          <p className="text-sm text-gray-500">
            Events from your organization will be automatically featured
          </p>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium mb-4">Create Featured Event Rule</h3>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="rule_name">Rule Name</Label>
            <Input
              id="rule_name"
              placeholder="e.g. Feature Music Events"
              value={ruleData.rule_name}
              onChange={(e) => setRuleData({ ...ruleData, rule_name: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="rule_type">Rule Type</Label>
            <select
              id="rule_type"
              value={ruleData.rule_type}
              onChange={(e) => {
                setRuleData({ ...ruleData, rule_type: e.target.value });
                setSelectedItems([]);
              }}
              className="mt-1 w-full p-2 border rounded-md"
            >
              {ruleTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {ruleTypes.find(t => t.value === ruleData.rule_type)?.description}
            </p>
          </div>

          {renderRuleConfiguration()}

          <div>
            <Label htmlFor="priority">Priority (1-10)</Label>
            <Input
              id="priority"
              type="number"
              min="1"
              max="10"
              value={ruleData.priority}
              onChange={(e) => setRuleData({ ...ruleData, priority: parseInt(e.target.value) })}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Higher numbers have higher priority
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Create Rule
          </Button>
        </div>
      </div>
    </div>
  );
};

// Event Submission Webhook Manager Component
const EventSubmissionWebhookManager = ({ hosts, currentOrganization }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchWebhookSettings();
    } else {
      // No organization available - set loading to false
      setLoading(false);
    }
  }, [currentOrganization]);

  const fetchWebhookSettings = async () => {
    if (!currentOrganization?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/organizations/${currentOrganization.id}/webhook-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching webhook settings:', error);
      
      // Only set default settings for 404 errors (settings don't exist)
      // For other errors, preserve existing settings and show error
      if (error.response?.status === 404) {
        // Settings don't exist, set defaults
        setSettings({
          organization_id: currentOrganization.id,
          webhook_enabled: false,
          webhook_secret: '',
          webhook_url: `${API}/webhooks/organization/${currentOrganization.id}`,
          auto_publish: false,
          notification_email: ''
        });
      } else {
        // For other errors (auth, network, server), don't clear existing settings
        // Show error toast but preserve current state
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load webhook settings. Please refresh the page.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updatedSettings) => {
    if (!currentOrganization?.id) return;
    
    try {
      const response = await axios.put(`${API}/organizations/${currentOrganization.id}/webhook-settings`, updatedSettings);
      setSettings(response.data);
      
      toast({
        title: "Success",
        description: "Webhook settings updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update webhook settings",
        variant: "destructive",
      });
    }
  };

  const generateWebhookSecret = () => {
    // Generate a cryptographically secure 32-character secret
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    updateSettings({ webhook_secret: secret });
    toast({ title: "Secret Generated!", description: "New webhook secret created" });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: "Copied to clipboard" });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Event Submission Webhooks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  // Show message if no organization is available
  if (!currentOrganization) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Event Submission Webhooks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <p>No organization available. Please select an organization to configure webhook settings.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Event Submission Webhooks</CardTitle>
            <CardDescription>
              Allow external systems to submit events programmatically via webhooks
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDocumentation(!showDocumentation)}
          >
            {showDocumentation ? 'Hide' : 'Show'} Documentation
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Webhook */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="webhook_enabled"
            checked={settings.webhook_enabled || false}
            onChange={(e) => updateSettings({ webhook_enabled: e.target.checked })}
            className="rounded"
          />
          <Label htmlFor="webhook_enabled" className="text-sm">
            Enable event submission webhooks
          </Label>
        </div>

        {settings.webhook_enabled && (
          <>
            {/* Webhook URL */}
            <div>
              <Label className="text-sm font-medium">Webhook URL</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Input
                  readOnly
                  value={settings.webhook_url || ''}
                  className="bg-gray-50 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(settings.webhook_url)}
                >
                  Copy
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                External systems POST to this URL to submit events
              </p>
            </div>

            {/* Webhook Secret */}
            <div>
              <Label className="text-sm font-medium">Webhook Secret</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Input
                  type="password"
                  placeholder="Click 'Generate' to create a secure secret"
                  value={settings.webhook_secret || ''}
                  onChange={(e) => updateSettings({ webhook_secret: e.target.value })}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateWebhookSecret}
                >
                  Generate
                </Button>
                {settings.webhook_secret && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(settings.webhook_secret)}
                  >
                    Copy
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Secret key for webhook authentication
              </p>
            </div>

            {/* Additional Settings */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="auto_publish"
                  checked={settings.auto_publish || false}
                  onChange={(e) => updateSettings({ auto_publish: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="auto_publish" className="text-sm">
                  Auto-publish webhook submissions (bypass draft status)
                </Label>
              </div>

              <div>
                <Label htmlFor="notification_email" className="text-sm">Notification Email (Optional)</Label>
                <Input
                  id="notification_email"
                  type="email"
                  placeholder="email@example.com"
                  value={settings.notification_email || ''}
                  onChange={(e) => updateSettings({ notification_email: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Receive notifications when events are submitted via webhook
                </p>
              </div>
            </div>
          </>
        )}

        {/* Documentation */}
        {showDocumentation && (
          <div className="border-t pt-6">
            <h4 className="font-medium mb-3">Webhook Documentation</h4>
            <div className="space-y-4 text-sm">
              <div>
                <h5 className="font-medium">Request Format:</h5>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`POST ${settings.webhook_url || '[webhook_url]'}
Content-Type: application/json

{
  "webhook_secret": "your_webhook_secret",
  "title": "Event Title",
  "description": "Event description",
  "start_at": "2025-09-15T14:00:00Z",
  "end_at": "2025-09-15T16:00:00Z",
  "timezone": "UTC",
  "event_type": "in-person",
  "venue_id": "venue-uuid-here",
  "category_ids": ["category-uuid-1", "category-uuid-2"],
  "tags": ["music", "concert"],
  "image_url": "https://example.com/event-image.jpg",
  "images": ["https://example.com/img1.jpg", "https://example.com/img2.jpg"],
  "ticket_url": "https://tickets.example.com",
  "event_price": 50.00,
  "price_min": 25.00,
  "price_max": 100.00,
  "max_attendees": 200,
  "is_free": false,
  "submitter": {
    "id": "contact_123",
    "name": "John Doe", 
    "email": "john@example.com",
    "phone": "+1-555-0123"
  },
  "recurrence_type": "weekly",
  "recurrence_data": {"interval": 1, "end_date": "2025-12-31"},
  "location": "Event Location",
  "event_website": "https://event.example.com",
  "custom_fields": {
    "field_key": "field_value",
    "another_field": "another_value"
  }
}`}
                </pre>
              </div>

              <div>
                <h5 className="font-medium">Required Fields:</h5>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><code>webhook_secret</code> - Your webhook authentication secret</li>
                  <li><code>title</code> - Event title</li>
                  <li><code>start_at</code> - Event start time (ISO 8601 format)</li>
                </ul>
                <p className="text-xs text-gray-600 mt-2">
                  <em>Note: <code>end_at</code> is optional - defaults to 2 hours after start time if not provided</em>
                </p>
              </div>

              <div>
                <h5 className="font-medium">All Standard Fields Supported:</h5>
                <div className="mt-1 text-xs space-y-1">
                  <p><strong>Basic:</strong> title, description, start_at, end_at, timezone</p>
                  <p><strong>Classification:</strong> venue_id, category_ids, tags, event_type</p>
                  <p><strong>Media:</strong> image_url, images, featured_image</p>
                  <p><strong>Pricing:</strong> ticket_url, event_price, price_min, price_max, is_free</p>
                  <p><strong>Capacity:</strong> max_attendees</p>
                  <p><strong>Organizer:</strong> organizer, organizer_email (creates/assigns event organizer)</p>
                  <p><strong>Recurrence:</strong> recurrence_type, recurrence_data</p>
                  <p><strong>Legacy:</strong> location, event_website (stored in custom_fields)</p>
                </div>
              </div>

              <div>
                <h5 className="font-medium">Event Organizer Assignment:</h5>
                <div className="mt-1 space-y-2">
                  <p>Specify who organizes the event (different from webhook owner):</p>
                  <div className="text-xs space-y-1">
                    <div><code>organizer</code> - Name of the event organizer</div>
                    <div><code>organizer_email</code> - Email of the event organizer (optional)</div>
                  </div>
                  <div className="text-xs text-gray-600 mt-2">
                    <em>If organizer doesn't exist, a new one will be created automatically. Can also be placed in custom_fields.</em>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium">Submitter Information:</h5>
                <div className="mt-1 space-y-2">
                  <p>Include contact details of the person submitting the event for admin review:</p>
                  <div className="text-xs space-y-1">
                    <div><code>submitter.id</code> - External contact ID</div>
                    <div><code>submitter.name</code> - Full name of submitter</div>
                    <div><code>submitter.email</code> - Email address for contact</div>
                    <div><code>submitter.phone</code> - Phone number for contact</div>
                  </div>
                  <div className="text-xs text-gray-600 mt-2">
                    <em>All submitter fields are optional but recommended for tracking and accountability</em>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium">Custom Fields:</h5>
                <p className="mt-1">
                  Include any custom fields you've created in the <code>custom_fields</code> object. 
                  Use the field key as the property name and provide the appropriate value.
                </p>
              </div>

              <div>
                <h5 className="font-medium">Pricing Options:</h5>
                <div className="mt-1 space-y-2">
                  <div>
                    <strong>Single Price:</strong> Use <code>event_price: 50.00</code> for fixed-price events
                  </div>
                  <div>
                    <strong>Price Range:</strong> Use <code>price_min</code> and <code>price_max</code> for variable pricing
                  </div>
                  <div>
                    <strong>Free Events:</strong> Set <code>is_free: true</code> or omit all pricing fields
                  </div>
                  <div className="text-xs text-gray-600 mt-2">
                    <em>Note: If <code>event_price</code> is provided, it takes precedence over <code>price_min</code>/<code>price_max</code></em>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium">Response:</h5>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs">
{`{
  "success": true,
  "message": "Event submitted successfully via webhook",
  "event_id": "uuid-of-created-event",
  "status": "draft"
}`}
                </pre>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Publish Calendar Modal Component
const PublishCalendarModal = ({ isOpen, onClose, promoter, template }) => {
  const [publishingSettings, setPublishingSettings] = useState({});
  const [deploymentConfig, setDeploymentConfig] = useState({ deployment_host: 'your-deployment-host.com', cname_target: 'your-deployment-host.com' });
  const [loading, setLoading] = useState(true);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [showDNSInstructions, setShowDNSInstructions] = useState(false);
  const [customDomainInput, setCustomDomainInput] = useState(''); // Local state for typing
  const { toast } = useToast();

  useEffect(() => {
    // Fetch deployment config on mount
    fetchDeploymentConfig();
  }, []);

  useEffect(() => {
    if (isOpen && promoter?.id) {
      fetchPublishingSettings();
    }
  }, [isOpen, promoter]);

  const fetchDeploymentConfig = async () => {
    try {
      const response = await axios.get(`${API}/deployment-config`);
      setDeploymentConfig(response.data);
    } catch (error) {
      console.error('Error fetching deployment config:', error);
      // Use default
    }
  };

  const fetchPublishingSettings = async () => {
    if (!promoter?.id) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/promoters/${promoter.id}/publishing-settings`);
      setPublishingSettings(response.data);
      setCustomDomainInput(response.data.custom_domain || ''); // Sync local input state
    } catch (error) {
      console.error('Error fetching publishing settings:', error);
      setPublishingSettings({
        promoter_id: promoter.id,
        custom_domain_enabled: false,
        custom_domain: '',
        domain_verified: false,
        ssl_enabled: true,
        publishing_enabled: false
      });
      setCustomDomainInput('');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updatedSettings) => {
    if (!promoter?.id) return;
    
    try {
      const response = await axios.put(`${API}/promoters/${promoter.id}/publishing-settings`, updatedSettings);
      if (response?.data && typeof response.data === 'object' && 'custom_domain' in response.data) {
        setPublishingSettings(response.data);
      } else {
        setPublishingSettings(prev => ({ ...prev, ...updatedSettings }));
      }
      toast({
        title: "Success",
        description: "Publishing settings updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update publishing settings",
        variant: "destructive",
      });
    }
  };

  const resetDomainSettings = async () => {
    try {
      await updateSettings({
        custom_domain: '',
        custom_domain_enabled: false,
        domain_verified: false,
        ssl_enabled: true
      });
      setCustomDomainInput('');
      toast({
        title: "Reset Complete",
        description: "Custom domain settings have been cleared.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reset custom domain settings",
        variant: "destructive",
      });
    }
  };

  const verifyDomain = async () => {
    if (!customDomainInput.trim()) {
      toast({
        title: "Error",
        description: "Please enter a custom domain first",
        variant: "destructive",
      });
      return;
    }

    setVerifyingDomain(true);
    try {
      // First save the domain, then verify
      await updateSettings({ 
        custom_domain: customDomainInput.trim(),
        calendar_template_id: template?.id
      });
      
      const response = await axios.post(`${API}/promoters/${promoter.id}/verify-domain`, {
        domain: customDomainInput.trim()
      });
      
      if (response.data.verified) {
        updateSettings({ domain_verified: true });
        toast({
          title: "Success",
          description: "Domain verified successfully!",
        });
      } else {
        toast({
          title: "Verification Failed",
          description: response.data.message || "Domain verification failed. Please check your DNS settings.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to verify domain",
        variant: "destructive",
      });
    } finally {
      setVerifyingDomain(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: "Copied to clipboard" });
  };

  const publishToCustomDomain = async () => {
    if (!publishingSettings.domain_verified) {
      toast({
        title: "Error",
        description: "Please verify your custom domain first",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateSettings({ 
        publishing_enabled: true,
        calendar_template_id: template?.id
      });
      toast({
        title: "Calendar Published!",
        description: `Your calendar is now live at ${publishingSettings.custom_domain}`,
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to publish calendar",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;
  const cnameTarget = publishingSettings.cname_target || deploymentConfig.cname_target || deploymentConfig.deployment_host;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Publish Calendar</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="space-y-6">
              {/* Calendar Preview */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">Calendar: {template?.name || 'Untitled Calendar'}</h3>
                <p className="text-sm text-gray-600">
                  Publishing this calendar will make it accessible to the public at your custom domain.
                </p>
              </div>

              {/* Publishing Options */}
              <div className="space-y-4">
                <h4 className="font-medium">Publishing Options</h4>
                
                {/* Custom Domain Publishing - MOVED UP */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="custom_domain_enabled"
                      checked={publishingSettings.custom_domain_enabled || false}
                      onChange={(e) => updateSettings({ custom_domain_enabled: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="custom_domain_enabled" className="font-medium">
                      Custom Domain Publishing
                    </Label>
                    {publishingSettings.domain_verified && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        ✓ Verified
                      </span>
                    )}
                  </div>

                  {publishingSettings.custom_domain_enabled && (
                    <div className="space-y-3 pl-6 border-l-2 border-gray-200">
                      <div>
                        <Label htmlFor="custom_domain" className="text-sm">Custom Domain</Label>
                        <div className="flex items-center space-x-2 mt-1">
                          <Input
                            id="custom_domain"
                            placeholder="events.yourcompany.com"
                            value={customDomainInput}
                            onChange={(e) => setCustomDomainInput(e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={verifyDomain}
                            disabled={verifyingDomain}
                          >
                            {verifyingDomain ? 'Verifying...' : 'Verify'}
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={resetDomainSettings}
                        >
                          Reset Domain Settings
                        </Button>
                      </div>

                      {publishingSettings.vercel_domain && (
                        <div className={`border rounded-lg p-3 text-sm ${
                          publishingSettings.vercel_domain.ok ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'
                        }`}>
                          <div className="font-medium mb-1">Vercel Domain Provisioning</div>
                          {publishingSettings.vercel_domain.ok ? (
                            <div>
                              Domain added to Vercel {publishingSettings.vercel_domain.message ? `(${publishingSettings.vercel_domain.message})` : ''}
                            </div>
                          ) : (
                            <div>
                              Failed to add domain to Vercel{publishingSettings.vercel_domain.error ? `: ${publishingSettings.vercel_domain.error}` : ''}
                            </div>
                          )}
                        </div>
                      )}

                      {/* DNS Instructions */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-medium text-blue-900">DNS Configuration</h5>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDNSInstructions(!showDNSInstructions)}
                          >
                            {showDNSInstructions ? 'Hide' : 'Show'} Setup
                          </Button>
                        </div>
                        
                        {showDNSInstructions && (
                          <div className="space-y-2 text-sm text-blue-800">
                            <p>Add this CNAME record to your DNS:</p>
                            <div className="bg-blue-100 p-2 rounded font-mono text-xs">
                              <div>Type: CNAME</div>
                              <div>Name: {customDomainInput?.split('.')[0] || 'events'}</div>
                              <div>Value: {cnameTarget}</div>
                            </div>
                            <p className="text-xs text-blue-600 mt-2">
                              After adding the CNAME record, DNS propagation may take up to 24 hours.
                            </p>
                          </div>
                        )}
                      </div>

                      {publishingSettings.domain_verified && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✓</span>
                            <div>
                              <p className="text-sm font-medium text-green-900">Domain Verified!</p>
                              <p className="text-xs text-green-700">Ready to publish</p>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  )}
                </div>

                {/* WordPress Plugin */}
                <div className="border rounded-lg p-4 space-y-2 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      disabled
                      className="rounded opacity-50"
                    />
                    <Label className="font-medium text-gray-600">
                      WordPress Plugin
                    </Label>
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                      Coming Soon
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Official WordPress plugin for seamless calendar integration
                  </p>
                </div>

                {/* Additional Publishing Options */}
                <div className="border rounded-lg p-4 space-y-2 bg-gray-50">
                  <h5 className="text-sm font-medium text-gray-600">Additional Options (Coming Soon)</h5>
                  <div className="space-y-1 text-xs text-gray-500">
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                      <span>Social Media Publishing (Facebook Events, LinkedIn)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                      <span>PDF Calendar Export</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                      <span>Email Newsletter Integration</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                      <span>QR Code Calendar Links</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Publish Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={publishToCustomDomain}
                  disabled={!publishingSettings.custom_domain_enabled || !publishingSettings.domain_verified}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Publish Calendar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Calendar Publishing Manager Component
const CalendarPublishingManager = ({ promoters }) => {
  const [publishingSettings, setPublishingSettings] = useState({});
  const [deploymentConfig, setDeploymentConfig] = useState({ deployment_host: 'your-deployment-host.com', cname_target: 'your-deployment-host.com' });
  const [loading, setLoading] = useState(true);
  const [showDNSInstructions, setShowDNSInstructions] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchDeploymentConfig();
    fetchPublishingSettings();
  }, []);

  const fetchDeploymentConfig = async () => {
    try {
      const response = await axios.get(`${API}/deployment-config`);
      setDeploymentConfig(response.data);
    } catch (error) {
      console.error('Error fetching deployment config:', error);
    }
  };

  const fetchPublishingSettings = async () => {
    const orgId = localStorage.getItem('currentOrganizationId');
    if (!orgId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/organizations/${orgId}/custom-domain`);
      // Map backend field names to frontend
      setPublishingSettings({
        ...response.data,
        custom_domain_enabled: response.data.domain_enabled // Map backend field to frontend
      });
    } catch (error) {
      console.error('Error fetching publishing settings:', error);
      // Set default settings if none exist
      setPublishingSettings({
        custom_domain_enabled: false,
        custom_domain: '',
        domain_verified: false,
        ssl_enabled: true,
        cname_target: deploymentConfig.deployment_host
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updatedSettings) => {
    const orgId = localStorage.getItem('currentOrganizationId');
    if (!orgId) return;
    
    try {
      // Map frontend field names to backend
      const backendSettings = {
        custom_domain: updatedSettings.custom_domain !== undefined ? updatedSettings.custom_domain : publishingSettings.custom_domain,
        domain_enabled: updatedSettings.custom_domain_enabled !== undefined ? updatedSettings.custom_domain_enabled : publishingSettings.custom_domain_enabled,
        ssl_enabled: updatedSettings.ssl_enabled !== undefined ? updatedSettings.ssl_enabled : publishingSettings.ssl_enabled
      };
      
      const response = await axios.put(`${API}/organizations/${orgId}/custom-domain`, backendSettings);
      const data = response?.data || {};
      if (data && typeof data === 'object' && 'custom_domain' in data) {
        setPublishingSettings({
          ...data,
          custom_domain_enabled: data.domain_enabled,
        });
      } else {
        setPublishingSettings(prev => ({ ...prev, ...updatedSettings }));
      }
      
      toast({
        title: "Success",
        description: "Publishing settings updated successfully",
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Error", 
        description: "Failed to update publishing settings",
        variant: "destructive",
      });
    }
  };

  const verifyDomain = async () => {
    if (!publishingSettings.custom_domain) {
      toast({
        title: "Error",
        description: "Please enter a custom domain first",
        variant: "destructive",
      });
      return;
    }

    const orgId = localStorage.getItem('currentOrganizationId');
    if (!orgId) return;

    setVerifyingDomain(true);
    try {
      const response = await axios.post(`${API}/organizations/${orgId}/custom-domain/verify`);
      
      if (response.data.verified) {
        setPublishingSettings(prev => ({ 
          ...prev, 
          domain_verified: true,
          verified_at: response.data.verified_at
        }));
        toast({
          title: "Success",
          description: "Domain verified successfully!",
        });
      } else {
        toast({
          title: "Verification Failed",
          description: response.data.message || "Domain verification failed. Please check your DNS settings.",
          variant: "warning",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to verify domain",
        variant: "destructive",
      });
    } finally {
      setVerifyingDomain(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: "Copied to clipboard" });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Calendar Publishing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Calendar Publishing</CardTitle>
            <CardDescription>
              Publish your calendar on a custom domain for professional branding
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Publishing Options */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Publishing Options</h4>
          
          {/* Custom Domain Publishing */}
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="custom_domain_enabled"
                checked={publishingSettings.custom_domain_enabled || false}
                onChange={(e) => updateSettings({ custom_domain_enabled: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="custom_domain_enabled" className="text-sm font-medium">
                Custom Domain Publishing
              </Label>
              {publishingSettings.domain_verified && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  ✓ Verified
                </span>
              )}
            </div>
            
            <p className="text-xs text-gray-600">
              Publish your calendar at your own domain (e.g., events.yourcompany.com)
            </p>

            {publishingSettings.custom_domain_enabled && (
              <div className="space-y-3 pl-6 border-l-2 border-gray-200">
                <div>
                  <Label htmlFor="custom_domain" className="text-sm">Custom Domain</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Input
                      id="custom_domain"
                      placeholder="events.yourcompany.com"
                      value={customDomainInput}
                      onChange={(e) => setCustomDomainInput(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={verifyDomain}
                      disabled={verifyingDomain}
                    >
                      {verifyingDomain ? 'Verifying...' : 'Verify'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetDomainSettings}
                    >
                      Reset
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the subdomain you want to use for your calendar
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-medium text-blue-900">DNS Configuration Required</h5>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDNSInstructions(!showDNSInstructions)}
                    >
                      {showDNSInstructions ? 'Hide' : 'Show'} Instructions
                    </Button>
                  </div>
                  
                  {showDNSInstructions && (
                    <div className="space-y-3 text-sm text-blue-800">
                      <p>Set up the following DNS record with your domain provider:</p>
                      
                      <div className="bg-blue-100 p-3 rounded font-mono text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span>Type: CNAME</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard('CNAME')}
                            className="h-6 px-2"
                          >
                            Copy
                          </Button>
                        </div>
                        <div className="flex items-center justify-between mb-1">
                          <span>Name: {publishingSettings.custom_domain?.split('.')[0] || 'events'}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(publishingSettings.custom_domain?.split('.')[0] || 'events')}
                            className="h-6 px-2"
                          >
                            Copy
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Value: {publishingSettings.cname_target || deploymentConfig.cname_target || deploymentConfig.deployment_host}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(publishingSettings.cname_target || deploymentConfig.cname_target || deploymentConfig.deployment_host)}
                            className="h-6 px-2"
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                      
                      <div className="text-xs">
                        <p><strong>Steps:</strong></p>
                        <ol className="list-decimal list-inside space-y-1 mt-1">
                          <li>Log in to your domain provider (GoDaddy, Namecheap, Cloudflare, etc.)</li>
                          <li>Go to DNS settings for your domain</li>
                          <li>Add a new CNAME record with the values above</li>
                          <li>Wait 5-15 minutes for DNS propagation</li>
                          <li>Click "Verify" to test your setup</li>
                        </ol>
                      </div>
                    </div>
                  )}
                </div>

                {publishingSettings.domain_verified && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <div>
                        <p className="text-sm font-medium text-green-900">Domain Verified!</p>
                        <p className="text-xs text-green-700">
                          Your calendar is now available at: 
                          <a 
                            href={`https://${publishingSettings.custom_domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 underline hover:no-underline"
                          >
                            {publishingSettings.custom_domain}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="ssl_enabled"
                    checked={publishingSettings.ssl_enabled !== false}
                    onChange={(e) => updateSettings({ ssl_enabled: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="ssl_enabled" className="text-sm">
                    Enable SSL/HTTPS (Recommended)
                  </Label>
                </div>
              </div>
            )}
          </div>

          {/* JavaScript Embed Widget - Coming Soon */}
          <div className="border rounded-lg p-4 space-y-3 bg-gray-50 opacity-75">
            <div className="flex items-center space-x-2">
              <Label className="text-sm font-medium text-gray-600">
                JavaScript Embed Widget
              </Label>
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                Coming Soon
              </span>
            </div>
            
            <p className="text-xs text-gray-600">
              Embed your calendar on any website with a simple JavaScript snippet
            </p>

            <div className="space-y-2 text-xs text-gray-500">
              <p>• Responsive design with auto-sizing</p>
              <p>• Shadow DOM isolation for style protection</p>
              <p>• Custom CSS support for branding</p>
              <p>• Copy-paste embed code</p>
            </div>
          </div>

          {/* Additional Future Publishing Options */}
          <div className="border rounded-lg p-4 space-y-2 bg-gray-50 opacity-75">
            <h5 className="text-sm font-medium text-gray-600">Additional Publishing Options (Coming Soon)</h5>
            <div className="space-y-1 text-xs text-gray-500">
              <p>• WordPress Plugin Integration</p>
              <p>• Social Media Publishing</p>
              <p>• PDF Calendar Export</p>
              <p>• Email Newsletter Integration</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default App;

